import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Card, Button, InputNumber, Form, Row, Col, Alert, Descriptions,
  Tag, Typography, Divider, Space, Tabs, message, Input, Radio, Table, Spin, Tooltip, Segmented, Checkbox, DatePicker, Modal, Switch, Collapse,
} from 'antd';
import {
  CalculatorOutlined, SafetyOutlined, ExperimentOutlined,
  ThunderboltOutlined, CheckCircleOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { adminAPI, covariateAPI, doseAPI, drugAPI, modelAPI } from '../services/api';
import { useResponsive } from '../contexts/ResponsiveContext';
import PKCurve from './PKCurve';
import TDMOptimizePanel from './TDMOptimizePanel';
import SelectableRegimenCard from './mobile/SelectableRegimenCard';
import EditableCandidateRegimenCard, {
  type CandidateRegimenRecalcState,
} from './mobile/EditableCandidateRegimenCard';
import GroupedCandidateRegimenTable, {
  resolveCandidateSourceIndex,
} from './GroupedCandidateRegimenTable';
import { METHOD_CONFIG, type RegimenColumn } from '../constants/methodConfig';
import {
  calcSimDoses,
  generatePKCurve,
  generateSteadyStatePKCurveIntervals,
} from '../utils/pkCurveGen';
import { normalizePkpdResults } from '../utils/pkpdResults';
import ClinicalInterpretation from './ClinicalInterpretation';
import { resolveEvaluationStatus, resolveRegimenStatus } from '../utils/regimenStatus';
import type { AdministrationMode, ModelMatchResult, DoseResult, PKFormulaResult, RemedialDoseResult, Covariates, PkPdMethodResult, GridSearchRegimen, RemedialCandidate } from '../types';
import {
  buildEffectiveRegistry,
  expandCovariateKeys,
  getRegistryVersion,
  subscribeRegistryChanges,
  type CovariateDefinition as RegistryCovariateDefinition,
} from '../config/covariateRegistry';
import { getDrugLabel } from '../constants/drugs';
import { normalizeScrUnit } from '../utils/clinicalUnits';
import { formatDoseMgKg } from '../utils/doseDisplayFormat';
import {
  convertCanonicalValue,
  convertUnitValue,
  formatCanonicalDose,
  getDoseDisplayUnit,
  projectCanonicalConcentrationCurve,
  projectCanonicalTdmPoints,
  unitFromResult,
} from '../utils/quantityUnits';
import {
  getMissingBackendDerivationTargets,
  mergeBackendDerivedValues,
  normalizeScrMgDl,
} from '../utils/derivedCovariates';
import { getCanonicalModelDisplayName } from '../utils/modelDisplay';
import {
  applyConfiguredConcentrationUnits,
  findTargetForAdministrationMode,
  getAdministrationModeLabel,
  getCalculationMethodConfigurationIssue,
  getConfiguredInputUnit,
  getConcentrationDisplayUnit,
  getDefaultAdministrationMode,
  normalizeAdministrationMode,
  getPreferredTargetParameter as getPreferredTargetParameterForMode,
  getSupportedAdministrationModes,
  resolveAvailableCalculationMethods,
} from '../utils/doseMethodVisibility';
import { resolveRemedialCurveDisplayRange } from '../utils/remedialCurveView';
import { formatDisplayNumber } from '../utils/displayNumbers';
import {
  getPkpdMetricPresentation,
  resolvePkpdComparisonStatusLabel,
  resolvePkpdTargetBounds,
} from '../utils/pkpdPresentation';
import { getExposureSafetyLimit } from '../utils/safetyRules';
import { usesIntervalCandidateAccordion } from '../utils/pkFormulaPresentation';

const { Title, Text } = Typography;
const { TextArea } = Input;

const DOSE_CALCULATION_STYLES = `
  .dose-calculation__shell {
    display: flex;
    flex-direction: column;
    gap: 18px;
    color: #0B2545;
  }

  .dose-calculation__hero {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    padding: 4px 4px 0;
  }

  .dose-calculation__hero-copy {
    min-width: 0;
  }

  .dose-calculation__hero-eyebrow {
    color: #7A869A;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .dose-calculation__hero-title {
    margin-top: 8px;
    color: #061529;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif;
    font-size: clamp(24px, 2vw, 30px);
    font-weight: 800;
    line-height: 1.06;
    letter-spacing: -0.03em;
  }

  .dose-calculation__hero-subtitle {
    margin-top: 10px;
    max-width: 62ch;
    color: #667085;
    font-size: 13px;
    line-height: 1.7;
  }

  .dose-calculation__hero-tags {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
  }

  .dose-calculation__hero-tag.ant-tag {
    margin-inline-end: 0;
    padding: 6px 10px;
    border-radius: 999px;
    border-color: #DCE7F3;
    background: linear-gradient(180deg, #FFFFFF 0%, #F7FAFD 100%);
    color: #46607A;
    font-size: 11px;
    font-weight: 700;
  }

  .dose-calculation__tabs .ant-tabs-nav {
    margin-bottom: 18px;
  }

  .dose-calculation__tabs .ant-tabs-nav::before {
    display: none;
  }

  .dose-calculation__tabs .ant-tabs-nav-list {
    gap: 8px;
    padding: 6px;
    border: 1px solid #E6ECF3;
    border-radius: 18px;
    background: linear-gradient(180deg, #FCFDFF 0%, #F7FAFD 100%);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.92);
  }

  .dose-calculation__tabs .ant-tabs-tab {
    margin: 0 !important;
    padding: 10px 14px !important;
    border-radius: 14px;
    transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  }

  .dose-calculation__tabs .ant-tabs-tab-active {
    background: linear-gradient(135deg, #0F62FE 0%, #0A4DC9 100%);
    box-shadow: 0 14px 26px rgba(15, 98, 254, 0.18);
  }

  .dose-calculation__tabs .ant-tabs-tab-active .ant-tabs-tab-btn,
  .dose-calculation__tabs .ant-tabs-tab-active .anticon {
    color: #FFFFFF !important;
  }

  .dose-calculation__tabs .ant-tabs-ink-bar {
    display: none;
  }

  .dose-calculation__tabs .ant-tabs-tab-btn {
    white-space: nowrap;
    color: #445266;
    font-size: 13px;
    font-weight: 700;
  }

  .dose-calculation__tab-panel {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .dose-calculation__inline-form.ant-form {
    display: block;
    padding: 20px;
    border: 1px solid #E6ECF3;
    border-radius: 18px;
    background: linear-gradient(180deg, #FFFFFF 0%, #FBFDFF 100%);
    box-shadow: 0 12px 28px rgba(11, 37, 69, 0.045);
  }

  .dose-calculation__remedial-form-row {
    width: 100%;
  }

  .dose-calculation__inline-form.ant-form .ant-form-item {
    margin-bottom: 0;
  }

  .dose-calculation__mode-loading-row {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 14px;
    padding-bottom: 14px;
    border-bottom: 1px solid #EEF3F8;
  }

  .dose-calculation__mode-loading-row > .ant-form-item {
    margin-bottom: 0 !important;
  }

  .dose-calculation__loading-toggle {
    min-height: 32px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border: 1px solid #DCE7F3;
    border-radius: 10px;
    background: #F8FBFF;
    white-space: nowrap;
  }

  .dose-calculation__mode-loading-row .ant-radio-group {
    display: flex;
    flex-wrap: wrap;
  }

  .dose-calculation__loading-card.ant-card {
    margin-bottom: 12px;
    border: 1px solid #DCE7F3;
    border-radius: 16px;
    background: linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%);
    box-shadow: none;
  }

  .dose-calculation__loading-card .ant-card-body {
    padding: 14px 16px 16px;
  }

  .dose-calculation__loading-card .ant-card-head {
    min-height: 44px;
    padding: 0 16px;
    border-bottom: 1px solid #E7EEF6;
    background: transparent;
  }

  .dose-calculation__loading-card .ant-card-head-title {
    padding: 12px 0 10px;
    color: #0B2545;
    font-size: 13px;
    font-weight: 750;
  }

  .dose-calculation__loading-card .ant-form-item-label > label {
    color: #5F6F82;
    font-size: 12px;
    font-weight: 600;
  }

  /* The parent form uses the inline layout for the main calculation row.
   * Override that layout inside the loading-dose card so each label sits
   * directly above its input and both controls share the same top edge. */
  .dose-calculation__loading-card .ant-form-item,
  .dose-calculation__loading-card .ant-form-item-row {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  .dose-calculation__loading-card .ant-form-item-label {
    width: auto;
    padding: 0 0 5px;
    line-height: 20px;
    text-align: left;
  }

  .dose-calculation__loading-card .ant-form-item-label > label {
    height: auto;
    white-space: normal;
  }

  .dose-calculation__loading-card .ant-form-item-control {
    width: 100%;
  }

  .dose-calculation__loading-card .ant-input-number {
    min-height: 38px;
    border-radius: 10px;
    border-color: #D5E2F0;
  }

  .dose-calculation__loading-card .ant-row {
    row-gap: 8px !important;
  }

  .dose-calculation__initial-target-row {
    width: 100%;
    margin-top: 10px;
  }

  .dose-calculation__initial-target-row .ant-form-item {
    margin-bottom: 0;
  }

  .dose-calculation__initial-action-col {
    display: flex;
    align-items: flex-end;
  }

  .dose-calculation__initial-action-col .ant-form-item {
    width: 100%;
  }

  .dose-calculation__initial-action-col .ant-btn {
    min-width: 140px;
  }

  .dose-calculation__inline-form.ant-form .ant-form-item-label > label {
    color: #5F6F82;
    font-size: 12px;
    font-weight: 600;
  }

  .dose-calculation__inline-form.ant-form .ant-input-number,
  .dose-calculation__inline-form.ant-form .ant-picker,
  .dose-calculation__pk-form.ant-form .ant-input-number {
    border-radius: 12px;
    border-color: #DCE6F1;
    min-height: 40px;
    width: 100%;
  }

  .dose-calculation__pk-form.ant-form {
    padding: 18px;
    border: 1px solid #E6ECF3;
    border-radius: 24px;
    background: linear-gradient(180deg, #FFFFFF 0%, #FBFDFF 100%);
    box-shadow: 0 12px 28px rgba(11, 37, 69, 0.045);
  }

  .dose-calculation__pk-form.ant-form .ant-form-item-label > label {
    color: #5F6F82;
    font-size: 12px;
    font-weight: 600;
  }

  .dose-calculation__segmented-scroll {
    overflow-x: auto;
    padding-bottom: 2px;
  }

  .dose-calculation__segmented-scroll .ant-segmented {
    min-width: max-content;
  }

  @media (max-width: 575px) {
    .dose-calculation__mode-loading-row {
      flex-direction: column;
      gap: 8px;
    }

    .dose-calculation__loading-toggle {
      padding: 6px 10px;
    }

    .dose-calculation__inline-form.ant-form {
      padding: 14px;
      border-radius: 18px;
    }

    .dose-calculation__loading-card .ant-card-head,
    .dose-calculation__loading-card .ant-card-body {
      padding-left: 12px;
      padding-right: 12px;
    }
  }

  .dose-calculation__pkpd-card.ant-card {
    border-radius: 8px;
    border-color: #DCE5EF;
    box-shadow: 0 8px 22px rgba(11, 37, 69, 0.045);
  }

  .dose-calculation__pkpd-card .ant-card-head {
    min-height: 48px;
    padding: 0 18px;
    border-bottom-color: #E7EDF4;
  }

  .dose-calculation__pkpd-card .ant-card-body {
    padding: 18px;
  }

  .dose-calculation__pkpd-title {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .dose-calculation__pkpd-title-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dose-calculation__pkpd-comparison {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    overflow: hidden;
    border: 1px solid #DCE5EF;
    border-radius: 8px;
    background: #FFFFFF;
  }

  .dose-calculation__pkpd-column {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "heading value"
      "metric metric";
    align-items: center;
    column-gap: 14px;
    row-gap: 5px;
    min-width: 0;
    padding: 14px 16px;
  }

  .dose-calculation__pkpd-column--target {
    background: #F8FAFC;
  }

  .dose-calculation__pkpd-column--prediction {
    border-left: 1px solid #DCE5EF;
  }

  .dose-calculation__pkpd-column-heading {
    grid-area: heading;
    color: #667085;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.35;
  }

  .dose-calculation__pkpd-metric-name {
    grid-area: metric;
    color: #344054;
    font-size: 13px;
    line-height: 1.4;
  }

  .dose-calculation__pkpd-value-row {
    grid-area: value;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 10px;
    min-width: 0;
    margin-top: 0;
    text-align: right;
  }

  .dose-calculation__pkpd-value {
    min-width: 0;
    color: #101828;
    font-size: 22px;
    font-weight: 800;
    line-height: 1.25;
    font-variant-numeric: tabular-nums;
    word-break: break-word;
  }

  .dose-calculation__pkpd-value--prediction {
    color: #0B5CBD;
  }

  .dose-calculation__pkpd-evidence {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid #E7EDF4;
  }

  .dose-calculation__pkpd-evidence-title {
    margin-bottom: 10px;
    color: #667085;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.35;
  }

  .dose-calculation__pkpd-evidence-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 24px;
  }

  .dose-calculation__pkpd-evidence-item {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 7px;
    min-height: 32px;
    color: #344054;
    font-size: 13px;
    line-height: 1.45;
  }

  .dose-calculation__pkpd-evidence-label {
    color: #667085;
  }

  .dose-calculation__pkpd-evidence-value {
    color: #101828;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: 575.98px) {
    .dose-calculation__initial-action-col .ant-btn {
      width: 100%;
    }

    .dose-calculation__pkpd-card .ant-card-head,
    .dose-calculation__pkpd-card .ant-card-body {
      padding-left: 14px;
      padding-right: 14px;
    }

    .dose-calculation__pkpd-comparison {
      grid-template-columns: 1fr;
    }

    .dose-calculation__pkpd-column {
      display: block;
      padding: 15px 16px;
    }

    .dose-calculation__pkpd-metric-name {
      margin-top: 8px;
    }

    .dose-calculation__pkpd-value-row {
      justify-content: flex-start;
      margin-top: 5px;
      text-align: left;
    }

    .dose-calculation__pkpd-column--prediction {
      border-top: 1px solid #DCE5EF;
      border-left: 0;
    }

    .dose-calculation__pkpd-value {
      font-size: 20px;
    }

    .dose-calculation__pkpd-evidence-list {
      display: grid;
      grid-template-columns: 1fr;
      gap: 8px;
    }
  }

  .dose-calculation__mobile-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .dose-calculation__candidate-table .ant-table-content {
    overflow-x: auto !important;
  }

  .dose-calculation__candidate-table .ant-table {
    min-width: 760px;
  }

  .dose-calculation__candidate-table .ant-table-cell {
    padding: 7px 5px !important;
    font-size: 12px;
    line-height: 1.3;
    overflow-wrap: normal;
  }

  .dose-calculation__candidate-table .ant-table-thead > tr > th {
    color: #5F6F82;
    font-size: 11px;
    font-weight: 700;
  }

  .dose-calculation__candidate-table .ant-input-number-input {
    padding-inline: 6px;
    font-size: 12px;
  }

  .dose-calculation__candidate-table .ant-table-row {
    cursor: pointer;
  }

  .dose-calculation__candidate-table .candidate-evaluation-column {
    position: sticky !important;
    right: 0;
    z-index: 2;
    background: #FFFFFF;
    box-shadow: -7px 0 9px -9px rgba(11, 37, 69, 0.7);
  }

  .dose-calculation__candidate-table .candidate-evaluation-column .ant-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    max-width: none;
    white-space: nowrap;
    line-height: 18px;
    margin: 0;
  }

  .dose-calculation__loading-maintenance-summary {
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    align-items: center;
    column-gap: 8px;
    row-gap: 4px;
    white-space: nowrap;
  }

  .dose-calculation__loading-maintenance-label {
    color: #7A869A;
    font-size: 11px;
  }

  .dose-calculation__loading-maintenance-value {
    min-width: 0;
    color: #0B2545;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
  }

  .dose-calculation__candidate-table .ant-table-thead > tr > th.candidate-evaluation-column {
    z-index: 3;
    background: #FAFAFA;
  }

  .dose-calculation__candidate-table .ant-table-tbody > tr:hover > td.candidate-evaluation-column {
    background: #FAFAFA !important;
  }

  .dose-calculation__candidate-table .ant-table-tbody > tr.ant-table-row-selected > td.candidate-evaluation-column {
    background: #E6F4FF !important;
  }

  @media (max-width: 575.98px) {
    .dose-calculation__candidate-table .ant-table-cell {
      padding: 6px 3px !important;
      font-size: 11px;
      vertical-align: top;
    }

    .dose-calculation__candidate-table .ant-table {
      min-width: 760px;
    }

    .dose-calculation__candidate-table .ant-tag {
      max-width: none;
      padding-inline: 4px;
      white-space: nowrap;
      text-align: center;
      line-height: 18px;
    }
  }

  .dose-calculation__mobile-card {
    padding: 14px 16px;
    border-radius: 18px;
    border: 1px solid #E6ECF3;
    background: #FFFFFF;
  }

  .dose-calculation__mobile-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 12px;
  }

  .dose-calculation__mobile-grid-label {
    display: block;
    margin-bottom: 3px;
    color: #7A869A;
    font-size: 12px;
    line-height: 1.2;
  }

  .dose-calculation__mobile-grid-value {
    color: #061529;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.25;
    word-break: break-word;
  }

  .dose-calculation__sticky-cta {
    position: fixed;
    left: 16px;
    right: 16px;
    bottom: calc(88px + env(safe-area-inset-bottom));
    z-index: 26;
    padding: 12px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.96);
    backdrop-filter: blur(12px);
    box-shadow: 0 18px 40px rgba(11, 37, 69, 0.16);
  }

  .dose-calculation__sticky-spacer {
    height: 96px;
  }

  .dose-calculation__model-card.ant-card {
    border-radius: 24px;
    border: 1px solid #E6ECF3;
    background:
      radial-gradient(circle at top left, rgba(15, 98, 254, 0.05) 0%, transparent 30%),
      linear-gradient(180deg, #FFFFFF 0%, #FCFDFF 100%);
    box-shadow: 0 14px 32px rgba(11, 37, 69, 0.05);
  }

  .dose-calculation__model-card.ant-card .ant-card-body {
    padding: 18px;
  }

  .dose-calculation__model-name {
    color: #061529;
    font-size: 18px;
    font-weight: 800;
    line-height: 1.35;
    word-break: break-word;
  }

  .dose-calculation__model-subtitle {
    margin-top: 6px;
    color: #7A869A;
    font-size: 13px;
    line-height: 1.5;
  }

  .dose-calculation__model-meta {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 14px;
  }

  .dose-calculation__model-meta-item--full {
    grid-column: 1 / -1;
  }

  .dose-calculation__model-meta-label {
    display: block;
    margin-bottom: 4px;
    color: #7A869A;
    font-size: 12px;
    line-height: 1.2;
  }

  .dose-calculation__model-meta-value {
    color: #061529;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.35;
    word-break: break-word;
  }

  .dose-calculation__summary-card.ant-card {
    border-radius: 18px;
    border: 1px solid #e8eef5;
    background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
    box-shadow: 0 12px 28px rgba(11, 37, 69, 0.045);
    overflow: hidden;
  }

  .dose-calculation__summary-card .ant-card-body {
    padding: 14px 18px;
  }

  .dose-calculation__summary-shell {
    display: block;
  }

  .dose-calculation__summary-hero {
    position: relative;
    padding: 0;
    background: transparent;
  }

  .dose-calculation__summary-hero::after {
    content: none;
  }

  .dose-calculation__summary-hero-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(11, 37, 69, 0.08);
  }

  .dose-calculation__summary-eyebrow {
    display: none;
  }

  .dose-calculation__summary-title {
    margin-top: 0;
    color: #061529;
    font-size: 15px;
    font-weight: 800;
    line-height: 1.2;
    letter-spacing: -0.02em;
  }

  .dose-calculation__summary-caption {
    margin-top: 4px;
    max-width: none;
    color: #7A869A;
    font-size: 12px;
    line-height: 1.45;
  }

  .dose-calculation__summary-badges {
    display: none;
  }

  .dose-calculation__summary-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 999px;
    border: 1px solid rgba(176, 197, 223, 0.9);
    background: rgba(255, 255, 255, 0.82);
    color: #0B2545;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    backdrop-filter: blur(10px);
  }

  .dose-calculation__summary-stats {
    display: none;
  }

  .dose-calculation__summary-stat {
    padding: 12px 13px;
    border-radius: 16px;
    border: 1px solid rgba(214, 227, 241, 0.98);
    background: rgba(255, 255, 255, 0.82);
    box-shadow: 0 10px 22px rgba(11, 37, 69, 0.05);
    backdrop-filter: blur(12px);
  }

  .dose-calculation__summary-stat-label {
    display: block;
    margin-bottom: 4px;
    color: #7A869A;
    font-size: 11px;
    line-height: 1.2;
  }

  .dose-calculation__summary-stat-value {
    color: #061529;
    font-size: 16px;
    font-weight: 800;
    line-height: 1.2;
    word-break: break-word;
  }

  .dose-calculation__summary-secondary {
    display: none;
  }

  .dose-calculation__summary-inline {
    display: inline-flex;
    align-items: baseline;
    gap: 5px;
    color: #5F6F82;
    font-size: 11px;
    line-height: 1.45;
  }

  .dose-calculation__summary-inline strong {
    color: #0B2545;
    font-size: 12px;
    font-weight: 700;
  }

  .dose-calculation__summary-panel {
    padding: 11px 12px;
    border-radius: 14px;
    border: 1px solid #E6ECF3;
    background: #FFFFFF;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
  }

  .dose-calculation__summary-panel-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 6px;
  }

  .dose-calculation__summary-panel-title {
    color: #061529;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.2;
  }

  .dose-calculation__summary-panel-copy {
    margin-top: 2px;
    max-width: 28ch;
    color: #7A869A;
    font-size: 10px;
    line-height: 1.3;
  }

  .dose-calculation__summary-table {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    column-gap: 24px;
    row-gap: 0;
    margin-top: 10px;
  }

  .dose-calculation__summary-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    min-width: 0;
    padding: 11px 0;
    border-bottom: 1px solid rgba(11, 37, 69, 0.06);
  }

  .dose-calculation__summary-row-label {
    flex: 0 0 auto;
    max-width: 48%;
    color: #7A869A;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.45;
  }

  .dose-calculation__summary-row-value {
    flex: 1 1 auto;
    min-width: 0;
    color: #061529;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.45;
    text-align: right;
    word-break: break-word;
  }

  .dose-calculation__summary-empty {
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: 12px;
    border: 1px dashed #d9e5f0;
    background: linear-gradient(180deg, #fbfdff 0%, #f5f9fe 100%);
    color: #5f6f82;
    font-size: 12px;
    line-height: 1.6;
  }

  .dose-calculation__section-card.ant-card {
    border-radius: 22px;
    border: 1px solid #E6ECF3;
    background: linear-gradient(180deg, #FFFFFF 0%, #FCFDFF 100%);
    box-shadow: 0 12px 28px rgba(11, 37, 69, 0.045);
    overflow: hidden;
  }

  .dose-calculation__section-card.ant-card .ant-card-head {
    min-height: 54px;
    padding: 0 18px;
    border-bottom: 1px solid #EEF3F8;
    background: linear-gradient(180deg, rgba(248, 251, 255, 0.9) 0%, rgba(255, 255, 255, 0.92) 100%);
  }

  .dose-calculation__section-card.ant-card .ant-card-head-title {
    color: #061529;
    font-size: 14px;
    font-weight: 700;
  }

  .dose-calculation__section-card.ant-card .ant-card-body {
    padding: 18px;
  }

  .dose-calculation__shell .ant-card.ant-card-bordered:not(.dose-calculation__summary-card):not(.dose-calculation__model-card) {
    border-radius: 22px;
    border-color: #E6ECF3;
    background: linear-gradient(180deg, #FFFFFF 0%, #FCFDFF 100%);
    box-shadow: 0 12px 28px rgba(11, 37, 69, 0.045);
  }

  .dose-calculation__shell .ant-card.ant-card-bordered:not(.dose-calculation__summary-card):not(.dose-calculation__model-card) .ant-card-head {
    border-bottom-color: #EEF3F8;
  }

  .dose-calculation__shell .ant-alert {
    border-radius: 16px;
  }

  .dose-calculation__shell .ant-descriptions .ant-descriptions-item-label {
    color: #6B7280;
  }

  .dose-calculation__soft-alert.ant-alert {
    border-radius: 16px;
    border: 1px solid #E7EDF5;
    background: linear-gradient(180deg, #FBFDFF 0%, #FFFFFF 100%);
  }

  .dose-calculation__remedial-timeline {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
    gap: 10px;
    width: 100%;
  }

  .dose-calculation__remedial-timepoint {
    min-height: 72px;
    padding: 12px 14px;
    border-radius: 14px;
    border: 1px solid #DCE7F3;
    background: #FFFFFF;
  }

  .dose-calculation__remedial-timepoint span {
    display: block;
    color: #6B7280;
    font-size: 12px;
    line-height: 1.35;
    word-break: keep-all;
  }

  .dose-calculation__remedial-timepoint strong {
    display: block;
    margin-top: 6px;
    color: #0B2545;
    font-size: 16px;
    font-weight: 800;
    line-height: 1.2;
    word-break: break-word;
  }

  .dose-calculation__remedial-row-near-best > td {
    background: #F6FFED !important;
  }

  .dose-calculation__remedial-compare-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
    align-items: start;
    margin-top: 12px;
  }

  .dose-calculation__remedial-compare-grid > .ant-card {
    min-width: 0;
  }

  .dose-calculation__remedial-table .ant-table-selection-column {
    width: 48px;
  }

  .dose-calculation__remedial-table .ant-table-thead > tr > th {
    white-space: normal;
    line-height: 1.25;
    padding-inline: 8px;
  }

  .dose-calculation__remedial-table .ant-table-tbody > tr > td {
    white-space: nowrap;
    padding-inline: 8px;
  }

  .dose-calculation__remedial-strategy {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
  }

  .dose-calculation__remedial-strategy .ant-tag {
    flex: 0 0 auto;
    margin-inline-end: 0;
    cursor: help;
  }

  .dose-calculation__remedial-strategy-title {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dose-calculation__remedial-dose-cell .ant-input-number {
    width: 100%;
    min-width: 0;
  }

  .dose-calculation__remedial-dose-cell,
  .dose-calculation__remedial-metric-cell {
    font-variant-numeric: tabular-nums;
  }

  .dose-calculation__remedial-action-cell .ant-space {
    flex-wrap: nowrap;
  }

  .dose-calculation__remedial-table .ant-table-row > td {
    transition: background-color 0.18s ease, box-shadow 0.18s ease;
  }

  .dose-calculation__remedial-table .ant-table-row:hover > td {
    background: #F8FBFF !important;
  }

  .dose-calculation__remedial-table .ant-table-row-selected > td,
  .dose-calculation__remedial-table .ant-table-row-selected:hover > td {
    background: #EDF5FF !important;
    box-shadow: inset 0 1px 0 #D8E9FF, inset 0 -1px 0 #D8E9FF;
  }

  .dose-calculation__remedial-table .ant-radio-wrapper {
    min-width: 32px;
    min-height: 32px;
    align-items: center;
    justify-content: center;
  }

  .dose-calculation__result-grid {
    align-items: stretch;
  }

  .dose-calculation__result-grid > .ant-col {
    display: flex;
  }

  .dose-calculation__result-card.ant-card,
  .dose-calculation__clinical-stack {
    width: 100%;
  }

  .dose-calculation__result-card.ant-card {
    height: 100%;
  }

  .dose-calculation__clinical-stack {
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: 100%;
  }

  .dose-calculation__clinical-card.ant-card {
    border-radius: 12px;
    border-color: #DCE5EF;
    box-shadow: none;
  }

  .dose-calculation__clinical-card.ant-card .ant-card-head {
    min-height: 40px;
    padding: 0 14px;
  }

  .dose-calculation__clinical-card.ant-card .ant-card-head-title {
    padding: 10px 0;
    font-size: 14px;
    font-weight: 700;
  }

  .dose-calculation__clinical-card.ant-card .ant-card-body {
    padding: 12px 14px;
  }

  .dose-calculation__safety-card.ant-card {
    flex: 0 0 auto;
  }

  .dose-calculation__pk-card.ant-card {
    flex: 1 1 auto;
    min-height: 0;
  }

  .dose-calculation__safety-status {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    min-height: 42px;
    padding: 8px 10px;
    border-radius: 10px;
    border: 1px solid #D7E3F1;
    background: #F7FAFD;
  }

  .dose-calculation__safety-status--ok {
    border-color: #BFE8CF;
    background: #F3FBF6;
  }

  .dose-calculation__safety-status--warning {
    border-color: #FFE1A8;
    background: #FFF9E8;
  }

  .dose-calculation__safety-status--danger {
    border-color: #FFD1D1;
    background: #FFF5F5;
  }

  .dose-calculation__safety-status .ant-tag {
    margin-inline-end: 0;
    font-weight: 700;
  }

  .dose-calculation__safety-copy {
    color: #5C6B7A;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.35;
    white-space: nowrap;
  }

  .dose-calculation__safety-alerts {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 92px;
    margin-top: 8px;
    overflow: auto;
  }

  .dose-calculation__safety-alerts .ant-alert {
    margin-bottom: 0 !important;
    padding: 6px 10px;
    border-radius: 8px;
  }

  .dose-calculation__pk-card .ant-descriptions-item {
    padding-bottom: 6px;
  }

  .dose-calculation__primary-btn.ant-btn {
    height: 42px;
    border: none;
    border-radius: 14px;
    background: linear-gradient(135deg, #0F62FE 0%, #0A4DC9 100%);
    box-shadow: 0 16px 30px rgba(15, 98, 254, 0.16);
    font-weight: 700;
  }

  .dose-calculation__continue-desktop {
    margin-top: 24px;
    text-align: center;
  }

  .dose-calculation__continue-desktop .ant-btn {
    min-width: 160px;
  }

  @media (max-width: 1024px) {
    .dose-calculation__hero {
      flex-direction: column;
    }

    .dose-calculation__hero-tags {
      justify-content: flex-start;
    }

    .dose-calculation__tabs .ant-tabs-nav {
      margin-bottom: 12px;
    }

    .dose-calculation__tabs .ant-tabs-nav-wrap {
      overflow-x: auto;
    }

    .dose-calculation__tabs .ant-tabs-nav-list {
      flex-wrap: nowrap;
      min-width: max-content;
    }

    .dose-calculation__tabs .ant-tabs-tab {
      flex: 0 0 auto;
      padding: 8px 10px;
      margin: 0 8px 0 0;
    }

    .dose-calculation__tabs .ant-tabs-tab .anticon {
      margin-right: 4px;
    }

    .dose-calculation__tabs .ant-tabs-tab-btn {
      font-size: 13px;
    }

    .dose-calculation__summary-hero-header,
    .dose-calculation__summary-panel-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .dose-calculation__summary-table {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 18px;
    }

  }

  @media (max-width: 575px) {
    .dose-calculation__hero-title {
      font-size: 24px;
    }

    .dose-calculation__summary-card .ant-card-body {
      padding: 12px 14px;
    }

    .dose-calculation__summary-hero {
      padding: 0;
    }

    .dose-calculation__summary-title {
      font-size: 14px;
    }

    .dose-calculation__summary-caption {
      font-size: 11px;
    }

    .dose-calculation__summary-table {
      grid-template-columns: 1fr;
      margin-top: 8px;
    }

    .dose-calculation__summary-row {
      padding: 10px 0;
    }

    .dose-calculation__summary-row-label {
      max-width: 44%;
      font-size: 10px;
    }

    .dose-calculation__summary-row-value {
      font-size: 12px;
    }
  }
`;

const renderMetricGrid = (metrics: Array<{ label: string; value: React.ReactNode }>) => (
  <div className="dose-calculation__mobile-grid">
    {metrics.map((metric) => (
      <div key={metric.label}>
        <span className="dose-calculation__mobile-grid-label">{metric.label}</span>
        <div className="dose-calculation__mobile-grid-value">{metric.value}</div>
      </div>
    ))}
  </div>
);

const renderContinueAction = (
  isMobileShell: boolean,
  onClick: () => void,
  label = '生成报告',
) => (
  isMobileShell ? (
    <>
      <div className="dose-calculation__sticky-spacer" />
      <div className="dose-calculation__sticky-cta">
        <Button type="primary" size="large" block onClick={onClick} className="dose-calculation__primary-btn">
          {label}
        </Button>
      </div>
    </>
  ) : (
    <div className="dose-calculation__continue-desktop">
      <Button type="primary" size="large" onClick={onClick} className="dose-calculation__primary-btn">
        {label}
      </Button>
    </div>
  )
);

type CovariateRegistryMap = Record<string, RegistryCovariateDefinition>;
type SummaryEntry = {
  key: string;
  label: string;
  value: string;
  missing?: boolean;
};

const SUMMARY_PATIENT_KEYS = ['weight', 'gestational_age', 'age_pna', 'age_pma', 'height', 'scr', 'egfr', 'crcl'];
const SUMMARY_BASIC_INFO_KEYS = ['gender', 'age_years', ...SUMMARY_PATIENT_KEYS.filter((key) => ['weight', 'height', 'gestational_age', 'age_pna', 'age_pma'].includes(key))];

const uniqueKeys = (keys: Array<string | null | undefined>) => {
  const seen = new Set<string>();
  return keys.reduce<string[]>((acc, key) => {
    const normalized = String(key || '').trim();
    if (!normalized || seen.has(normalized)) return acc;
    seen.add(normalized);
    acc.push(normalized);
    return acc;
  }, []);
};

const normalizeBooleanLike = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  }
  return null;
};

const formatSummaryNumber = (value: number) => {
  if (!Number.isFinite(value)) return '-';
  if (Math.abs(value) >= 100 || Number.isInteger(value)) return String(Math.round(value * 1000) / 1000).replace(/\.0+$/, '');
  if (Math.abs(value) >= 10) return value.toFixed(1).replace(/\.0$/, '');
  if (Math.abs(value) >= 1) return value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  return value.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
};

const normalizeFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const resolveLoadingTransitionTime = (
  loading: { enabled?: boolean; count?: number; interval_h?: number } | null | undefined,
  maintenanceInterval: unknown,
): number | undefined => {
  if (!loading || loading.enabled === false) return undefined;
  const maintenance = normalizeFiniteNumber(maintenanceInterval);
  if (maintenance == null || maintenance <= 0) return undefined;
  const countValue = normalizeFiniteNumber(loading.count);
  const count = countValue != null && countValue >= 1 ? Math.floor(countValue) : 1;
  const loadingInterval = normalizeFiniteNumber(loading.interval_h) ?? maintenance;
  return (count - 1) * loadingInterval + maintenance;
};

const formatDateTimeToMinuteString = (value: unknown): string | undefined => {
  if (!value) return undefined;
  const parsed = dayjs(value as dayjs.ConfigType);
  if (!parsed.isValid()) return undefined;
  return parsed.format('YYYY-MM-DD HH:mm');
};

const PK_PARAMETER_KEYS = new Set([
  'CL', 'Cl', 'cl', 'clearance', 'clearance_l_h', 'CL_individual', 'CL_pop',
  'Vc', 'VC', 'vc', 'V', 'V1', 'Vd', 'vd', 'volume', 'Vc_individual', 'Vc_pop',
  'ke', 'Ke', 'kel', 'KEL', 'half_life',
]);

const hasDisplayablePkParameters = (pk: unknown): pk is Record<string, unknown> => (
  !!pk
  && typeof pk === 'object'
  && Object.entries(pk as Record<string, unknown>).some(
    ([key, value]) => PK_PARAMETER_KEYS.has(key) && normalizeFiniteNumber(value) !== null,
  )
);

const buildModelInputValues = (covariates: Covariates, backendDerivedValues: Record<string, any>) => {
  const next: Record<string, any> = mergeBackendDerivedValues(covariates, backendDerivedValues);

  const normalizedScr = normalizeScrMgDl(next.scr, next.scr_unit);
  if (normalizedScr != null) {
    next.scr = normalizedScr;
    next.scr_unit = 'mg/dL';
  }

  return next;
};

const formatSummaryEntry = (
  key: string,
  values: Record<string, any>,
  registry: CovariateRegistryMap,
): SummaryEntry => {
  const definition = registry[key];
  const label = definition?.label || key;
  const rawValue = values[key];

  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return { key, label, value: '未提供', missing: true };
  }

  const boolValue = normalizeBooleanLike(rawValue);
  if (boolValue !== null) {
    return { key, label, value: boolValue ? '是' : '否' };
  }

  if (definition?.type === 'select' && Array.isArray(definition.options)) {
    const option = definition.options.find((item) => String(item.value) === String(rawValue));
    return { key, label, value: option?.label || String(rawValue) };
  }

  if (key === 'scr') {
    return {
      key,
      label,
      value: `${formatSummaryNumber(Number(rawValue))} ${normalizeScrUnit(values.scr_unit || 'mg/dL')}`,
    };
  }

  const renderedValue = typeof rawValue === 'number'
    ? formatSummaryNumber(rawValue)
    : String(rawValue);
  const unit = definition?.unit;

  return {
    key,
    label,
    value: unit ? `${renderedValue} ${unit}` : renderedValue,
  };
};

interface DoseCalculationProps {
  caseId: string;
  selectedModel: ModelMatchResult | null;
  covariates: Covariates;
  drugName?: string;
  administrationMode?: AdministrationMode;
  onAdministrationModeChange?: (mode: AdministrationMode) => void;
  onCalculated: (result: DoseResult) => void;
  onRemedialCalculated?: (result: RemedialDoseResult) => void;
  /** 接收PK公式法结果用于报告生成 */
  onPKFormulaCalculated?: (result: PKFormulaResult) => void;
  /** 初始激活的Tab (如从模型匹配跳转PK公式法时传 'pk_formula') */
  initialTab?: string;
  tdmReplayDraft?: {
    source?: string;
    sourceLabel?: string;
    calculationId?: string;
    modelId?: string;
    selectedMethod?: string;
    administrationMode?: AdministrationMode;
    dosingRegimens?: Array<Record<string, any>>;
    tdmSamples?: Array<Record<string, any>>;
    targetValues?: Record<string, number | undefined>;
    pkFormulaInput?: Record<string, any>;
    calculationType?: string;
  } | null;
}

const DoseCalculation: React.FC<DoseCalculationProps> = ({
  caseId, selectedModel, covariates, drugName,
  administrationMode: controlledAdministrationMode,
  onAdministrationModeChange,
  onCalculated, onRemedialCalculated, onPKFormulaCalculated, initialTab, tdmReplayDraft,
}) => {
  const { isMobileShell } = useResponsive();
  const [form] = Form.useForm();
  const [pkForm] = Form.useForm();
  const [remedialForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DoseResult | null>(null);
  const [pkResult, setPkResult] = useState<PKFormulaResult | null>(null);
  const [remedialResult, setRemedialResult] = useState<RemedialDoseResult | null>(null);
  const [activeTab, setActiveTab] = useState<string | undefined>(initialTab);
  const [submittedTargetAucMic, setSubmittedTargetAucMic] = useState<number | null>(null);
  const initialInfusionTouchedRef = useRef(false);
  const initialMicTouchedRef = useRef(false);
  const pkInfusionTouchedRef = useRef(false);
  const drugConfigAppliedRef = useRef(false);

  // 药物配置 + 计算方法选择
  const [drugConfig, setDrugConfig] = useState<any>(null);
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [loadingEnabled, setLoadingEnabled] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState<Record<string, any> | null>(null);
  const [administrationMode, setAdministrationMode] = useState<AdministrationMode>(
    controlledAdministrationMode || 'intermittent',
  );
  const [modelDetail, setModelDetail] = useState<Record<string, any> | null>(null);

  const commitAdministrationMode = useCallback((nextMode: AdministrationMode) => {
    if (nextMode === administrationMode) return;
    setAdministrationMode(nextMode);
    setResult(null);
    setPkResult(null);
    setRemedialResult(null);
    setSubmittedTargetAucMic(null);
    initialInfusionTouchedRef.current = false;
    initialMicTouchedRef.current = false;
    pkInfusionTouchedRef.current = false;
    onAdministrationModeChange?.(nextMode);
  }, [administrationMode, onAdministrationModeChange]);

  const handleAdministrationModeChange = (nextMode: AdministrationMode) => {
    if (nextMode === administrationMode) return;
    const hasCalculatedResult = Boolean(result || pkResult || remedialResult);
    if (!hasCalculatedResult) {
      commitAdministrationMode(nextMode);
      return;
    }
    Modal.confirm({
      title: '切换给药方式？',
      content: '切换后将清空当前计算结果，并重新加载该给药方式对应的模型和目标参数。',
      okText: '确认切换',
      cancelText: '取消',
      onOk: () => commitAdministrationMode(nextMode),
    });
  };

  useEffect(() => {
    if (controlledAdministrationMode && controlledAdministrationMode !== administrationMode) {
      setAdministrationMode(controlledAdministrationMode);
      setResult(null);
      setPkResult(null);
      setRemedialResult(null);
      setSubmittedTargetAucMic(null);
    }
  }, [administrationMode, controlledAdministrationMode]);

  useEffect(() => {
    if (drugName) {
      drugAPI.get(drugName).then((config: any) => {
        setDrugConfig(config);
        const supportedModes = getSupportedAdministrationModes(config);
        const nextMode = controlledAdministrationMode && supportedModes.includes(controlledAdministrationMode)
          ? controlledAdministrationMode
          : getDefaultAdministrationMode(config);
        const methods = resolveAvailableCalculationMethods(
          config,
          nextMode,
          config?.continuous_infusion?.target_parameter || 'Css',
        );
        setSelectedMethod(methods[0] || '');
        setAdministrationMode(nextMode);
      }).catch(() => {});
    }
  }, [controlledAdministrationMode, drugName]);

  useEffect(() => {
    initialInfusionTouchedRef.current = false;
    initialMicTouchedRef.current = false;
    pkInfusionTouchedRef.current = false;
    drugConfigAppliedRef.current = false;
    form.setFieldValue('infusion_duration_h', undefined);
    form.setFieldValue('mic', undefined);
    pkForm.setFieldValue('infusion_duration', undefined);
  }, [drugName, form, pkForm]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedModel?.model_id) {
      setModelDetail(null);
      return () => {
        cancelled = true;
      };
    }

    modelAPI.getDetail(selectedModel.model_id)
      .then((data) => {
        if (!cancelled) setModelDetail(data || null);
      })
      .catch(() => {
        if (!cancelled) setModelDetail(null);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedModel?.model_id]);

  const [registryVersion, setRegistryVersion] = useState(() => getRegistryVersion());
  useEffect(() => subscribeRegistryChanges(setRegistryVersion), []);
  const covariateRegistry = useMemo(() => buildEffectiveRegistry(), [registryVersion]);
  const [backendDerivedValues, setBackendDerivedValues] = useState<Record<string, any>>({});

  useEffect(() => {
    let cancelled = false;
    const targetKeys = getMissingBackendDerivationTargets(covariates as Record<string, unknown>);
    if (targetKeys.length === 0) {
      setBackendDerivedValues({});
      return () => {
        cancelled = true;
      };
    }

    covariateAPI.derive({
      covariates,
      target_keys: targetKeys,
    }).then((data: any) => {
      if (!cancelled) setBackendDerivedValues(data?.values || {});
    }).catch(() => {
      if (!cancelled) setBackendDerivedValues({});
    });

    return () => {
      cancelled = true;
    };
  }, [covariates]);

  const modelInputValues = useMemo(
    () => buildModelInputValues(covariates, backendDerivedValues),
    [backendDerivedValues, covariates],
  );
  const patientSummaryEntries = useMemo(() => {
    const entries: SummaryEntry[] = [];
    SUMMARY_BASIC_INFO_KEYS.forEach((key) => {
      if (modelInputValues[key] == null || modelInputValues[key] === '') return;
      entries.push(formatSummaryEntry(key, modelInputValues, covariateRegistry));
    });
    return entries;
  }, [covariateRegistry, modelInputValues]);
  const patientSummaryKeySet = useMemo(
    () => new Set(patientSummaryEntries.map((entry) => entry.key)),
    [patientSummaryEntries],
  );
  const modelSummaryKeys = useMemo(() => {
    const required = Array.isArray(modelDetail?.matching_criteria?.required_patient_data)
      ? modelDetail.matching_criteria.required_patient_data
      : [];
    const optional = Array.isArray(modelDetail?.matching_criteria?.optional_patient_data)
      ? modelDetail.matching_criteria.optional_patient_data
      : [];
    const fallback = Object.keys(selectedModel?.boundary_status || {});
    return expandCovariateKeys(uniqueKeys([
      ...required,
      ...optional,
      ...fallback,
    ]));
  }, [modelDetail, selectedModel?.boundary_status]);
  const modelSummaryEntries = useMemo(
    () => modelSummaryKeys.map((key) => formatSummaryEntry(key, modelInputValues, covariateRegistry)),
    [covariateRegistry, modelInputValues, modelSummaryKeys],
  );
  const visibleModelSummaryEntries = useMemo(
    () => modelSummaryEntries.filter((entry) => !entry.missing && !patientSummaryKeySet.has(entry.key)),
    [modelSummaryEntries, patientSummaryKeySet],
  );
  const combinedSummaryEntries = useMemo(() => {
    const merged: SummaryEntry[] = [];
    const seen = new Set<string>();

    [...patientSummaryEntries, ...visibleModelSummaryEntries].forEach((entry) => {
      if (seen.has(entry.key)) return;
      merged.push(entry);
      seen.add(entry.key);
    });

    return merged;
  }, [patientSummaryEntries, visibleModelSummaryEntries]);
  const hasSummaryCard = combinedSummaryEntries.length > 0;
  const summaryEntries = useMemo(() => combinedSummaryEntries, [combinedSummaryEntries]);
  const selectedModelDisplayName = useMemo(
    () => (selectedModel ? getCanonicalModelDisplayName({
      modelId: selectedModel.model_id,
      modelName: selectedModel.model_name,
      drugName,
      publicationInfo: selectedModel.publication_info,
    }) : ''),
    [drugName, selectedModel],
  );
  const summaryCardCopy = combinedSummaryEntries.length > 0
    ? `\u5171 ${combinedSummaryEntries.length} \u9879\u60a3\u8005\u4fe1\u606f`
    : '\u5f53\u524d\u6682\u65e0\u60a3\u8005\u6458\u8981';

  const supportedAdministrationModes: AdministrationMode[] = useMemo(() => {
    return getSupportedAdministrationModes(drugConfig);
  }, [drugConfig]);
  const isContinuousMode = administrationMode === 'continuous';
  const isOralMode = administrationMode === 'oral';
  const isBolusMode = administrationMode === 'bolus';
  const loadingProfile = useMemo(() => {
    const policy = drugConfig?.loading_dose_policy;
    const legacy = drugConfig?.loading_dose;
    let profiles: any[] = [];
    if (Array.isArray(policy?.profiles)) profiles = policy.profiles;
    else if (policy?.profiles && typeof policy.profiles === 'object') {
      profiles = Object.entries(policy.profiles).map(([mode, value]) => ({
        ...(value as Record<string, any>),
        id: (value as Record<string, any>)?.id || mode,
        administration_mode: (value as Record<string, any>)?.administration_mode || mode,
      }));
    }
    const modeProfile = profiles.find((item) =>
      String(item?.administration_mode || item?.mode || '').toLowerCase() === administrationMode,
    );
    const profile = modeProfile || profiles.find((item) => (
      item?.default === true
      && !String(item?.administration_mode || item?.mode || '').trim()
    ));
    const source = profile || legacy;
    const supported = policy?.supported !== false && Boolean(source || policy?.supported === true);
    if (!supported || !source) return null;
    const strategy = source.strategy && typeof source.strategy === 'object' ? source.strategy : source;
    return {
      ...source,
      ...strategy,
      profile_id: source.profile_id || source.id,
      administration_mode: administrationMode,
      basis: strategy.basis
        || strategy.dose_basis
        || (strategy.dose_mg_m2 != null ? 'mg_m2' : strategy.dose_mg != null ? 'mg' : strategy.dose_mg_kg != null ? 'mg_kg' : undefined)
        || drugConfig?.units?.dose?.basis
        || drugConfig?.dose_basis
        || 'mg_kg',
      value: strategy.value ?? strategy.dose_value ?? strategy.dose_mg_kg ?? strategy.dose_mg ?? strategy.dose_mg_m2,
      count: strategy.count ?? strategy.repeat_count ?? 1,
      interval_h: strategy.interval_h ?? strategy.repeat_interval_h,
      infusion_duration_h: strategy.infusion_duration_h ?? 0,
    };
  }, [administrationMode, drugConfig]);
  const loadingSupported = Boolean(loadingProfile);
  const loadingUserOverrideAllowed = drugConfig?.loading_dose_policy?.allow_user_override !== false;

  useEffect(() => {
    if (!loadingProfile) {
      setLoadingEnabled(false);
      setLoadingDraft(null);
      return;
    }
    setLoadingEnabled(
      drugConfig?.loading_dose_policy?.default_enabled === true
      || (drugConfig?.loading_dose_policy == null && drugConfig?.loading_dose?.enabled === true),
    );
    setLoadingDraft({ ...loadingProfile });
  }, [drugConfig, loadingProfile]);

  const continuousTargetParameter = drugConfig?.continuous_infusion?.target_parameter || 'Css';
  const pkDoseMassUnit = unitFromResult(
    null,
    'dose',
    drugConfig?.units?.dose?.display_mass_unit || 'mg',
  );
  const pkConcentrationUnit = unitFromResult(
    null,
    'concentration',
    drugConfig?.units?.concentration?.display_unit || 'mg/L',
  );
  const methodDef = selectedMethod ? METHOD_CONFIG[selectedMethod] : undefined;
  const shortestCandidateIntervalH = useMemo(() => {
    const intervals = Array.isArray(drugConfig?.candidate_intervals_h)
      ? drugConfig.candidate_intervals_h
          .map((value: unknown) => normalizeFiniteNumber(value))
          .filter((value: number | null): value is number => value != null && value > 0)
      : [];
    return intervals.length > 0 ? Math.min(...intervals) : undefined;
  }, [drugConfig?.candidate_intervals_h]);

  useEffect(() => {
    if (administrationMode !== 'intermittent' || initialInfusionTouchedRef.current) return;
    const configuredDuration = normalizeFiniteNumber(drugConfig?.default_infusion_duration_h);
    if (configuredDuration != null && configuredDuration > 0) {
      form.setFieldValue('infusion_duration_h', configuredDuration);
    }
  }, [administrationMode, drugConfig?.default_infusion_duration_h, form]);

  useEffect(() => {
    if (administrationMode !== 'intermittent' || pkInfusionTouchedRef.current) return;
    const configuredDuration = normalizeFiniteNumber(drugConfig?.default_infusion_duration_h);
    if (configuredDuration != null && configuredDuration > 0) {
      pkForm.setFieldValue('infusion_duration', configuredDuration);
    }
  }, [administrationMode, drugConfig?.default_infusion_duration_h, pkForm]);
  const remedialEnabled = Boolean(drugConfig?.remedial_dosing?.enabled);
  const modelTabLabel = isMobileShell ? (
    <><ExperimentOutlined /> 初始给药</>
  ) : (
    <><ExperimentOutlined /> 初始给药 (PopPK)</>
  );
  const optimizeTabLabel = isMobileShell ? (
    <><ThunderboltOutlined /> TDM优化</>
  ) : (
    <><ThunderboltOutlined /> TDM剂量优化</>
  );
  const pkFormulaTabLabel = isMobileShell ? (
    <><CalculatorOutlined /> PK公式</>
  ) : (
    <><CalculatorOutlined /> PK公式法 (两点法)</>
  );
  const remedialTabLabel = isMobileShell ? (
    <><SafetyOutlined /> 补服</>
  ) : (
    <><SafetyOutlined /> 补服漏服</>
  );

  const getPreferredTargetParameter = (method: string): string | undefined => {
    return getPreferredTargetParameterForMode(method, administrationMode, continuousTargetParameter);
  };

  /** 通用查找: 优先匹配当前给药模式, 仅在缺少模式化配置时回退到旧配置 */
  const findTarget = (method: string, parameter = getPreferredTargetParameter(method)) => {
    return findTargetForAdministrationMode(
      drugConfig,
      method,
      administrationMode,
      continuousTargetParameter,
      parameter,
    );
  };

  const availableMethods = useMemo(() => {
    return resolveAvailableCalculationMethods(
      drugConfig,
      administrationMode,
      continuousTargetParameter,
    );
  }, [administrationMode, drugConfig, continuousTargetParameter]);

  useEffect(() => {
    if (availableMethods.length === 0) {
      if (selectedMethod) setSelectedMethod('');
      return;
    }
    if (!availableMethods.includes(selectedMethod)) {
      setSelectedMethod(availableMethods[0]);
    }
  }, [availableMethods, selectedMethod]);

  /** 从 drugConfig 获取输入框的动态默认值, 覆盖 METHOD_CONFIG 中的静态默认值 */
  const getInputDefault = (inputName: string): number | undefined => {
    if (!drugConfig) return undefined;
    if (inputName === 'mic') {
      const configuredMic = normalizeFiniteNumber(drugConfig.default_mic);
      return configuredMic != null && configuredMic > 0 ? configuredMic : undefined;
    }
    if (inputName === 'target_ft') {
      const target = findTarget('%fT>MIC', '%fT>MIC');
      return target?.min;
    }
    if (inputName === 'target_auc') {
      const target = findTarget('Target AUC', 'AUC24');
      return target?.min ? Math.round((target.min + (target.max || target.min)) / 2) : undefined;
    }
    if (inputName === 'target_auc_mic') {
      const target = findTarget('AUC/MIC', 'AUC24/MIC');
      return target?.min ? Math.round((target.min + (target.max || target.min)) / 2) : undefined;
    }
    if (inputName === 'target_cmin') {
      const target = findTarget('Target Concentration', getPreferredTargetParameter('Target Concentration'));
      return target?.min ? Math.round((target.min + (target.max || target.min)) / 2) : undefined;
    }
    if (inputName === 'target_cmax') {
      const target = findTarget('Target Cmax', 'Cmax');
      return target?.min ? Math.round((target.min + (target.max || target.min)) / 2) : undefined;
    }
    return undefined;
  };

  // 当 selectedMethod 变化时, 重置表单默认值; drugConfig 首次加载后仅补填空字段
  const prevMethodRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (!methodDef) return;
    const isMethodChange = prevMethodRef.current !== selectedMethod;
    prevMethodRef.current = selectedMethod;
    if (isMethodChange) initialMicTouchedRef.current = false;

    // drugConfig 后续变化（非首次）且方法未变 → 不覆盖用户已填的值
    const isDrugConfigFirstLoad = !drugConfigAppliedRef.current && drugConfig != null;
    if (drugConfig != null) drugConfigAppliedRef.current = true;

    const currentValues = form.getFieldsValue();
    const values: Record<string, number | undefined> = {};
    for (const input of methodDef.inputs) {
      const dynamicDefault = getInputDefault(input.name);
      if (isMethodChange) {
        // 切换方法 → 始终重置为新方法的默认值
        values[input.name] = dynamicDefault;
      } else if (isDrugConfigFirstLoad && currentValues[input.name] == null) {
        // drugConfig 首次加载 → 仅补填空字段
        values[input.name] = dynamicDefault;
      }
    }
    if (Object.keys(values).length > 0) {
      form.setFieldsValue(values);
    }
  }, [administrationMode, drugConfig, selectedMethod]);

  const handleInitialDose = async (values: any) => {
    if (!selectedModel) {
      message.warning('请先选择PK模型');
      return;
    }
    const modelModes = selectedModel.administration_modes?.length
      ? selectedModel.administration_modes
      : ['intermittent'];
    if (!modelModes.includes(administrationMode)) {
      message.error('当前模型不支持所选给药方式，请重新选择适用模型');
      return;
    }
    setLoading(true);
    try {
      // 根据选定方法，从表单值中提取对应的靶值
      let targetAuc = values.target_auc;
      if (selectedMethod === 'AUC/MIC') targetAuc = undefined;
      // 如果仍然没有，从 drugConfig.targets 获取中位值
      if (!targetAuc && selectedMethod !== 'AUC/MIC' && drugConfig?.targets) {
        const aucTarget = findTarget('Target AUC', 'AUC24');
        if (aucTarget) targetAuc = Math.round((aucTarget.min + (aucTarget.max || aucTarget.min)) / 2);
      }

      const loadingValue = normalizeFiniteNumber(loadingDraft?.value ?? loadingProfile?.value);
      const loadingCountValue = normalizeFiniteNumber(loadingDraft?.count ?? loadingProfile?.count) ?? 1;
      const loadingCount = Math.min(Math.max(Math.round(loadingCountValue), 1), 20);
      const loadingInterval = loadingCount > 1
        ? normalizeFiniteNumber(loadingDraft?.interval_h ?? loadingProfile?.interval_h) ?? undefined
        : undefined;
      const loadingInfusion = administrationMode === 'intermittent'
        ? normalizeFiniteNumber(values.infusion_duration_h)
          ?? normalizeFiniteNumber(loadingDraft?.infusion_duration_h ?? loadingProfile?.infusion_duration_h)
          ?? undefined
        : 0;
      const loadingStrategy = loadingSupported && loadingProfile && loadingEnabled && loadingValue != null
        ? {
            enabled: true,
            profile_id: loadingDraft?.profile_id || loadingProfile.profile_id,
            administration_mode: administrationMode,
            basis: loadingDraft?.basis || loadingProfile.basis || 'mg_kg',
            value: loadingValue,
            count: loadingCount,
            ...(loadingInterval != null ? { interval_h: loadingInterval } : {}),
            ...(loadingInfusion != null ? { infusion_duration_h: loadingInfusion } : {}),
          }
        : loadingSupported && loadingProfile
          ? { enabled: false }
          : undefined;

      const res = await doseAPI.calculateInitial({
        case_id: caseId,
        model_id: selectedModel.model_id,
        administration_mode: administrationMode,
        infusion_duration_h: administrationMode === 'intermittent' && initialInfusionTouchedRef.current
          ? normalizeFiniteNumber(values.infusion_duration_h) ?? undefined
          : undefined,
        target_auc: targetAuc || undefined,
        target_type: selectedMethod,
        target_value: values.target_ft ?? values.target_cmin ?? values.target_cmax ?? values.target_auc_mic ?? undefined,
        mic: initialMicTouchedRef.current ? normalizeFiniteNumber(values.mic) ?? undefined : undefined,
        loading_strategy: loadingStrategy,
      });
      setSubmittedTargetAucMic(selectedMethod === 'AUC/MIC' ? normalizeFiniteNumber(values.target_auc_mic) : null);
      setResult(res);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ')
        : (typeof detail === 'string' ? detail : '计算失败');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePKFormula = async (values: any) => {
    setLoading(true);
    setPkResult(null);
    try {
      const targetFormValues = form.getFieldsValue();
      const aucTarget = findTarget('Target AUC', 'AUC24');
      const defaultAucMid = aucTarget?.min ? Math.round((aucTarget.min + (aucTarget.max || aucTarget.min)) / 2) : undefined;
      let explicitTargetAuc = targetFormValues.target_auc;
      if (selectedMethod === 'Target AUC' && defaultAucMid != null && explicitTargetAuc === defaultAucMid) {
        explicitTargetAuc = undefined;
      }
      if (selectedMethod === 'AUC/MIC') explicitTargetAuc = undefined;

      const pkPayload: Parameters<typeof doseAPI.pkFormula>[0] = {
        case_id: caseId,
        administration_mode: administrationMode,
        dose: values.dose,
        infusion_duration: values.infusion_duration,
        infusion_duration_source: (pkInfusionTouchedRef.current ? 'user_input' : 'drug_config_default') as 'user_input' | 'drug_config_default',
        dosing_interval: values.dosing_interval,
        weight: covariates.weight,
        target_auc: explicitTargetAuc,
        target_type: selectedMethod,
        target_value: targetFormValues.target_ft ?? targetFormValues.target_cmin ?? targetFormValues.target_cmax ?? targetFormValues.target_auc_mic ?? undefined,
        mic: initialMicTouchedRef.current
          ? normalizeFiniteNumber(targetFormValues.mic) ?? undefined
          : undefined,
        ...(drugConfig?.pk_formula?.strategy_key === 'meropenem_chen_2018_ss_two_point_ftmic'
          ? {
              inputs: {
                dose: values.dose,
                dosing_interval: values.dosing_interval,
                infusion_duration: values.infusion_duration,
                early_concentration: values.early_concentration,
                early_sample_before_next_dose_h: values.early_sample_before_next_dose_h,
                late_concentration: values.late_concentration,
                late_sample_before_next_dose_h: values.late_sample_before_next_dose_h,
                steady_state_confirmed: values.steady_state_confirmed !== false,
              },
            }
          : {
              measured_peak: values.measured_peak,
              measured_trough: values.measured_trough,
              t_peak: values.t_peak,
              t_trough: values.t_trough,
            }),
      };
      if (tdmReplayDraft?.pkFormulaInput) {
        Object.assign(pkPayload, tdmReplayDraft.pkFormulaInput);
      }
      const res = tdmReplayDraft?.source === 'admin-data-query'
        ? await adminAPI.replayPkFormula(pkPayload)
        : await doseAPI.pkFormula(pkPayload);
      setSubmittedTargetAucMic(selectedMethod === 'AUC/MIC' ? normalizeFiniteNumber(targetFormValues.target_auc_mic) : null);
      setPkResult(res);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ')
        : (typeof detail === 'string' ? detail : '计算失败');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRemedialDose = async (values: any) => {
    if (!selectedModel) {
      message.warning('请先选择PK模型');
      return;
    }
    setLoading(true);
    try {
      const res = await doseAPI.remedial({
        case_id: caseId,
        model_id: selectedModel.model_id,
        administration_mode: administrationMode,
        current_regimen: {
          dose_mg: Number(values.dose_mg),
          interval_h: Number(values.interval_h),
        },
        scheduled_time: formatDateTimeToMinuteString(values.scheduled_time) || values.scheduled_time,
        noticed_time: formatDateTimeToMinuteString(values.noticed_time) || values.noticed_time,
        risk_preference: values.risk_preference || 'balanced',
        prefer_simple_doses: Boolean(values.prefer_simple_doses),
        return_top_n: 3,
      });
      setRemedialResult(res);
    } catch (err: any) {
      const isTimeout = err?.code === 'ECONNABORTED'
        || String(err?.message || '').toLowerCase().includes('timeout');
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ')
        : (typeof detail === 'string'
          ? detail
          : isTimeout
            ? '补服方案计算耗时较长，前端等待已超时；后台可能仍在完成并保存结果，请稍后在病例记录中查看。'
            : '补服方案计算失败');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const hasPkpdTargetMethods = availableMethods.length > 0;
  const calculationMethodConfigurationIssue = getCalculationMethodConfigurationIssue(
    drugConfig,
    administrationMode,
    continuousTargetParameter,
  );
  const canShowPkFormula = hasPkpdTargetMethods
    && drugConfig?.supports_pk_formula === true
    && !isOralMode
    && !isBolusMode;
  const tabItems = [];

  // 初始给药(PopPK)标签
  if (selectedModel && hasPkpdTargetMethods) {
    tabItems.push({
      key: 'model',
      label: modelTabLabel,
      children: (
        <div className="dose-calculation__tab-panel">
          <Card size="small" className="dose-calculation__model-card dose-calculation__section-card">
            {isMobileShell ? (
              <div>
                <div className="dose-calculation__model-name">{selectedModelDisplayName}</div>
                <div className="dose-calculation__model-subtitle">
                  {[selectedModel.publication_info.first_author, selectedModel.publication_info.year]
                    .filter(Boolean)
                    .join(' · ') || '已选用于当前初始剂量计算'}
                </div>
                <div className="dose-calculation__model-meta">
                  <div>
                    <span className="dose-calculation__model-meta-label">匹配评分</span>
                    <div className="dose-calculation__model-meta-value">{selectedModel.score}/100</div>
                  </div>
                  <div>
                    <span className="dose-calculation__model-meta-label">PMID</span>
                    <div className="dose-calculation__model-meta-value">{selectedModel.publication_info.pmid || 'N/A'}</div>
                  </div>
                  <div className="dose-calculation__model-meta-item--full">
                    <span className="dose-calculation__model-meta-label">适用人群</span>
                    <div className="dose-calculation__model-meta-value">{selectedModel.population || 'N/A'}</div>
                  </div>
                </div>
              </div>
            ) : (
              <Descriptions size="small" column={3}>
                <Descriptions.Item label="选用模型">{selectedModelDisplayName}</Descriptions.Item>
                <Descriptions.Item label="匹配评分">{selectedModel.score}/100</Descriptions.Item>
                <Descriptions.Item label="PMID">{selectedModel.publication_info.pmid || 'N/A'}</Descriptions.Item>
              </Descriptions>
            )}
          </Card>

          <Form
            form={form}
            className="dose-calculation__inline-form"
            layout={isMobileShell ? 'vertical' : 'inline'}
            onFinish={handleInitialDose}
            onValuesChange={(changedValues) => {
              if (Object.prototype.hasOwnProperty.call(changedValues || {}, 'mic')) {
                initialMicTouchedRef.current = true;
              }
            }}
          >
            {(supportedAdministrationModes.length > 1 || (loadingSupported && loadingProfile)) && (
              <div className="dose-calculation__mode-loading-row">
                {supportedAdministrationModes.length > 1 && (
                  <Form.Item label="给药模式" style={{ marginBottom: 12 }}>
                    <Radio.Group
                      value={administrationMode}
                      onChange={(event) => handleAdministrationModeChange(event.target.value)}
                    >
                      {supportedAdministrationModes.includes('intermittent') && (
                        <Radio.Button value="intermittent">静脉滴注</Radio.Button>
                      )}
                      {supportedAdministrationModes.includes('continuous') && (
                        <Radio.Button value="continuous">连续输注</Radio.Button>
                      )}
                      {supportedAdministrationModes.includes('oral') && (
                        <Radio.Button value="oral">口服</Radio.Button>
                      )}
                      {supportedAdministrationModes.includes('bolus') && (
                        <Radio.Button value="bolus">静脉注射</Radio.Button>
                      )}
                    </Radio.Group>
                  </Form.Item>
                )}
                {loadingSupported && loadingProfile && (
                  <div className="dose-calculation__loading-toggle">
                    <Typography.Text strong>使用负荷剂量</Typography.Text>
                    <Switch
                      checked={loadingEnabled}
                      onChange={setLoadingEnabled}
                      disabled={!loadingUserOverrideAllowed}
                      checkedChildren="是"
                      unCheckedChildren="否"
                    />
                  </div>
                )}
              </div>
            )}
            {availableMethods.length > 1 && (
              <Form.Item label="计算方法" style={{ marginBottom: 12 }}>
                <div className="dose-calculation__segmented-scroll">
                  <Segmented
                    value={selectedMethod}
                    onChange={(val) => setSelectedMethod(val as string)}
                    options={availableMethods.map((m: string) => ({
                      value: m,
                      label: (
                        <Tooltip title={METHOD_CONFIG[m]?.description || m}>
                          <span
                            style={{ padding: '0 4px' }}
                            dangerouslySetInnerHTML={{ __html: METHOD_CONFIG[m]?.label || m }}
                          />
                        </Tooltip>
                      ),
                    }))}
                  />
                </div>
              </Form.Item>
            )}
            {loadingSupported && loadingProfile && loadingEnabled && (
              <Card
                size="small"
                className="dose-calculation__loading-card"
                title={(
                  <Space size={6}>
                    <ThunderboltOutlined style={{ color: '#0F62FE' }} />
                    <span>首剂负荷方案</span>
                  </Space>
                )}
              >
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      label={`负荷剂量 (${getDoseDisplayUnit(drugConfig, loadingProfile.basis)})`}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        min={0.001}
                        disabled={!loadingUserOverrideAllowed}
                        value={loadingDraft?.value}
                        onChange={(value) => setLoadingDraft((prev) => ({ ...(prev || loadingProfile), value: value ?? undefined }))}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={7}>
                    <Form.Item label="给药剂次" style={{ marginBottom: 0 }}>
                      <InputNumber
                        min={1}
                        max={20}
                        precision={0}
                        disabled={!loadingUserOverrideAllowed}
                        value={loadingDraft?.count ?? 1}
                        onChange={(value) => setLoadingDraft((prev) => ({ ...(prev || loadingProfile), count: value ?? 1 }))}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  {(Number(loadingDraft?.count) || 1) > 1 && (
                    <Col xs={24} sm={12} md={7}>
                      <Form.Item label="负荷间隔 (h)" style={{ marginBottom: 0 }}>
                        <InputNumber
                          min={0.1}
                          disabled={!loadingUserOverrideAllowed}
                          value={loadingDraft?.interval_h}
                          onChange={(value) => setLoadingDraft((prev) => ({ ...(prev || loadingProfile), interval_h: value ?? undefined }))}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </Card>
            )}
            <Row className="dose-calculation__initial-target-row" gutter={[12, 12]} align="bottom">
              {methodDef?.inputs.map(input => {
                const inputLabel = input.name === 'target_cmin' && isContinuousMode ? '目标Css' : input.label;
                const inputUnit = getConfiguredInputUnit(
                  drugConfig,
                  input,
                  selectedMethod,
                  administrationMode,
                  continuousTargetParameter,
                );
                const displayLabel = inputUnit ? `${inputLabel} (${inputUnit})` : inputLabel;
                return (
                  <Col key={input.name} xs={24} sm={12} md={6} lg={5}>
                    <Form.Item
                      name={input.name}
                      label={displayLabel}
                      rules={[{ required: true, message: `请输入${inputLabel}` }]}
                    >
                      <InputNumber
                        min={0}
                        step={input.name === 'mic' ? 0.125 : (input.step || 1)}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                );
              })}
              {administrationMode === 'intermittent' && (
                <Col xs={24} sm={12} md={6} lg={5}>
                  <Form.Item
                    name="infusion_duration_h"
                    label="输注时间 (h)"
                    tooltip="初始值来自后台药物配置的默认输注时间；手动调整后，本次初始优化与候选方案统一采用该值。"
                    rules={[
                      { required: true, message: '请输入输注时间' },
                      { type: 'number', min: 0.1, message: '输注时间必须大于 0' },
                      ...(shortestCandidateIntervalH != null
                        ? [{ type: 'number' as const, max: shortestCandidateIntervalH, message: `输注时间不得超过最短候选间隔 ${shortestCandidateIntervalH} h` }]
                        : []),
                    ]}
                  >
                    <InputNumber
                      min={0.1}
                      max={shortestCandidateIntervalH}
                      step={0.1}
                      precision={2}
                      style={{ width: '100%' }}
                      onChange={() => {
                        initialInfusionTouchedRef.current = true;
                      }}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} sm={12} md={6} lg={5} className="dose-calculation__initial-action-col">
                <Form.Item>
                  <Button
                    className="dose-calculation__primary-btn"
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<CalculatorOutlined />}
                    block={isMobileShell}
                    size={isMobileShell ? 'large' : 'middle'}
                  >
                    计算给药方案
                  </Button>
                </Form.Item>
              </Col>
            </Row>
	          </Form>

	          {isContinuousMode && (
	            <Alert
	              className="dose-calculation__soft-alert"
	              type="success"
	              showIcon
	              message="当前为连续输注模式"
	              description="该模式按连续零级输入优化，不再受 4 小时输注上限约束；维持总日剂量与泵速按连续输注展示，但主靶值仍由当前计算方法决定。"
	            />
	          )}

          {result && <DoseResultDisplay result={result} weight={covariates.weight} covariates={covariates} selectedMethod={selectedMethod} administrationMode={administrationMode} drugConfig={drugConfig} submittedTargetAucMic={submittedTargetAucMic} onContinue={onCalculated} caseId={caseId} modelId={selectedModel?.model_id ?? ''} />}
        </div>
      ),
    });
  }

  // 剂量优化标签 — 排在初始给药之后
  if (hasPkpdTargetMethods) {
    tabItems.push({
      key: 'optimize',
      label: optimizeTabLabel,
      children: (
        <TDMOptimizePanel
          caseId={caseId}
          covariates={covariates}
          lastModelId={selectedModel?.model_id}
          drugName={drugName}
          administrationMode={administrationMode}
          replayDraft={tdmReplayDraft}
          onCalculated={onCalculated}
        />
      ),
    });
  }

  if (remedialEnabled && selectedModel) {
    tabItems.push({
      key: 'remedial',
      label: remedialTabLabel,
      children: (
        <RemedialDosePanel
          form={remedialForm}
          loading={loading}
          result={remedialResult}
          onFinish={handleRemedialDose}
          drugConfig={drugConfig}
          isMobileShell={isMobileShell}
          onGenerateReport={onRemedialCalculated}
        />
      ),
    });
  }

  // PK公式法标签 — 排在最后（受后台 supports_pk_formula 开关控制）
  if (canShowPkFormula) tabItems.push({
    key: 'pk_formula',
    label: pkFormulaTabLabel,
    children: (
      <div className="dose-calculation__tab-panel">
        <Alert
          message="PK公式法说明"
          description="基于实测峰浓度和谷浓度,使用两点法计算个体PK参数。适用于无匹配PopPK模型的情况。"
          type="info"
          showIcon
          className="dose-calculation__soft-alert"
          style={{ marginBottom: 0 }}
        />

        <Form
          form={pkForm}
          className="dose-calculation__pk-form"
          layout="vertical"
          onFinish={handlePKFormula}
          disabled={isContinuousMode}
          onValuesChange={(changedValues) => {
            if (Object.prototype.hasOwnProperty.call(changedValues || {}, 'infusion_duration')) {
              pkInfusionTouchedRef.current = true;
            }
          }}
        >
           {drugConfig?.pk_formula?.strategy_key !== 'meropenem_chen_2018_ss_two_point_ftmic' && <Row gutter={16}>
            <Col xs={24} md={12} xl={6}>
              <Form.Item name="dose" label={`给药剂量 (${pkDoseMassUnit})`} rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} placeholder="如: 45" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Form.Item name="measured_peak" label={`实测峰浓度 (${pkConcentrationUnit})`} rules={[{ required: true }]}>
                <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="如: 25" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Form.Item name="measured_trough" label={`实测谷浓度 (${pkConcentrationUnit})`} rules={[{ required: true }]}>
                <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="如: 8" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Form.Item name="dosing_interval" label="给药间隔 (h)" rules={[{ required: true, message: '请输入给药间隔' }]}>
                <InputNumber min={4} max={48} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>}
          {drugConfig?.pk_formula?.strategy_key !== 'meropenem_chen_2018_ss_two_point_ftmic' && <Row gutter={16}>
            <Col xs={24} md={12} xl={6}>
              <Form.Item name="infusion_duration" label="输注时间 (h)" rules={[{ required: true, message: '请输入输注时间' }]}>
                <InputNumber min={0.5} max={4} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Form.Item name="t_peak" label="输注结束至峰浓度采样 (h)" rules={[{ required: true, message: '请输入峰浓度采样时间' }]}>
                <InputNumber min={0} max={4} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Form.Item name="t_trough" label="谷浓度距下次给药 (h)" rules={[{ required: true }]}>
                <InputNumber min={0} max={4} step={0.5} style={{ width: '100%' }} placeholder="如: 0.5" />
              </Form.Item>
            </Col>
           </Row>}
           {drugConfig?.pk_formula?.strategy_key === 'meropenem_chen_2018_ss_two_point_ftmic' && (
            <>
            <Row gutter={16}>
              <Col xs={24} md={12} xl={8}>
                <Form.Item name="dose" label={`给药剂量 (${pkDoseMassUnit})`} rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <Form.Item name="dosing_interval" label="给药间隔 (h)" rules={[{ required: true }]}>
                  <InputNumber min={4} max={48} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <Form.Item name="infusion_duration" label="输注时间 (h)" rules={[{ required: true }]}>
                  <InputNumber min={0.5} max={4} step={0.5} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={12} xl={6}>
                <Form.Item name="early_concentration" label={`较早消除相浓度 (${pkConcentrationUnit})`} rules={[{ required: true }]}>
                  <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={6}>
                <Form.Item name="early_sample_before_next_dose_h" label="较早样本距下次给药时间 (h)" initialValue={3} rules={[{ required: true }]}>
                  <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={6}>
                <Form.Item name="late_concentration" label={`较晚消除相浓度 (${pkConcentrationUnit})`} rules={[{ required: true }]}>
                  <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={6}>
                <Form.Item name="late_sample_before_next_dose_h" label="较晚样本距下次给药时间 (h)" initialValue={0.5} rules={[{ required: true }]}>
                  <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            </>
          )}
          <Form.Item>
            <Button
              className="dose-calculation__primary-btn"
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<CalculatorOutlined />}
              block={isMobileShell}
              size={isMobileShell ? 'large' : 'middle'}
            >
              计算PK参数
            </Button>
          </Form.Item>
        </Form>

        {pkResult && (
          <PKFormulaResultDisplay
            result={pkResult}
            onContinue={onPKFormulaCalculated || onCalculated}
            drugConfig={drugConfig}
            selectedMethod={selectedMethod}
            weight={covariates.weight}
            covariates={covariates}
            adminReplay={tdmReplayDraft?.source === 'admin-data-query'}
          />
        )}
      </div>
    ),
  });

  const tabKeys = tabItems.map((item) => item.key);
  const tabKeySignature = tabKeys.join('|');
  useEffect(() => {
    if (tabKeys.length === 0) {
      if (activeTab !== undefined) setActiveTab(undefined);
      return;
    }
    if (activeTab && tabKeys.includes(activeTab)) return;
    const preferred = initialTab && tabKeys.includes(initialTab) ? initialTab : tabKeys[0];
    setActiveTab(preferred);
  }, [activeTab, initialTab, tabKeySignature]);

  return (
    <div className="dose-calculation dose-calculation__shell">
      <style>{DOSE_CALCULATION_STYLES}</style>
      <section className="dose-calculation__hero">
        <div className="dose-calculation__hero-copy">
          <div className="dose-calculation__hero-eyebrow">剂量方案设计</div>
          <div className="dose-calculation__hero-title">剂量计算</div>
          {/* <div className="dose-calculation__hero-subtitle">
            在同一页内完成初始给药、TDM 优化和 PK 公式评估，保留关键指标、模型来源与结果解释，
            但减少工程化噪音，让临床决策视线更集中。
          </div> */}
        </div>
        <div className="dose-calculation__hero-tags">
          {drugName ? <Tag className="dose-calculation__hero-tag">{getDrugLabel(drugName, drugName)}</Tag> : null}
          {selectedModelDisplayName ? <Tag className="dose-calculation__hero-tag">{selectedModelDisplayName}</Tag> : null}
          {selectedMethod ? <Tag className="dose-calculation__hero-tag">{selectedMethod}</Tag> : null}
          <Tag className="dose-calculation__hero-tag">{getAdministrationModeLabel(administrationMode)}模式</Tag>
        </div>
      </section>
      {hasSummaryCard && (
        <Card className="dose-calculation__summary-card" bordered={false}>
          <div className="dose-calculation__summary-shell">
            <section className="dose-calculation__summary-hero">
              <div className="dose-calculation__summary-hero-header">
                <div>
                  <div className="dose-calculation__summary-title">{"\u60a3\u8005\u4fe1\u606f"}</div>
                  <div className="dose-calculation__summary-caption">{summaryCardCopy}</div>
                </div>
              </div>
              {summaryEntries.length > 0 ? (
                <div className="dose-calculation__summary-table">
                  {summaryEntries.map((entry) => (
                    <div className="dose-calculation__summary-row" key={entry.key}>
                      <span className="dose-calculation__summary-row-label">{entry.label}</span>
                      <div className="dose-calculation__summary-row-value">{entry.value}</div>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          </div>
        </Card>
      )}
      {calculationMethodConfigurationIssue ? (
        <Alert
          className="dose-calculation__soft-alert"
          type="warning"
          showIcon
          message="当前给药模式暂不可计算"
          description={`${calculationMethodConfigurationIssue}请联系管理员检查药物的“计算方法”和“靶标适用给药模式”配置。`}
        />
      ) : (
        <Tabs
          className="dose-calculation__tabs"
          items={tabItems}
          activeKey={activeTab}
          onChange={setActiveTab}
          size={isMobileShell ? 'small' : 'middle'}
          tabBarGutter={isMobileShell ? 8 : 16}
        />
      )}
    </div>
  );
};

const formatRemedialDose = (
  value: number | string | null | undefined,
  displayMassUnit = 'mg',
): string => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return `0 ${displayMassUnit}`;
  const converted = convertUnitValue(numeric, 'mg', displayMassUnit, 'mass');
  const formatted = converted.toFixed(3).replace(/\.?0+$/, '');
  return `${formatted || '0'} ${displayMassUnit}`;
};

const formatRemedialHours = (value: number | null | undefined): string => `${Number(value || 0).toFixed(2)} h`;

const getRemedialTotalDeviation = (candidate?: RemedialCandidate | null): number => (
  Number(candidate?.time_above_upper_h || 0) + Number(candidate?.time_below_lower_h || 0)
);

const resolveRemedialStrategyDisplay = (
  candidate: RemedialCandidate,
): { code: string; title: string } => {
  if (candidate.is_custom && candidate.display_label) {
    return {
      code: candidate.platform_strategy_code || '自定义方案',
      title: candidate.strategy_interpretation || candidate.display_label,
    };
  }
  return {
    code: candidate.platform_strategy_code || '候选策略',
    title: candidate.strategy_interpretation || candidate.temporal_pattern_label || '候选给药策略',
  };
};

const getTherapeuticWindowSourceLabel = (source?: string): string => {
  const normalized = String(source || '').toLowerCase();
  if (normalized.includes('ipred_p05_p95') || normalized.includes('simulation_reference')) {
    return '稳态 Monte Carlo 参考：5% 谷浓度 / 95% 峰浓度';
  }
  if (normalized.includes('configured') || normalized.includes('manual')) {
    return '后台固定治疗窗';
  }
  return '后台配置';
};

export const RemedialDosePanel: React.FC<{
  form: any;
  loading: boolean;
  result: RemedialDoseResult | null;
  onFinish: (values: any) => void;
  drugConfig?: any;
  isMobileShell: boolean;
  onGenerateReport?: (result: RemedialDoseResult) => void;
}> = ({ form, loading, result, onFinish, drugConfig, isMobileShell, onGenerateReport }) => {
  const remedialConfig = drugConfig?.remedial_dosing || {};
  const defaultDose = remedialConfig.default_dose_mg
    || remedialConfig.regular_dose_mg
    || remedialConfig.full_strengths_mg?.[0];
  const defaultInterval = normalizeFiniteNumber(remedialConfig.default_interval_h) ?? undefined;
  const window = result?.therapeutic_window || {};
  const doseMassUnit = unitFromResult(
    result,
    'dose',
    drugConfig?.units?.dose?.display_mass_unit || 'mg',
  );
  const formatDose = (value: number | string | null | undefined) => formatRemedialDose(value, doseMassUnit);
  const concentrationUnit = unitFromResult(
    result,
    'concentration',
    drugConfig?.units?.concentration?.display_unit || 'mg/L',
  );
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [customCandidates, setCustomCandidates] = useState<RemedialCandidate[]>([]);
  const [editingCandidateId, setEditingCandidateId] = useState<string | null>(null);
  const [editingDosesMg, setEditingDosesMg] = useState<[number, number] | null>(null);
  const [customCandidateLoading, setCustomCandidateLoading] = useState(false);
  const [interpretationByCandidate, setInterpretationByCandidate] = useState<Record<string, string>>({});
  const [interpretLoading, setInterpretLoading] = useState(false);

  useEffect(() => {
    setCustomCandidates([]);
    setEditingCandidateId(null);
    setEditingDosesMg(null);
    setInterpretationByCandidate({});
  }, [result?.calculation_id]);

  const rankedCandidates = useMemo(() => {
    const originals = result?.display_candidates?.length
      ? result.display_candidates
      : [...(result?.representative_candidates || []), ...(result?.top_candidates || [])]
        .filter((candidate, index, values) => values.findIndex((item) => item.id === candidate.id) === index);
    const customByBase = new Map(customCandidates.map((candidate) => [candidate.base_candidate_id || candidate.id, candidate]));
    const candidates = originals.flatMap((candidate) => [
      candidate,
      ...(customByBase.get(candidate.id) ? [customByBase.get(candidate.id)!] : []),
    ]);
    return [...candidates].sort((a, b) => (
      a.score - b.score
      || (a.time_above_upper_h + a.time_below_lower_h) - (b.time_above_upper_h + b.time_below_lower_h)
      || a.complexity_score - b.complexity_score
    ));
  }, [result?.display_candidates, result?.representative_candidates, result?.top_candidates, customCandidates]);

  const best = rankedCandidates[0] || result?.top_candidates?.[0];
  useEffect(() => {
    if (rankedCandidates.length === 0) {
      if (selectedCandidateId !== null) setSelectedCandidateId(null);
      return;
    }
    if (!selectedCandidateId || !rankedCandidates.some((candidate) => candidate.id === selectedCandidateId)) {
      setSelectedCandidateId(rankedCandidates[0].id);
    }
  }, [rankedCandidates, selectedCandidateId]);

  const selectedCandidate = rankedCandidates.find((candidate) => candidate.id === selectedCandidateId) || best;
  const recommendedCandidateId = result?.remedial_selected_candidate?.id || result?.top_candidates?.[0]?.id;
  const selectedInterpretation = selectedCandidate
    ? interpretationByCandidate[selectedCandidate.id]
      || (selectedCandidate.id === recommendedCandidateId ? result?.clinical_interpretation || '' : '')
    : '';
  const topSimulation = selectedCandidate?.simulation;
  const selectedSimulationN = Number(topSimulation?.n_subjects || window.candidate_n_sub || window.reference_n_sub || 0);
  const selectedStrategyMeta = selectedCandidate
    ? resolveRemedialStrategyDisplay(selectedCandidate)
    : null;
  const selectedConstraintWarnings = selectedCandidate?.constraint_warnings || [];
  const bestTotalDeviation = best ? getRemedialTotalDeviation(best) : null;
  const nearBestToleranceH = Number(result?.equivalence_tolerance_h ?? remedialConfig.equivalence_tolerance_h ?? 0.1);
  const nearBestCandidates = rankedCandidates.filter((candidate) => (
    bestTotalDeviation !== null
      && Math.abs(getRemedialTotalDeviation(candidate) - bestTotalDeviation) <= nearBestToleranceH
  ));
  const nearBestCandidateIds = new Set(nearBestCandidates.map((candidate) => candidate.id));
  const windowSource = String(window.source || result?.therapeutic_window_source || '');
  const windowLower = typeof window.lower === 'number'
    ? convertCanonicalValue(window.lower, concentrationUnit, 'concentration')
    : null;
  const windowUpper = typeof window.upper === 'number'
    ? convertCanonicalValue(window.upper, concentrationUnit, 'concentration')
    : null;
  const windowUnit = concentrationUnit;
  const windowRangeLabel = windowLower !== null && windowUpper !== null
    ? `${windowLower.toFixed(2)}-${windowUpper.toFixed(2)} ${windowUnit}`
    : '待后台模拟或配置';
  const referenceWindowLabel = typeof window.reference_start_h === 'number' && typeof window.reference_end_h === 'number'
    ? `${window.reference_start_h.toFixed(0)}-${window.reference_end_h.toFixed(0)} h`
    : '稳态给药区间';
  const eventLabel = result?.event_classification === 'missed' ? '完全漏服' : '延迟服药';
  const referenceDoseLabel = typeof result?.reference_dose_mg === 'number'
    ? formatDose(result.reference_dose_mg)
    : '未返回';
  const slotLabels = Array.from(
    { length: Number(result?.decision_point_count || 2) },
    (_, index) => index === 0 ? '即刻补服' : index === 1 ? '下次计划剂量' : `后续第 ${index + 1} 次`,
  );

  const displayTimeRange = useMemo<[number, number] | undefined>(() => {
    return resolveRemedialCurveDisplayRange(
      topSimulation?.time || [],
      Number(result?.current_regimen?.interval_h || 0),
      Number(result?.delay_h || 0),
    );
  }, [topSimulation?.time, result?.current_regimen?.interval_h, result?.delay_h]);

  const projectedDisplaySimulation = useMemo(() => (
    topSimulation?.time?.length && topSimulation?.concentration?.length
      ? projectCanonicalConcentrationCurve({
        time: topSimulation.time,
        concentration: topSimulation.concentration,
        concentration_p05: topSimulation.concentration_p05,
        concentration_p95: topSimulation.concentration_p95,
        concentration_min: topSimulation.concentration_min,
        concentration_max: topSimulation.concentration_max,
        n_subjects: topSimulation.n_subjects,
      }, concentrationUnit)
      : undefined
  ), [topSimulation, concentrationUnit]);

  useEffect(() => {
    if (!result || !selectedCandidate || interpretationByCandidate[selectedCandidate.id]) return;
    if (selectedCandidate.id === recommendedCandidateId && result.clinical_interpretation) {
      setInterpretationByCandidate((previous) => ({
        ...previous,
        [selectedCandidate.id]: result.clinical_interpretation || '',
      }));
      return;
    }
    let cancelled = false;
    setInterpretLoading(true);
    doseAPI.interpret(
      result.calculation_id,
      { selected_candidate: selectedCandidate },
      'Remedial Dose',
    ).then((interpretation) => {
      if (!cancelled) {
        setInterpretationByCandidate((previous) => ({
          ...previous,
          [selectedCandidate.id]: interpretation,
        }));
      }
    }).catch(() => {
      if (!cancelled) message.error('补服策略临床解读生成失败');
    }).finally(() => {
      if (!cancelled) setInterpretLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [
    interpretationByCandidate,
    recommendedCandidateId,
    result,
    selectedCandidate,
  ]);

  const renderDoseSequence = (candidate: RemedialCandidate) => (
    <Space size={4} wrap>
      {candidate.doses_mg.map((dose, index) => (
        <Tag key={`${candidate.id}-${index}`} color={index === 0 ? 'blue' : 'default'}>
          {slotLabels[index] || `后续第 ${index + 1} 次`} {formatDose(dose)}
        </Tag>
      ))}
    </Space>
  );

  const manualAdjustmentAvailable = Boolean(
    result
      && Number(result.decision_point_count || 2) === 2,
  );
  const referenceDoseMg = Number(result?.reference_dose_mg || result?.current_regimen?.dose_mg || 0);
  const configuredManualSingleRatio = Number(remedialConfig.candidate_max_dose_ratio);
  const manualDoseMaxDisplay = Number.isFinite(configuredManualSingleRatio) && configuredManualSingleRatio > 0
    ? convertCanonicalValue(referenceDoseMg * configuredManualSingleRatio, doseMassUnit, 'mass') ?? undefined
    : undefined;
  const configuredStepMg = Number(
    remedialConfig.min_split_dose_mg
      || remedialConfig.full_strengths_mg?.[0]
      || (referenceDoseMg > 0 ? referenceDoseMg / 2 : 1),
  );
  const manualDoseStepDisplay = convertCanonicalValue(configuredStepMg, doseMassUnit, 'mass') || 1;

  const beginManualAdjustment = (candidate: RemedialCandidate) => {
    if (!manualAdjustmentAvailable) return;
    const doses = candidate.doses_mg || [];
    setEditingCandidateId(candidate.base_candidate_id || candidate.id);
    setEditingDosesMg([
      Number(doses[0] || 0),
      Number(doses[1] ?? referenceDoseMg),
    ]);
  };

  const cancelManualAdjustment = () => {
    setEditingCandidateId(null);
    setEditingDosesMg(null);
  };

  const recalculateManualCandidate = async (candidate: RemedialCandidate) => {
    if (!result || !editingDosesMg || !manualAdjustmentAvailable) return;
    const baseCandidateId = candidate.base_candidate_id || candidate.id;
    setCustomCandidateLoading(true);
    try {
      const response = await doseAPI.remedialCustomCandidate({
        calculation_id: result.calculation_id,
        base_candidate_id: baseCandidateId,
        immediate_dose_mg: editingDosesMg[0],
        next_dose_mg: editingDosesMg[1],
      });
      setCustomCandidates((previous) => [
        ...previous.filter((item) => (item.base_candidate_id || item.id) !== baseCandidateId),
        response.candidate,
      ]);
      setSelectedCandidateId(response.candidate.id);
      cancelManualAdjustment();
      if (response.validation_messages?.length) {
        message.warning({
          content: response.validation_messages.join('；'),
          duration: 8,
        });
      } else {
        message.success('自定义补服方案已重新计算');
      }
    } catch (error: unknown) {
      const apiError = error as { response?: { data?: { detail?: string } }; message?: string };
      message.error(apiError.response?.data?.detail || apiError.message || '自定义补服方案计算失败');
    } finally {
      setCustomCandidateLoading(false);
    }
  };

  const removeCustomCandidate = (candidate: RemedialCandidate) => {
    const baseCandidateId = candidate.base_candidate_id || candidate.id;
    setCustomCandidates((previous) => previous.filter((item) => (item.base_candidate_id || item.id) !== baseCandidateId));
    if (selectedCandidateId === candidate.id) setSelectedCandidateId(baseCandidateId);
  };

  const columns = [
    {
      title: '策略',
      dataIndex: 'label',
      width: 92,
      render: (_: any, record: RemedialCandidate) => {
        const meta = resolveRemedialStrategyDisplay(record);
        const isNearBest = nearBestCandidateIds.has(record.id);
        return (
          <div className="dose-calculation__remedial-strategy">
            <Tooltip title={(
              <span>{meta.title}</span>
            )}>
              <Tag color={isNearBest ? 'green' : 'blue'}>{meta.code}</Tag>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: '即刻补服',
      dataIndex: 'doses_mg',
      width: 108,
      className: 'dose-calculation__remedial-dose-cell',
      render: (doses: number[], record: RemedialCandidate) => {
        const editing = editingCandidateId === record.id && !record.is_custom && editingDosesMg;
        return editing ? (
          <InputNumber
            size="small"
            min={0}
            max={manualDoseMaxDisplay}
            step={manualDoseStepDisplay}
            disabled={customCandidateLoading}
            value={convertCanonicalValue(editingDosesMg[0], doseMassUnit, 'mass') ?? 0}
            onChange={(value) => setEditingDosesMg([convertUnitValue(Number(value || 0), doseMassUnit, 'mg', 'mass'), editingDosesMg[1]])}
            onClick={(event) => event.stopPropagation()}
          />
        ) : formatDose(doses?.[0]);
      },
    },
    {
      title: '下次计划剂量',
      dataIndex: 'doses_mg',
      width: 118,
      className: 'dose-calculation__remedial-dose-cell',
      render: (doses: number[], record: RemedialCandidate) => {
        const editing = editingCandidateId === record.id && !record.is_custom && editingDosesMg;
        return editing ? (
          <InputNumber
            size="small"
            min={0}
            max={manualDoseMaxDisplay}
            step={manualDoseStepDisplay}
            disabled={customCandidateLoading}
            value={convertCanonicalValue(editingDosesMg[1], doseMassUnit, 'mass') ?? 0}
            onChange={(value) => setEditingDosesMg([editingDosesMg[0], convertUnitValue(Number(value || 0), doseMassUnit, 'mg', 'mass')])}
            onClick={(event) => event.stopPropagation()}
          />
        ) : formatDose(doses?.[1]);
      },
    },
    ...(Number(result?.decision_point_count || 2) > 2 ? [{
      title: '后续决策剂量',
      dataIndex: 'doses_mg',
      width: 180,
      className: 'dose-calculation__remedial-dose-cell',
      render: (doses: number[]) => doses.slice(2).map((dose) => formatDose(dose)).join(' / ') || '-',
    }] : []),
    {
      title: '高于治疗窗（h）',
      dataIndex: 'time_above_upper_h',
      width: 112,
      className: 'dose-calculation__remedial-metric-cell',
      render: (value: number) => formatRemedialHours(value),
    },
    {
      title: '低于治疗窗（h）',
      dataIndex: 'time_below_lower_h',
      width: 112,
      className: 'dose-calculation__remedial-metric-cell',
      render: (value: number) => formatRemedialHours(value),
    },
    {
      title: '总偏离时间（h）',
      dataIndex: 'score',
      width: 120,
      className: 'dose-calculation__remedial-metric-cell',
      render: (_: number, record: RemedialCandidate) => (
        <Text strong>{formatRemedialHours(getRemedialTotalDeviation(record))}</Text>
      ),
    },
    {
      title: '手动调整',
      key: 'manual_adjustment',
      width: 142,
      className: 'dose-calculation__remedial-action-cell',
      render: (_: unknown, record: RemedialCandidate) => {
        const isEditing = editingCandidateId === record.id && !record.is_custom;
        if (record.is_custom) {
          return (
            <Button size="small" danger disabled={customCandidateLoading} onClick={(event) => { event.stopPropagation(); removeCustomCandidate(record); }}>
              移除自定义
            </Button>
          );
        }
        if (!manualAdjustmentAvailable) {
          return <Tooltip title="手动调整界面当前支持两个优化决策时点"><Button size="small" disabled>调整</Button></Tooltip>;
        }
        if (isEditing) {
          return (
            <Space size={4}>
              <Button size="small" type="primary" loading={customCandidateLoading} onClick={(event) => { event.stopPropagation(); recalculateManualCandidate(record); }}>重算</Button>
              <Button size="small" disabled={customCandidateLoading} onClick={(event) => { event.stopPropagation(); cancelManualAdjustment(); }}>取消</Button>
            </Space>
          );
        }
        return <Button size="small" disabled={customCandidateLoading} onClick={(event) => { event.stopPropagation(); beginManualAdjustment(record); }}>调整</Button>;
      },
    },
  ];

  return (
    <div className="dose-calculation__tab-panel">
      <Form
        form={form}
        layout="vertical"
        className="dose-calculation__inline-form"
        onFinish={(values) => onFinish({
          ...values,
          dose_mg: convertUnitValue(values.dose_mg, doseMassUnit, 'mg', 'mass'),
        })}
        initialValues={{
          dose_mg: convertCanonicalValue(defaultDose, doseMassUnit, 'mass') ?? defaultDose,
          interval_h: defaultInterval,
          prefer_simple_doses: false,
          risk_preference: 'balanced',
        }}
      >
        <Row gutter={[12, 12]} align="bottom" className="dose-calculation__remedial-form-row">
          <Col xs={24} sm={12} lg={6} xl={4}>
            <Form.Item name="dose_mg" label={`患者当前单次剂量 (${doseMassUnit})`} rules={[{ required: true }]}>
              <InputNumber min={0} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={6} xl={4}>
            <Form.Item name="interval_h" label="给药间隔 (h)" rules={[{ required: true }]}>
              <InputNumber min={1} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} lg={6} xl={5}>
            <Form.Item name="scheduled_time" label="计划服药时间" rules={[{ required: true }]}>
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                placeholder="选择日期时间"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} lg={6} xl={5}>
            <Form.Item name="noticed_time" label="发现/拟补服时间" rules={[{ required: true }]}>
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                placeholder="选择日期时间"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={8} xl={6}>
            <Form.Item name="risk_preference" label="风险侧重">
              <Segmented
                block
                options={[
                  { label: '均衡', value: 'balanced' },
                  { label: '出血风险', value: 'bleeding' },
                  { label: '血栓风险', value: 'thrombosis' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6} lg={4} xl={3}>
            <Form.Item name="prefer_simple_doses" valuePropName="checked">
              <Checkbox>优先整规格</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={12} sm={6} lg={4} xl={3}>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} icon={<SafetyOutlined />} block>
                搜索
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {result && (
        <Card size="small" className="dose-calculation__section-card" title="模拟依据与治疗窗" style={{ marginTop: 12 }}>
          <Descriptions size="small" column={isMobileShell ? 1 : 2}>
            <Descriptions.Item label="延迟时间">{formatRemedialHours(result.delay_h)}</Descriptions.Item>
            <Descriptions.Item label="事件类型">{eventLabel}</Descriptions.Item>
            <Descriptions.Item label="治疗窗 (OTR)">{windowRangeLabel}</Descriptions.Item>
            <Descriptions.Item label="治疗窗来源">{getTherapeuticWindowSourceLabel(windowSource)}</Descriptions.Item>
            <Descriptions.Item label="参考区间">{referenceWindowLabel}</Descriptions.Item>
            <Descriptions.Item label="参考模拟 n">{Number(window.reference_n_sub || 0) || '未返回'}</Descriptions.Item>
            <Descriptions.Item label="候选曲线 n">{selectedSimulationN || Number(window.candidate_n_sub || 0) || '未返回'}</Descriptions.Item>
            <Descriptions.Item label="排序依据">{result.recommendation_rationale}</Descriptions.Item>
          </Descriptions>
          <Text type="secondary">当前剂量 {formatDose(result.current_regimen.dose_mg)} · 策略基准 {referenceDoseLabel}</Text>
          {nearBestCandidates.length > 1 && (
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              message={`等效策略（差值 ≤ ${nearBestToleranceH.toFixed(2)} h）：${nearBestCandidates.map((candidate) => resolveRemedialStrategyDisplay(candidate).code).join(' / ')}`}
            />
          )}
          {result.clinical_flags.includes('outside_single_event_evidence_scope') && (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              message="延迟已超过一个给药间隔，超出单次延迟/漏服研究的主要证据范围，请结合临床评估。"
            />
          )}
        </Card>
      )}

      {selectedCandidate && (
        <>
          <Card size="small" className="dose-calculation__section-card" title="选定补服策略" style={{ marginTop: 12 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space size={8} wrap>
                {selectedStrategyMeta && <Tag color="blue">{selectedStrategyMeta.code}</Tag>}
                <Text strong>当前选中</Text>
                <Text type="secondary">延迟 {formatRemedialHours(result?.delay_h)}</Text>
              </Space>
              <Text strong>{selectedStrategyMeta?.title || selectedCandidate.label}</Text>
              {renderDoseSequence(selectedCandidate)}
              {selectedConstraintWarnings.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  message={selectedConstraintWarnings.join('；')}
                />
              )}
              <Space size={6} wrap>
                <Tag color="processing">总偏离时间 {formatRemedialHours(getRemedialTotalDeviation(selectedCandidate))}</Tag>
                <Tag color="error">高于治疗窗 {formatRemedialHours(selectedCandidate.time_above_upper_h)}</Tag>
                <Tag color="warning">低于治疗窗 {formatRemedialHours(selectedCandidate.time_below_lower_h)}</Tag>
                {typeof selectedCandidate.population_expected_time_above_upper_h === 'number' && (
                  <Tag>群体期望高于 {formatRemedialHours(selectedCandidate.population_expected_time_above_upper_h)}</Tag>
                )}
                {typeof selectedCandidate.population_expected_time_below_lower_h === 'number' && (
                  <Tag>群体期望低于 {formatRemedialHours(selectedCandidate.population_expected_time_below_lower_h)}</Tag>
                )}
                {typeof selectedCandidate.max_probability_above_upper === 'number' && (
                  <Tag>最高超上限比例 {(selectedCandidate.max_probability_above_upper * 100).toFixed(1)}%</Tag>
                )}
                {typeof selectedCandidate.max_probability_below_lower === 'number' && (
                  <Tag>最高低下限比例 {(selectedCandidate.max_probability_below_lower * 100).toFixed(1)}%</Tag>
                )}
                <Tag>OTR {windowRangeLabel}</Tag>
                {selectedCandidate.is_equivalent && <Tag color="green">等效最优</Tag>}
                {typeof result?.baseline_improvement_h === 'number' && (
                  <Tag color={result.baseline_improvement_h > 0 ? 'cyan' : 'default'}>
                    较基线减少 {formatRemedialHours(Math.max(0, result.baseline_improvement_h))}
                  </Tag>
                )}
              </Space>
              <div className="dose-calculation__remedial-timeline">
                {selectedCandidate.doses_mg.map((dose, index) => (
                  <div className="dose-calculation__remedial-timepoint" key={`${selectedCandidate.id}-timeline-${index}`}>
                    <span>{slotLabels[index] || `后续第 ${index + 1} 次`}</span>
                    <strong>{formatDose(dose)}</strong>
                  </div>
                ))}
              </div>
            </Space>
          </Card>

          <div className="dose-calculation__remedial-compare-grid">
            <Card
              size="small"
              className="dose-calculation__section-card"
              title="选定方案药时曲线与给药事件"
              extra={<Text type="secondary">漏服前完整给药曲线及选定补服方案模拟</Text>}
            >
              <PKCurve
                simulationData={projectedDisplaySimulation}
                targetRange={{
                  troughMin: windowLower ?? undefined,
                  troughMax: windowUpper ?? undefined,
                }}
                concentrationUnit={concentrationUnit}
                doseEvents={(topSimulation?.dose_events || []).map((event: any) => ({
                  time: Number(event.time_h),
                  dose: Number(event.dose_mg || 0),
                  eventType: String(event.event_type || ''),
                  label: String(event.label || ''),
                  doseLabel: Number(event.dose_mg || 0) > 0
                    ? formatDose(Number(event.dose_mg))
                    : '未服用',
                  decisionIndex: Number(event.decision_index || 0) || undefined,
                }))}
                strategyLabel={selectedStrategyMeta ? `${selectedStrategyMeta.code}：${selectedStrategyMeta.title}` : undefined}
                showDeviationSegments
                timeRange={displayTimeRange}
              />
            </Card>

            <Card size="small" className="dose-calculation__section-card" title="候选方案对比">
              <Table
                className="dose-calculation__remedial-table"
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={rankedCandidates}
                columns={columns}
                tableLayout="fixed"
                scroll={isMobileShell ? { x: 900 } : undefined}
                rowSelection={{
                  type: 'radio',
                  fixed: true,
                  selectedRowKeys: selectedCandidate ? [selectedCandidate.id] : [],
                  onChange: (keys) => setSelectedCandidateId(keys[0] ? String(keys[0]) : null),
                  preserveSelectedRowKeys: false,
                }}
                rowClassName={(record: RemedialCandidate) => [
                  record.id === selectedCandidate.id ? 'ant-table-row-selected' : '',
                  nearBestCandidateIds.has(record.id) ? 'dose-calculation__remedial-row-near-best' : '',
                ].filter(Boolean).join(' ')}
                onRow={(record: RemedialCandidate) => ({
                  onClick: () => setSelectedCandidateId(record.id),
                  style: { cursor: 'pointer' },
                })}
              />
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                单选任一策略，可同步切换上方时间轴与曲线；生成报告将采用当前选定策略。
              </Text>
            </Card>
          </div>
          <ClinicalInterpretation
            interpretation={selectedInterpretation}
            regimenChanged={selectedCandidate.id !== recommendedCandidateId}
            loading={interpretLoading}
            onInterpretationChange={(text) => setInterpretationByCandidate((previous) => ({
              ...previous,
              [selectedCandidate.id]: text,
            }))}
          />
          {onGenerateReport && result && renderContinueAction(
            isMobileShell,
            () => onGenerateReport({
              ...result,
              all_candidates: rankedCandidates,
              top_candidates: rankedCandidates.slice(0, result.top_candidates?.length || 3),
              clinical_interpretation: selectedInterpretation,
              remedial_selected_candidate: selectedCandidate,
              selected_candidate_id: selectedCandidate.id,
              report_candidate_ids: Array.from(new Set([
                selectedCandidate.id,
                ...rankedCandidates.slice(0, 4).map((candidate) => candidate.id),
              ])),
            }),
          )}
        </>
      )}
    </div>
  );
};

/** 初始给药(PopPK)计算结果展示 */
/** 美罗培南公式法：原方案与选定方案始终在同一张对比卡中呈现，避免结果不断叠加。 */
const MeropenemFormulaPlanComparison: React.FC<{
  originalRegimen?: Record<string, any> | null;
  originalPkpdResult?: PkPdMethodResult;
  selectedRegimen?: Record<string, any> | null;
  selectedPkpdResult?: PkPdMethodResult;
  drugConfig?: any;
}> = ({ originalRegimen, originalPkpdResult, selectedRegimen, selectedPkpdResult, drugConfig }) => {
  const { isMobileShell } = useResponsive();
  const micUnit = drugConfig?.units?.mic?.display_unit || 'mg/L';
  const formatMic = (value: unknown) => Number.isFinite(Number(value)) ? Number(value).toFixed(2) : '-';
  const formatPercent = (value: unknown) => Number.isFinite(Number(value)) ? `${Number(value).toFixed(2)}%` : '-';
  const formatRegimen = (regimen?: Record<string, any> | null) => {
    if (!regimen || (regimen.dose_mg == null && regimen.interval_h == null)) return '未提供';
    const infusion = regimen.infusion_h ?? regimen.infusion_duration_h;
    return `${regimen.dose_mg ?? '-'} mg · q${regimen.interval_h ?? '-'}h · 输注 ${infusion ?? '-'} h`;
  };
  const normalizeResult = (pkpdResult?: PkPdMethodResult) => {
    const details = pkpdResult?.details || {};
    const rows = (details.mic_breakpoints || details.rows || []) as Array<Record<string, any>>;
    const targetMin = Number.isFinite(Number(pkpdResult?.target_min)) ? Number(pkpdResult?.target_min) : null;
    const targetMax = Number.isFinite(Number(pkpdResult?.target_max)) ? Number(pkpdResult?.target_max) : null;
    const byMic = new Map<string, Record<string, any>>();
    rows.forEach((row) => {
      const mic = Number(row.mic);
      if (!Number.isFinite(mic)) return;
      const ftMet = row.ft_met ?? row.target_met ?? (
        (targetMin == null || Number(row.ft_above_mic) >= targetMin)
        && (targetMax == null || Number(row.ft_above_mic) <= targetMax)
      );
      byMic.set(String(mic), { ...row, mic, ftMet });
    });
    return {
      byMic,
      defaultMic: details.default_mic ?? details.selected_mic,
      value: pkpdResult?.value,
      targetMin,
      targetMax,
    };
  };
  const original = normalizeResult(originalPkpdResult);
  const selected = normalizeResult(selectedPkpdResult);
  const hasSelected = Boolean(selectedRegimen && selectedPkpdResult);
  const allMics = Array.from(new Set([
    ...Array.from(original.byMic.keys()),
    ...(hasSelected ? Array.from(selected.byMic.keys()) : []),
  ])).sort((a, b) => Number(a) - Number(b));
  if (allMics.length === 0) return null;
  const defaultMic = selected.defaultMic ?? original.defaultMic;
  const renderStatus = (row?: Record<string, any>) => {
    if (!row) return <Text type="secondary">-</Text>;
    return row.ftMet
      ? <Tag color="green" icon={<CheckCircleOutlined />}>达标</Tag>
      : <Tag color="red" icon={<CloseCircleOutlined />}>未达标</Tag>;
  };
  const columns = [
    {
      title: `MIC (${micUnit})`,
      dataIndex: 'mic',
      render: (value: number, record: any) => record._isDefault
        ? <Text strong>{formatMic(value)} (默认)</Text>
        : formatMic(value),
    },
    {
      title: '原方案 %fT>MIC',
      dataIndex: 'originalFt',
      render: formatPercent,
    },
    ...(hasSelected ? [{
      title: '选定方案 %fT>MIC',
      dataIndex: 'selectedFt',
      render: formatPercent,
    }] : []),
    {
      title: '评价',
      dataIndex: 'evaluation',
      render: (_: unknown, record: any) => (
        <Space size={4} wrap>
          <Text type="secondary">原</Text>{renderStatus(record.originalRow)}
          {hasSelected && <><Text type="secondary">选</Text>{renderStatus(record.selectedRow)}</>}
        </Space>
      ),
    },
  ];
  const tableRows = allMics.map((mic) => {
    const originalRow = original.byMic.get(mic);
    const selectedRow = selected.byMic.get(mic);
    return {
      key: mic,
      mic: Number(mic),
      originalFt: originalRow?.ft_above_mic,
      selectedFt: selectedRow?.ft_above_mic,
      originalRow,
      selectedRow,
      _isDefault: defaultMic != null && Number(defaultMic) === Number(mic),
    };
  });
  const mobileRows = tableRows.map((row) => ({
    ...row,
    metrics: [
      { label: '原方案 %fT>MIC', value: formatPercent(row.originalFt) },
      ...(hasSelected ? [{ label: '选定方案 %fT>MIC', value: formatPercent(row.selectedFt) }] : []),
      { label: '评价', value: `${row.originalRow?.ftMet ? '原方案达标' : '原方案未达标'}${hasSelected ? `；${row.selectedRow?.ftMet ? '选定方案达标' : '选定方案未达标'}` : ''}` },
    ],
  }));

  return (
    <Card size="small" title="美罗培南 PK 公式法：方案结果对比" style={{ marginTop: 16 }}>
      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Card size="small" type="inner" title={<Space><Tag color="blue">基线</Tag>原给药方案</Space>}>
            <Text strong>{formatRegimen(originalRegimen)}</Text>
            <div style={{ marginTop: 6, color: '#64748B' }}>%fT&gt;MIC（默认 MIC）：{formatPercent(original.value)}</div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card size="small" type="inner" title={<Space><Tag color={hasSelected ? 'green' : 'default'}>{hasSelected ? '已选' : '待选'}</Tag>候选方案</Space>}>
            <Text strong>{hasSelected ? formatRegimen(selectedRegimen) : '尚未选择候选方案'}</Text>
            <div style={{ marginTop: 6, color: '#64748B' }}>%fT&gt;MIC（默认 MIC）：{hasSelected ? formatPercent(selected.value) : '选择后计算'}</div>
          </Card>
        </Col>
      </Row>
      <Alert
        type={hasSelected ? 'success' : 'info'}
        showIcon
        style={{ marginTop: 12 }}
        message={hasSelected ? '已选方案结果已替换候选预览，以下仅保留一份原方案/选定方案对比。' : '当前仅显示原给药方案；选择候选方案后将在此处替换为对比结果。'}
      />
      <Text type="secondary" style={{ display: 'block', margin: '12px 0 8px' }}>
        不同 MIC 断点下的 %fT&gt;MIC（PK公式法不提供群体变异参数，PTA 不适用）
      </Text>
      {isMobileShell ? (
        <div className="dose-calculation__mobile-list">
          {mobileRows.map((row) => (
            <div key={row.key} className="dose-calculation__mobile-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text strong>MIC {formatMic(row.mic)} {micUnit}</Text>
                {row._isDefault ? <Tag color="blue">默认</Tag> : null}
              </div>
              {renderMetricGrid(row.metrics)}
            </div>
          ))}
        </div>
      ) : (
        <Table
          dataSource={tableRows}
          columns={columns}
          pagination={false}
          size="small"
          rowClassName={(record: any) => record._isDefault ? 'ant-table-row-selected' : ''}
        />
      )}
    </Card>
  );
};

/** PK/PD 方法结果展示 (通用, 支持 %fT>MIC 多MIC表格 + 网格搜索) */
const PkPdResultsCard: React.FC<{
  pkpdResults?: Record<string, PkPdMethodResult>;
  preferredMethod?: string;
  evaluationRegimen?: any;
  gridSearchResults?: Record<string, GridSearchRegimen[]>;
  selectedGridRegimen?: { method: string; index: number } | null;
  onSelectGridRegimen?: (method: string, index: number, regimen: GridSearchRegimen) => void;
  onPkpdResultsChange?: (updated: Record<string, PkPdMethodResult>) => void;
  drugConfig?: any;
  administrationMode?: AdministrationMode;
  editableGridMethod?: string;
  editableGridRows?: GridSearchRegimen[];
  onEditableGridDoseChange?: (index: number, value: number | null, regimen: GridSearchRegimen) => void;
  hideFtMicBreakpointTable?: boolean;
  ftMicPanelTitle?: string;
  /** 美罗培南公式法仅显示候选方案选择，原/选结果由专属对比卡统一呈现。 */
  formulaCandidateOnly?: boolean;
}> = ({
  pkpdResults,
  preferredMethod,
  evaluationRegimen,
  gridSearchResults,
  selectedGridRegimen,
  onSelectGridRegimen,
  onPkpdResultsChange,
  drugConfig,
  administrationMode,
  editableGridMethod,
  editableGridRows,
  onEditableGridDoseChange,
  hideFtMicBreakpointTable = false,
  ftMicPanelTitle,
  formulaCandidateOnly = false,
}) => {
  const { isMobileShell } = useResponsive();
  const normalizedPkpdResults = useMemo(
    () => normalizePkpdResults(pkpdResults, preferredMethod),
    [pkpdResults, preferredMethod],
  );
  const normalizedEvaluationPkpdResults = useMemo(
    () => normalizePkpdResults(evaluationRegimen?.pkpd_results, preferredMethod),
    [evaluationRegimen?.pkpd_results, preferredMethod],
  );
  const resultAdministrationMode = normalizeAdministrationMode(
    administrationMode || evaluationRegimen?.administration_mode || 'intermittent',
  );
  const isMeropenemFormulaCard = evaluationRegimen?.strategy_key === 'meropenem_chen_2018_ss_two_point_ftmic';
  const useFormulaIntervalAccordion = usesIntervalCandidateAccordion(evaluationRegimen);
  const continuousTargetParameter = drugConfig?.continuous_infusion?.target_parameter || 'Css';
  const doseMassUnit = unitFromResult(
    evaluationRegimen,
    'dose',
    drugConfig?.units?.dose?.display_mass_unit || 'mg',
  );
  const aucUnit = unitFromResult(
    evaluationRegimen,
    'auc',
    drugConfig?.units?.auc?.display_unit || 'mg*h/L',
  );
  const micUnit = unitFromResult(
    evaluationRegimen,
    'mic',
    drugConfig?.units?.mic?.display_unit || drugConfig?.units?.concentration?.display_unit || 'mg/L',
  );
  const snapshotConcentrationUnit = unitFromResult(
    evaluationRegimen,
    'concentration',
    getConcentrationDisplayUnit(
      drugConfig,
      preferredMethod || '',
      resultAdministrationMode,
      continuousTargetParameter,
    ),
  );
  const getConcentrationUnit = (_method: string) => snapshotConcentrationUnit;
  const displayConcentration = (value: unknown) => convertCanonicalValue(
    value,
    snapshotConcentrationUnit,
    'concentration',
  );
  const displayAuc = (value: unknown) => convertCanonicalValue(value, aucUnit, 'auc');
  const displayMic = (value: unknown) => convertCanonicalValue(value, micUnit, 'concentration');
  const formatResultDose = (value: unknown) => {
    const numeric = normalizeFiniteNumber(value);
    return numeric == null ? '-' : formatCanonicalDose(numeric, { dose_mass_unit: doseMassUnit });
  };
  const formatResultDosePerKg = (value: unknown) => {
    const numeric = normalizeFiniteNumber(value);
    if (numeric == null) return '-';
    const converted = convertUnitValue(numeric, 'mg/kg', `${doseMassUnit}/kg`, 'dose_per_weight');
    return `${Number(converted.toFixed(2))} ${doseMassUnit}/kg`;
  };
  const calculateAucMic = (aucValue: number, micValue: number) => {
    if (!(micValue > 0)) return 0;
    return +(aucValue / micValue).toFixed(1);
  };
  const projectColumnValue = (value: unknown, dataKey: unknown): unknown => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return value;
    const key = String(dataKey || '').toLowerCase();
    if (key.includes('auc') && !key.includes('mic')) return displayAuc(value);
    if (/(cmin|cmax|css|concentration|trough|peak)/.test(key) && !key.includes('mic')) {
      return displayConcentration(value);
    }
    return value;
  };
  const methodDisplayUnit = (method: string): string => {
    if (method === 'Target AUC') return aucUnit;
    if (method === 'AUC/MIC') return 'h';
    if (method === 'Target Concentration' || method === 'Target Cmax') return snapshotConcentrationUnit;
    if (method === '%fT>MIC') return '%';
    return '';
  };
  const displayMethodValue = (method: string, value: unknown): number | null => {
    if (method === 'Target AUC') return displayAuc(value);
    if (method === 'Target Concentration' || method === 'Target Cmax') return displayConcentration(value);
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };
  const formatConcentrationResult = (value: unknown, fallback = '-') => (
    formatDisplayNumber(displayConcentration(value), 2, fallback)
  );
  const applySnapshotUnitToColumn = (column: any) => {
    const dataKey = String(column?.dataIndex || column?.dataKey || '').toLowerCase();
    if (!column?.title || typeof column.title !== 'string') return column;
    let unit: string | null = null;
    if (dataKey.includes('auc')) unit = aucUnit;
    else if (/(cmin|cmax|css|concentration|trough|peak)/.test(dataKey)) unit = snapshotConcentrationUnit;
    else if (dataKey.includes('dose_mg_kg') || dataKey === 'dose_kg') unit = `${doseMassUnit}/kg`;
    else if (dataKey.includes('dose_mg') || dataKey === 'dose') unit = doseMassUnit;
    if (!unit || !column.title.includes('(')) return column;
    return { ...column, title: column.title.replace(/\([^)]*\)/, `(${unit})`) };
  };
  const formatCandidateSubtitle = (intervalH?: number, infusionH?: number) => {
    if (resultAdministrationMode === 'continuous') return '连续输注';
    if (resultAdministrationMode === 'oral') return `Q${intervalH ?? '-'}h`;
    return `Q${intervalH ?? '-'}h · ${infusionH ?? '-'} h`;
  };

  if (Object.keys(normalizedPkpdResults).length === 0) return null;

  const getMethodEvaluation = (method: string, result: PkPdMethodResult) => {
    const fallbackRegimen = {
      pta: result.pta,
      target_status: result.target_status,
      max_dose_exceeded: result.details?.max_dose_exceeded,
      pkpd_results: { [method]: result },
    };

    const regimenForEvaluation = evaluationRegimen
      ? {
          ...evaluationRegimen,
          pta: evaluationRegimen?.pta ?? result.pta,
          pkpd_results: {
            ...normalizedEvaluationPkpdResults,
            [method]: result,
          },
        }
      : fallbackRegimen;

    const regimenStatus = resolveRegimenStatus({
      regimen: regimenForEvaluation,
      selectedMethod: method,
      drugConfig,
    });
    const evaluationStatus = resolveEvaluationStatus(
      regimenStatus,
      regimenForEvaluation?.pta ?? result.pta,
    );

    return { regimenStatus, evaluationStatus };
  };

  const micBreakpointColumns = [
    {
      title: `MIC (${micUnit})`,
      dataIndex: 'mic',
      render: (v: number, record: any) => {
        const isDefault = record._isDefault;
        const projected = formatDisplayNumber(displayMic(v), 2);
        return isDefault ? <Text strong>{projected} (默认)</Text> : projected;
      },
    },
    {
      title: '%fT>MIC',
      dataIndex: 'ft_above_mic',
      render: (v: number) => `${v}%`,
    },
    {
      title: '评估结果',
      dataIndex: 'ft_met',
      render: (v: boolean | undefined, record: any) => {
        const met = v ?? record.target_met;
        return met
          ? <Tag color="green" icon={<CheckCircleOutlined />}>达标</Tag>
          : <Tag color="red" icon={<CloseCircleOutlined />}>未达标</Tag>;
      },
    },
  ];

  const getGridColumns = (method: string, editable = false) => {
    const concentrationUnit = getConcentrationUnit(method);
    if (editable) {
      const editableColumns = [
        {
          title: '\u9009\u62e9',
          dataIndex: 'select',
          width: 60,
          render: (_: any, record: GridSearchRegimen, index: number) => (
            <Radio
              checked={selectedGridRegimen?.method === method && selectedGridRegimen?.index === index}
              disabled={Boolean((record as any)._recalc_loading || (record as any)._recalc_error)}
              onChange={() => {
                if (!(record as any)._recalc_loading && !(record as any)._recalc_error) {
                  onSelectGridRegimen?.(method, index, record);
                }
              }}
            />
          ),
        },
        {
          title: '\u7ed9\u836f\u95f4\u9694',
          dataIndex: 'interval_h',
          render: (value: number) => `Q${value}h`,
        },
        {
          title: `\u8c03\u6574\u5242\u91cf (${doseMassUnit})`,
          dataIndex: 'dose_mg',
          render: (value: number, record: GridSearchRegimen, index: number) => (
            <div onClick={(event) => event.stopPropagation()}>
              <InputNumber
                size="small"
                min={1}
                step={1}
                value={convertCanonicalValue(value, doseMassUnit, 'mass') ?? undefined}
                style={{ width: 112 }}
                onChange={(nextValue) => onEditableGridDoseChange?.(
                  resolveCandidateSourceIndex(record, index),
                  nextValue == null ? null : convertUnitValue(nextValue, doseMassUnit, 'mg', 'mass'),
                  record,
                )}
              />
            </div>
          ),
        },
        {
          title: `\u5242\u91cf (${doseMassUnit}/kg)`,
          dataIndex: 'dose_mg_kg',
          render: (value: number, record: any) => (
            <span style={record?._modified ? { color: '#0B2545', fontWeight: 500 } : undefined}>
              {formatResultDosePerKg(value)}
            </span>
          ),
        },
        {
          title: '预测AUC\u2082\u2084/MIC',
          dataIndex: 'auc24_mic',
          render: (value: number, record: any) => (
            <span style={record?._modified ? { color: '#0B2545', fontWeight: 500 } : undefined}>
              {value != null ? Number(value).toFixed(1) : '-'}
            </span>
          ),
        },
        {
          title: '\u9884\u6d4bAUC\u2082\u2084 (mg\u00b7h/L)',
          dataIndex: 'predicted_auc24',
          render: (value: number, record: any) => (
            <span style={record?._modified ? { color: '#0B2545', fontWeight: 500 } : undefined}>
              {value != null ? Number(displayAuc(value)).toFixed(1) : '-'}
            </span>
          ),
        },
        {
          title: `预测Cmin (${concentrationUnit})`,
          dataIndex: 'predicted_cmin',
          render: (value: number, record: any) => (
            <span style={record?._modified ? { color: '#0B2545', fontWeight: 500 } : undefined}>
              {value != null ? Number(displayConcentration(value)).toFixed(2) : '-'}
            </span>
          ),
        },
        {
          title: `预测Cmax (${concentrationUnit})`,
          dataIndex: 'predicted_cmax',
          render: (value: number, record: any) => (
            <span style={record?._modified ? { color: '#0B2545', fontWeight: 500 } : undefined}>
              {value != null ? Number(displayConcentration(value)).toFixed(2) : '-'}
            </span>
          ),
        },
        {
          title: 'PTA (%)',
          dataIndex: 'pta',
          render: (value: number, record: any) => (
            <span style={record?._modified ? { color: '#0B2545', fontWeight: 500 } : undefined}>
              {value != null ? `${Number(value).toFixed(1)}%` : '-'}
            </span>
          ),
        },
        {
          title: '\u8bc4\u4ef7',
          key: 'evaluation',
          render: (_: any, record: GridSearchRegimen) => {
            if ((record as any)._recalc_loading) {
              return <Tag color="blue" icon={<Spin size="small" />}>后台重算中</Tag>;
            }
            if ((record as any)._recalc_error) {
              return (
                <Tooltip title={(record as any)._recalc_error}>
                  <Tag color="red">重算失败</Tag>
                </Tooltip>
              );
            }
            const candidateStatus = resolveRegimenStatus({ regimen: record, selectedMethod: method, drugConfig });
            const candidateEvaluation = resolveEvaluationStatus(candidateStatus, record?.pta);
            return <Tag color={candidateEvaluation.color}>{candidateEvaluation.label}</Tag>;
          },
        },
      ];
      return editableColumns.map(applySnapshotUnitToColumn);
    }
    const base = [
      { title: '给药间隔', dataIndex: 'interval_h', render: (v: number) => `Q${v}h` },
      { title: `单次剂量（${doseMassUnit}）`, dataIndex: 'dose_mg', render: formatResultDose },
      { title: `按体重剂量（${doseMassUnit}/kg）`, dataIndex: 'dose_mg_kg', render: formatResultDosePerKg },
    ];
    const numRender = (v: number | undefined) => v != null ? (typeof v === 'number' ? v.toFixed(1) : v) : '-';
    const pctRender = (v: number | undefined) => v != null ? `${typeof v === 'number' ? v.toFixed(1) : v}%` : '-';

    // 从 METHOD_CONFIG.regimenColumns 动态生成列定义
    const methodDef = METHOD_CONFIG[method];
    let dynamic: any[] = [];
    if (methodDef?.regimenColumns?.length) {
      dynamic = applyConfiguredConcentrationUnits(
        methodDef.regimenColumns,
        drugConfig,
        method,
        resultAdministrationMode,
        continuousTargetParameter,
      ).map((col: RegimenColumn) => {
        const renderer = (value: number | undefined) => {
          const projected = projectColumnValue(value, col.dataKey);
          return col.format === 'percent'
            ? pctRender(projected as number | undefined)
            : numRender(projected as number | undefined);
        };
        // 从 regimen 的平铺字段或 pkpd_results 中获取值
        const dataKey = col.source === 'pkpd' ? col.dataKey : col.dataKey;
        return {
          title: col.title,
          dataIndex: dataKey.includes('.') ? dataKey.split('.')[0] : dataKey,
          render: col.source === 'pkpd'
            ? (_: any, record: any) => {
                // pkpd source: 从 record.pkpd_results[method] 的嵌套路径取值
                const parts = col.dataKey.split('.');
                let val: any = record;
                for (const p of parts) val = val?.[p];
                return renderer(val);
              }
            : renderer,
        };
      });
    } else {
      // 未配置 regimenColumns 的方法使用通用 fallback
      dynamic = [
        { title: '预测值', dataIndex: 'predicted_value', render: numRender },
      ];
    }
    if (method === '%fT>MIC') {
      dynamic = [
        {
          title: '%fT>MIC (%)',
          dataIndex: 'ft_above_mic',
          render: (value: number | null) => value == null ? '-' : `${formatDisplayNumber(value, 2)}%`,
        },
        ...(!isMeropenemFormulaCard ? [{
          title: 'PTA (%)',
          dataIndex: 'pta',
          render: (value: number | null, record: any) => {
            const pta = value ?? record?.pkpd_results?.[method]?.pta;
            return pta == null ? '-' : `${formatDisplayNumber(pta, 1)}%`;
          },
        }] : []),
      ];
    }
    const status = {
      title: '评价',
      key: 'evaluation',
      render: (_: boolean, record: any) => {
        const regimenStatus = resolveRegimenStatus({ regimen: record, selectedMethod: method, drugConfig });
        const evaluationStatus = resolveEvaluationStatus(regimenStatus, record?.pta);
        return <Tag color={evaluationStatus.color}>{evaluationStatus.label}</Tag>;
      },
    };
    return [...base, ...dynamic, status].map(applySnapshotUnitToColumn);
  };

  const renderCandidateMobileCard = (method: string, r: GridSearchRegimen, index: number) => {
    const methodConfig = METHOD_CONFIG[method];
    const methodColumns = applyConfiguredConcentrationUnits(
      methodConfig?.regimenColumns || [],
      drugConfig,
      method,
      resultAdministrationMode,
      continuousTargetParameter,
    );
    const candidateStatus = resolveRegimenStatus({ regimen: r, selectedMethod: method, drugConfig });
    const candidateEvaluation = resolveEvaluationStatus(candidateStatus, r?.pta);
    const metrics = [
      { label: '给药间隔', value: `Q${r.interval_h}h` },
      { label: `单次剂量（${doseMassUnit}）`, value: formatResultDose(r.dose_mg) },
      { label: `按体重剂量（${doseMassUnit}/kg）`, value: formatResultDosePerKg(r.dose_mg_kg) },
      ...(method === '%fT>MIC'
        ? [
            { label: '%fT>MIC', value: r.ft_above_mic != null ? `${formatDisplayNumber(r.ft_above_mic, 2)}%` : '-' },
            ...(!isMeropenemFormulaCard
              ? [{
                  label: 'PTA',
                  value: (r.pta ?? r.pkpd_results?.[method]?.pta) != null
                    ? `${formatDisplayNumber(r.pta ?? r.pkpd_results?.[method]?.pta, 1)}%`
                    : '-',
                }]
              : []),
          ]
        : methodColumns.slice(0, 3).map((col) => {
            const parts = col.dataKey.split('.');
            let value: any = col.source === 'field' ? (r as any)[col.dataKey] : (r as any);
            if (col.source !== 'field') for (const part of parts) value = value?.[part];
            return {
              label: col.title.replace(/ \(.*\)/, ''),
              value: value == null
                ? '-'
                : col.format === 'percent'
                  ? `${formatDisplayNumber(value, col.decimals ?? 1)}%`
                  : formatDisplayNumber(projectColumnValue(value, col.dataKey), col.decimals ?? 1),
            };
          })),
    ];
    return (
      <SelectableRegimenCard
        key={`${method}-${index}`}
        title={formatResultDosePerKg(r.dose_mg_kg)}
        subtitle={formatCandidateSubtitle(r.interval_h, r.infusion_h)}
        metrics={metrics}
        targetMet={candidateStatus.clinicallyMet}
        targetLabel={candidateEvaluation.label}
        targetColor={candidateEvaluation.color}
        selected={selectedGridRegimen?.method === method && selectedGridRegimen?.index === index}
        onClick={candidateStatus.clinicallyMet && onSelectGridRegimen
          ? () => onSelectGridRegimen(method, index, r)
          : undefined}
      />
    );
  };

  const renderEditableCandidateMobileCard = (method: string, r: GridSearchRegimen, index: number) => {
    const candidateStatus = resolveRegimenStatus({ regimen: r, selectedMethod: method, drugConfig });
    const candidateEvaluation = resolveEvaluationStatus(candidateStatus, r?.pta);
    const rowRecalcLoading = Boolean((r as any)._recalc_loading);
    const rowRecalcError = (r as any)._recalc_error as string | undefined;
    const resultConcentrationUnit = getConcentrationUnit(method);
    const metrics = [
      { label: '给药间隔', value: `Q${r.interval_h}h` },
      { label: `剂量 (${doseMassUnit}/kg)`, value: formatResultDosePerKg(r.dose_mg_kg) },
      { label: '预测AUC₂₄/MIC', value: r.auc24_mic != null ? Number(r.auc24_mic).toFixed(1) : '-' },
      { label: '预测AUC₂₄', value: r.predicted_auc24 != null ? Number(displayAuc(r.predicted_auc24)).toFixed(1) : '-' },
      { label: '预测Cmin', value: r.predicted_cmin != null ? `${Number(displayConcentration(r.predicted_cmin)).toFixed(2)} ${resultConcentrationUnit}` : '-' },
      { label: '预测Cmax', value: r.predicted_cmax != null ? `${Number(displayConcentration(r.predicted_cmax)).toFixed(2)} ${resultConcentrationUnit}` : '-' },
      { label: 'PTA', value: r.pta != null ? `${Number(r.pta).toFixed(1)}%` : '-' },
    ];

    return (
      <div>
        <SelectableRegimenCard
          title={formatResultDose(r.dose_mg)}
          subtitle={formatCandidateSubtitle(r.interval_h, r.infusion_h)}
          metrics={metrics}
          targetMet={!rowRecalcLoading && !rowRecalcError && candidateStatus.clinicallyMet}
          targetLabel={rowRecalcLoading ? '后台重算中' : rowRecalcError ? '重算失败' : candidateEvaluation.label}
          targetColor={rowRecalcLoading ? 'blue' : rowRecalcError ? 'red' : candidateEvaluation.color}
          selected={selectedGridRegimen?.method === method && selectedGridRegimen?.index === index}
          onClick={onSelectGridRegimen && !rowRecalcLoading && !rowRecalcError
            ? () => onSelectGridRegimen(method, index, r)
            : undefined}
        />
        {rowRecalcError && (
          <Alert type="error" showIcon message={rowRecalcError} style={{ margin: '8px 6px 0' }} />
        )}
        <div style={{ padding: '10px 6px 2px' }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
            {`调整剂量 (${doseMassUnit})`}
          </Text>
          <InputNumber
            min={1}
            step={1}
            value={convertCanonicalValue(r.dose_mg, doseMassUnit, 'mass') ?? undefined}
            style={{ width: '100%' }}
            onChange={(value) => onEditableGridDoseChange?.(
              index,
              value == null ? null : convertUnitValue(value, doseMassUnit, 'mg', 'mass'),
              r,
            )}
          />
        </div>
      </div>
    );
  };

  const candidateIntervalGroups = (method: string, rows: GridSearchRegimen[]) => {
    if (method !== '%fT>MIC') return [{ key: 'all', interval: null, entries: rows.map((r, index) => ({ r, index })) }];
    const grouped = new Map<string, { r: GridSearchRegimen; index: number }[]>();
    rows.forEach((r, index) => {
      const key = String(r.interval_h);
      grouped.set(key, [...(grouped.get(key) || []), { r, index }]);
    });
    return Array.from(grouped.entries()).map(([key, entries]) => ({ key, interval: Number(key), entries }));
  };

  return (
    <>
      {Object.entries(normalizedPkpdResults).map(([method, result]) => {
        const { regimenStatus, evaluationStatus } = getMethodEvaluation(method, result);
        const showWarning = evaluationStatus.color === 'red';
        const concentrationUnit = getConcentrationUnit(method);
        const displayedValue = displayMethodValue(method, result.value);
        const displayedUnit = methodDisplayUnit(method);
        const metricPresentation = getPkpdMetricPresentation(
          method,
          resultAdministrationMode,
          continuousTargetParameter,
        );
        const targetBounds = resolvePkpdTargetBounds(
          method,
          result,
          drugConfig,
          resultAdministrationMode,
          continuousTargetParameter,
        );
        const displayedTargetMin = targetBounds.min == null
          ? null
          : displayMethodValue(method, targetBounds.min);
        const displayedTargetMax = targetBounds.max == null
          ? null
          : displayMethodValue(method, targetBounds.max);
        const targetRangeDisplay = displayedTargetMin != null && displayedTargetMax != null
          ? `${formatDisplayNumber(displayedTargetMin, 2)}–${formatDisplayNumber(displayedTargetMax, 2)}`
          : displayedTargetMin != null
            ? `≥${formatDisplayNumber(displayedTargetMin, 2)}`
            : displayedTargetMax != null
              ? `≤${formatDisplayNumber(displayedTargetMax, 2)}`
              : '未配置';
        const hasTargetBounds = displayedTargetMin != null || displayedTargetMax != null;
        const comparisonStatusLabel = resolvePkpdComparisonStatusLabel({
          evaluationLabel: evaluationStatus.label,
          predictedValue: displayedValue,
          targetMin: displayedTargetMin,
          targetMax: displayedTargetMax,
          blocksDirectionalStatus: regimenStatus.maxDoseExceeded || regimenStatus.ptaMet === false,
        });
        const comparisonStatusColor = comparisonStatusLabel === '无法评价'
          ? 'blue'
          : evaluationStatus.color;
        const details = result.details || {};
        const selectedMic = details.default_mic ?? details.selected_mic;
        const defaultResult = details.default_result ?? details.selected_result;
        const micBreakpointRows = method === '%fT>MIC'
          ? (details.mic_breakpoints || details.rows || []).map((row: any) => ({
              ...row,
              mic: row.mic,
              ft_above_mic: row.ft_above_mic,
              ft_above_4mic: row.ft_above_4mic,
              ft_met: row.ft_met ?? row.target_met ?? (
                (targetBounds.min == null || row.ft_above_mic >= targetBounds.min)
                && (targetBounds.max == null || row.ft_above_mic <= targetBounds.max)
              ),
            }))
          : [];
        const mic = details.mic ?? details.default_mic ?? evaluationRegimen?.mic;
        const auc24 = details.auc24 ?? (method === 'Target AUC' ? result.value : undefined);
        const aucMic = mic != null && Number(mic) > 0 && auc24 != null
          ? calculateAucMic(Number(auc24), Number(mic))
          : details.auc24_mic;
        const cmax = details.css_peak ?? details.cmax;
        const hasEvidence = result.pta != null
          || ((method === 'Target AUC' || method === 'AUC/MIC') && mic != null)
          || (method === 'Target AUC' && aucMic != null)
          || (method === 'AUC/MIC' && auc24 != null)
          || (method === '%fT>MIC' && selectedMic != null)
          || (method === 'Cmax/MIC' && (details.mic != null || cmax != null));

        return (
          <Card
            key={method}
            title={(
              <div className="dose-calculation__pkpd-title">
                <ExperimentOutlined />
                <span className="dose-calculation__pkpd-title-text">
                  {formulaCandidateOnly && method === '%fT>MIC'
                    ? '候选方案选择'
                    : `${metricPresentation.shortLabel} 靶值分析`}
                </span>
              </div>
            )}
            size="small"
            className="dose-calculation__pkpd-card"
            style={{ marginTop: 16 }}
          >
            <div
              className="dose-calculation__pkpd-summary"
              style={formulaCandidateOnly && method === '%fT>MIC' ? { display: 'none' } : undefined}
            >
            <div className="dose-calculation__pkpd-comparison">
              <div className="dose-calculation__pkpd-column dose-calculation__pkpd-column--target">
                <div className="dose-calculation__pkpd-column-heading">治疗目标</div>
                <div className="dose-calculation__pkpd-metric-name">
                  {metricPresentation.metricLabel}（{metricPresentation.shortLabel}）
                </div>
                <div className="dose-calculation__pkpd-value-row">
                  <span className="dose-calculation__pkpd-value">
                    {targetRangeDisplay}{hasTargetBounds && displayedUnit ? ` ${displayedUnit}` : ''}
                  </span>
                </div>
              </div>
              <div className="dose-calculation__pkpd-column dose-calculation__pkpd-column--prediction">
                <div className="dose-calculation__pkpd-column-heading">
                  {isMeropenemFormulaCard && selectedGridRegimen ? '候选方案预测' : '当前方案预测'}
                </div>
                <div className="dose-calculation__pkpd-metric-name">{metricPresentation.predictedLabel}</div>
                <div className="dose-calculation__pkpd-value-row">
                  <span className="dose-calculation__pkpd-value dose-calculation__pkpd-value--prediction">
                    {formatDisplayNumber(displayedValue, 2, String(result.value ?? ''))}
                    {displayedUnit ? ` ${displayedUnit}` : ''}
                  </span>
                  <Tag color={comparisonStatusColor}>{comparisonStatusLabel}</Tag>
                </div>
              </div>
            </div>

            {hasEvidence && <div className="dose-calculation__pkpd-evidence">
              <div className="dose-calculation__pkpd-evidence-title">
                {['Target AUC', 'Target Concentration', 'Target Cmax'].includes(method)
                  ? '补充指标'
                  : '计算依据'}
              </div>
              <div className="dose-calculation__pkpd-evidence-list">
                {result.pta != null && (
                  <div className="dose-calculation__pkpd-evidence-item">
                    <span className="dose-calculation__pkpd-evidence-label">目标达成概率 PTA</span>
                    <span className="dose-calculation__pkpd-evidence-value">
                      {formatDisplayNumber(result.pta, 2)}%
                    </span>
                  </div>
                )}
                {(method === 'Target AUC' || method === 'AUC/MIC') && mic != null && (
                  <div className="dose-calculation__pkpd-evidence-item">
                    <span className="dose-calculation__pkpd-evidence-label">MIC</span>
                    <span className="dose-calculation__pkpd-evidence-value">
                      {formatDisplayNumber(displayMic(mic), 2)} {micUnit}
                    </span>
                  </div>
                )}
                {method === 'Target AUC' && aucMic != null && (
                  <div className="dose-calculation__pkpd-evidence-item">
                    <span className="dose-calculation__pkpd-evidence-label">预测 AUC₀–₂₄/MIC</span>
                    <span className="dose-calculation__pkpd-evidence-value">
                      {formatDisplayNumber(aucMic, 2)} h
                    </span>
                  </div>
                )}
                {method === 'AUC/MIC' && auc24 != null && (
                  <div className="dose-calculation__pkpd-evidence-item">
                    <span className="dose-calculation__pkpd-evidence-label">预测 AUC₀–₂₄</span>
                    <span className="dose-calculation__pkpd-evidence-value">
                      {formatDisplayNumber(displayAuc(auc24), 2)} {aucUnit}
                    </span>
                  </div>
                )}
                {method === '%fT>MIC' && selectedMic != null && (
                  <div className="dose-calculation__pkpd-evidence-item">
                    <span className="dose-calculation__pkpd-evidence-label">MIC</span>
                    <span className="dose-calculation__pkpd-evidence-value">
                      {formatDisplayNumber(displayMic(selectedMic), 2)} {micUnit}
                    </span>
                  </div>
                )}
                {method === 'Cmax/MIC' && details.mic != null && (
                  <div className="dose-calculation__pkpd-evidence-item">
                    <span className="dose-calculation__pkpd-evidence-label">MIC</span>
                    <span className="dose-calculation__pkpd-evidence-value">
                      {formatDisplayNumber(displayMic(details.mic), 2)} {micUnit}
                    </span>
                  </div>
                )}
                {method === 'Cmax/MIC' && cmax != null && (
                  <div className="dose-calculation__pkpd-evidence-item">
                    <span className="dose-calculation__pkpd-evidence-label">预测 Cmax,ss</span>
                    <span className="dose-calculation__pkpd-evidence-value">
                      {formatConcentrationResult(cmax)} {concentrationUnit}
                    </span>
                  </div>
                )}
              </div>
            </div>}

            {/* %fT>MIC 特有: 详细参数 */}
            {method === '%fT>MIC' && defaultResult && (
              <Descriptions column={isMobileShell ? 1 : 3} size="small" style={{ marginTop: 8 }}>
                <Descriptions.Item label="fu (游离分数)">{details.fu}</Descriptions.Item>
                <Descriptions.Item label="游离峰浓度">{formatConcentrationResult(defaultResult.free_css_peak)} {snapshotConcentrationUnit}</Descriptions.Item>
                <Descriptions.Item label="游离谷浓度">{formatConcentrationResult(defaultResult.free_css_trough)} {snapshotConcentrationUnit}</Descriptions.Item>
              </Descriptions>
            )}

            {/* %fT>MIC 特有: 多MIC断点表格 */}
            {method === '%fT>MIC' && !hideFtMicBreakpointTable && micBreakpointRows.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>{ftMicPanelTitle || '不同 MIC 断点下的 %fT&gt;MIC 与 PTA:'}</Text>
                {isMobileShell ? (
                  <div className="dose-calculation__mobile-list">
                    {micBreakpointRows.map((bp: any) => {
                      const isDefault = bp.mic === selectedMic;
                      return (
                        <div key={bp.mic} className="dose-calculation__mobile-card">
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                            <Text strong>MIC {formatDisplayNumber(displayMic(bp.mic), 2)} {micUnit}</Text>
                            {isDefault ? <Tag color="blue">默认</Tag> : null}
                          </div>
                          {renderMetricGrid([
                             { label: '%fT>MIC', value: `${formatDisplayNumber(bp.ft_above_mic, 2)}%` },
                            { label: '评估结果', value: (bp.ft_met ?? bp.target_met) ? '是' : '否' },
                          ])}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Table
                    dataSource={micBreakpointRows.map((bp: any) => ({
                      ...bp,
                      key: bp.mic,
                      _isDefault: bp.mic === selectedMic,
                    }))}
                    columns={micBreakpointColumns}
                    pagination={false}
                    size="small"
                    rowClassName={(record: any) => record._isDefault ? 'ant-table-row-selected' : ''}
                  />
                )}
              </div>
            )}

            {/* 网格搜索结果 */}
            </div>

            {editableGridMethod === method && editableGridRows && editableGridRows.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Alert
                  type="info"
                  showIcon
                  message={`支持在候选方案中微调“调整剂量 (${doseMassUnit})”。AUC、Cmin/Cmax、靶值评价和安全检查均由后台完整重算；PK 两点法未提供群体变异时，PTA 显示“不适用”。`}
                  style={{ marginBottom: 8 }}
                />
                {useFormulaIntervalAccordion ? (
                  <GroupedCandidateRegimenTable
                    rows={editableGridRows}
                    columns={getGridColumns(method, true)}
                    selectedIndex={selectedGridRegimen?.method === method ? selectedGridRegimen.index : null}
                    onSelect={(sourceIndex) => {
                      const regimen = editableGridRows[sourceIndex];
                      if (
                        regimen
                        && onSelectGridRegimen
                        && !(regimen as any)._recalc_loading
                        && !(regimen as any)._recalc_error
                      ) {
                        onSelectGridRegimen(method, sourceIndex, regimen);
                      }
                    }}
                    getEvaluation={(regimen) => resolveEvaluationStatus(
                      resolveRegimenStatus({ regimen, selectedMethod: method, drugConfig }),
                      regimen?.pta,
                    )}
                    isRecommended={(regimen) => regimen?.is_recommended === true}
                    formatDose={(dose) => formatResultDose(dose)}
                    isMobile={isMobileShell}
                    renderMobileRow={(regimen, sourceIndex) => renderEditableCandidateMobileCard(
                      method,
                      regimen as GridSearchRegimen,
                      sourceIndex,
                    )}
                    resetKey={evaluationRegimen?.calculation_id ?? null}
                  />
                ) : isMobileShell ? (
                  <div className="dose-calculation__mobile-list">
                    {editableGridRows.map((r: GridSearchRegimen, index: number) => {
                      const candidateStatus = resolveRegimenStatus({ regimen: r, selectedMethod: method, drugConfig });
                      const candidateEvaluation = resolveEvaluationStatus(candidateStatus, r?.pta);
                      const rowRecalcLoading = Boolean((r as any)._recalc_loading);
                      const rowRecalcError = (r as any)._recalc_error as string | undefined;
                      const concentrationUnit = getConcentrationUnit(method);
                      const metrics = [
                        { label: '\u7ed9\u836f\u95f4\u9694', value: `Q${r.interval_h}h` },
                        { label: `\u5242\u91cf (${doseMassUnit}/kg)`, value: formatResultDosePerKg(r.dose_mg_kg) },
                        { label: '预测AUC\u2082\u2084/MIC', value: r.auc24_mic != null ? Number(r.auc24_mic).toFixed(1) : '-' },
                        { label: '\u9884\u6d4bAUC\u2082\u2084', value: r.predicted_auc24 != null ? Number(displayAuc(r.predicted_auc24)).toFixed(1) : '-' },
                        { label: '预测Cmin', value: r.predicted_cmin != null ? `${Number(displayConcentration(r.predicted_cmin)).toFixed(2)} ${concentrationUnit}` : '-' },
                        { label: '预测Cmax', value: r.predicted_cmax != null ? `${Number(displayConcentration(r.predicted_cmax)).toFixed(2)} ${concentrationUnit}` : '-' },
                        { label: 'PTA', value: r.pta != null ? `${Number(r.pta).toFixed(1)}%` : '-' },
                      ];

                      return (
                        <div key={`${method}-${index}`}>
                          <SelectableRegimenCard
                            title={formatResultDose(r.dose_mg)}
                            subtitle={formatCandidateSubtitle(r.interval_h, r.infusion_h)}
                            metrics={metrics}
                            targetMet={!rowRecalcLoading && !rowRecalcError && candidateStatus.clinicallyMet}
                            targetLabel={rowRecalcLoading ? '后台重算中' : rowRecalcError ? '重算失败' : candidateEvaluation.label}
                            targetColor={rowRecalcLoading ? 'blue' : rowRecalcError ? 'red' : candidateEvaluation.color}
                            selected={selectedGridRegimen?.method === method && selectedGridRegimen?.index === index}
                            onClick={onSelectGridRegimen && !rowRecalcLoading && !rowRecalcError
                              ? () => onSelectGridRegimen(method, index, r)
                              : undefined}
                          />
                          {rowRecalcError && (
                            <Alert type="error" showIcon message={rowRecalcError} style={{ margin: '8px 6px 0' }} />
                          )}
                          <div style={{ padding: '10px 6px 2px' }}>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
                              {`\u8c03\u6574\u5242\u91cf (${doseMassUnit})`}
                            </Text>
                            <InputNumber
                              min={1}
                              step={1}
                              value={convertCanonicalValue(r.dose_mg, doseMassUnit, 'mass') ?? undefined}
                              style={{ width: '100%' }}
                              onChange={(value) => onEditableGridDoseChange?.(
                                index,
                                value == null ? null : convertUnitValue(value, doseMassUnit, 'mg', 'mass'),
                                r,
                              )}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Table
                    dataSource={editableGridRows.map((r: GridSearchRegimen, i: number) => ({ ...r, key: i }))}
                    columns={getGridColumns(method, true)}
                    pagination={false}
                    size="small"
                    rowClassName={(_record: GridSearchRegimen, index: number) => (
                      selectedGridRegimen?.method === method && selectedGridRegimen?.index === index
                        ? 'ant-table-row-selected'
                        : ''
                    )}
                    onRow={(record: any, index?: number) => ({
                      onClick: () => {
                        if (
                          onSelectGridRegimen
                          && index !== undefined
                          && !record?._recalc_loading
                          && !record?._recalc_error
                        ) {
                          onSelectGridRegimen(method, index, record);
                        }
                      },
                      style: onSelectGridRegimen && !record?._recalc_loading && !record?._recalc_error
                        ? { cursor: 'pointer' }
                        : undefined,
                    })}
                  />
                )}
              </div>
            )}
            {gridSearchResults?.[method] && gridSearchResults[method].length > 0 && editableGridMethod !== method && (
              <div style={{ marginTop: 16 }}>
                <Alert
                  type={showWarning ? 'warning' : 'info'}
                  showIcon
                  message={showWarning
                    ? (onSelectGridRegimen
                      ? `当前方案 ${method} 评价为不达标，请点击选择候选达标方案`
                      : `当前方案 ${method} 评价为不达标，以下为候选达标方案`)
                    : (onSelectGridRegimen
                      ? `当前方案 ${method} 评价为达标，以下保留其他候选方案供参考`
                      : `当前方案 ${method} 评价为达标，以下为其他候选方案`)}
                  style={{ marginBottom: 8 }}
                />
                {isMobileShell ? (
                  method === '%fT>MIC' ? (
                    <Collapse
                      size="small"
                      accordion
                      defaultActiveKey={[]}
                      items={candidateIntervalGroups(method, gridSearchResults[method]).map((group) => ({
                        key: group.key,
                        label: `Q${group.interval}h（${group.entries.length} 个方案）`,
                        children: <div className="dose-calculation__mobile-list">
                          {group.entries.map(({ r, index }) => renderCandidateMobileCard(method, r, index))}
                        </div>,
                      }))}
                    />
                  ) : (
                    <div className="dose-calculation__mobile-list">
                      {gridSearchResults[method].map((r: GridSearchRegimen, index: number) => renderCandidateMobileCard(method, r, index))}
                    </div>
                  )
                ) : (
                  method === '%fT>MIC' ? (
                    <Collapse
                      size="small"
                      accordion
                      defaultActiveKey={[]}
                      items={candidateIntervalGroups(method, gridSearchResults[method]).map((group) => ({
                        key: group.key,
                        label: `Q${group.interval}h（${group.entries.length} 个方案）`,
                        children: (
                          <Table
                            dataSource={group.entries.map(({ r, index }) => ({ ...r, key: index }))}
                            columns={getGridColumns(method)}
                            pagination={false}
                            size="small"
                            rowClassName={(record: GridSearchRegimen) => {
                              const selected = selectedGridRegimen?.method === method
                                && selectedGridRegimen?.index === (record as any).key;
                              if (selected) return 'ant-table-row-selected';
                              return resolveRegimenStatus({ regimen: record, selectedMethod: method, drugConfig }).clinicallyMet
                                ? '' : 'ant-table-row-disabled';
                            }}
                            onRow={(record: any) => ({
                              onClick: () => {
                                const index = Number(record.key);
                                const status = resolveRegimenStatus({ regimen: record, selectedMethod: method, drugConfig });
                                if (onSelectGridRegimen && status.clinicallyMet) onSelectGridRegimen(method, index, record);
                              },
                            })}
                          />
                        ),
                      }))}
                    />
                  ) : (
                  <Table
                    dataSource={gridSearchResults[method].map((r: GridSearchRegimen, i: number) => ({ ...r, key: i }))}
                    columns={getGridColumns(method)}
                    pagination={false}
                    size="small"
                    rowClassName={(record: GridSearchRegimen, index: number) => {
                      const isSelected = selectedGridRegimen?.method === method && selectedGridRegimen?.index === index;
                      if (isSelected) return 'ant-table-row-selected';
                      const candidateStatus = resolveRegimenStatus({ regimen: record, selectedMethod: method, drugConfig });
                      return candidateStatus.clinicallyMet ? '' : 'ant-table-row-disabled';
                    }}
                    onRow={(record: any, index?: number) => ({
                      onClick: () => {
                        const candidateStatus = resolveRegimenStatus({ regimen: record, selectedMethod: method, drugConfig });
                        if (onSelectGridRegimen && candidateStatus.clinicallyMet && index !== undefined) {
                          onSelectGridRegimen(method, index, record);
                        }
                      },
                      style: (() => {
                        const candidateStatus = resolveRegimenStatus({ regimen: record, selectedMethod: method, drugConfig });
                        return onSelectGridRegimen && candidateStatus.clinicallyMet ? { cursor: 'pointer' } : undefined;
                      })(),
                    })}
                  />
                  )
                )}
              </div>
            )}
          </Card>
        );
      })}
    </>
  );
};

/** 初始给药结果展示 */
const DoseResultDisplay: React.FC<{
  result: DoseResult;
  weight: number;
  covariates?: Covariates;
  selectedMethod?: string;
  administrationMode?: AdministrationMode;
  drugConfig?: any;
  submittedTargetAucMic?: number | null;
  onContinue: (r: DoseResult) => void;
  caseId: string;
  modelId: string;
  tdmData?: Array<any>;
}> = ({ result, weight, covariates, selectedMethod, administrationMode, drugConfig, submittedTargetAucMic, onContinue, caseId, modelId, tdmData }) => {
  const { isMobileShell } = useResponsive();
  const [selectedRegimen, setSelectedRegimen] = useState<number | null>(null);
  const [modifiedDoses, setModifiedDoses] = useState<Record<number, number>>({});
  const [modifiedInfusions, setModifiedInfusions] = useState<Record<number, number>>({});
  const [recalcStatus, setRecalcStatus] = useState<Record<number, 'loading' | 'done' | 'error'>>({});
  const [recalcOverrides, setRecalcOverrides] = useState<Record<number, any>>({});
  const regimenDebounceRefs = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const regimenLatestRequestRef = useRef<Record<number, string>>({});
  const [pkpdOverride, setPkpdOverride] = useState<Record<string, PkPdMethodResult> | null>(null);
  const [editedInterpretation, setEditedInterpretation] = useState<string | undefined>(undefined);
  const [regenInterpretation, setRegenInterpretation] = useState<string | undefined>(undefined);
  const [interpretLoading, setInterpretLoading] = useState(false);
  const resolvedMethod = selectedMethod || 'Target AUC';
  const resolvedAdministrationMode = normalizeAdministrationMode(result.administration_mode || administrationMode || 'intermittent');
  const isContinuousMode = resolvedAdministrationMode === 'continuous';
  const isOralMode = resolvedAdministrationMode === 'oral';
  const isBolusMode = resolvedAdministrationMode === 'bolus';
  const hasZeroInputDuration = isOralMode || isBolusMode;
  const methodDef = METHOD_CONFIG[resolvedMethod];
  const continuousTargetParameter = drugConfig?.continuous_infusion?.target_parameter || 'Css';
  const configuredConcentrationUnit = getConcentrationDisplayUnit(
    drugConfig,
    resolvedMethod,
    resolvedAdministrationMode,
    continuousTargetParameter,
  );
  const concentrationUnit = unitFromResult(result, 'concentration', configuredConcentrationUnit);
  const aucUnit = unitFromResult(result, 'auc', 'mg*h/L');
  const micUnit = unitFromResult(result, 'mic', drugConfig?.units?.mic?.display_unit || concentrationUnit);
  const doseMassUnit = unitFromResult(result, 'dose', 'mg');
  const formatResultDose = (value: unknown) => {
    const numeric = normalizeFiniteNumber(value);
    return numeric == null ? 'N/A' : formatCanonicalDose(numeric, { dose_mass_unit: doseMassUnit });
  };

  const formatResultDosePerKg = (value: unknown) => {
    const numeric = normalizeFiniteNumber(value);
    if (numeric == null) return 'N/A';
    const converted = convertUnitValue(numeric, 'mg/kg', `${doseMassUnit}/kg`, 'dose_per_weight');
    return `${formatDoseMgKg(converted)} ${doseMassUnit}/kg`;
  };
  const formatConcentrationValue = (value: unknown, decimals = 2) => {
    const numberValue = convertCanonicalValue(value, concentrationUnit, 'concentration');
    return numberValue != null ? `${numberValue.toFixed(decimals)} ${concentrationUnit}` : 'N/A';
  };
  const formatAucValue = (value: unknown, decimals = 1) => {
    const numberValue = convertCanonicalValue(value, aucUnit, 'auc');
    return numberValue != null ? `${numberValue.toFixed(decimals)} ${aucUnit}` : 'N/A';
  };
  const targetAucMicForTable = useMemo(() => {
    if (resolvedMethod !== 'AUC/MIC') return null;
    const submitted = normalizeFiniteNumber(submittedTargetAucMic);
    if (submitted != null && submitted > 0) return submitted;
    const ptaContext = (result as any).pta_context || {};
    const contextMin = normalizeFiniteNumber(ptaContext.target_min);
    const contextMax = normalizeFiniteNumber(ptaContext.target_max);
    if (ptaContext.target_is_ratio && contextMin != null && contextMin > 0) {
      return contextMax != null && contextMax > 0 ? (contextMin + contextMax) / 2 : contextMin;
    }
    const aucMicResult = normalizePkpdResults(result.pkpd_results, resolvedMethod)?.['AUC/MIC'];
    const resultMin = normalizeFiniteNumber(aucMicResult?.target_min);
    const resultMax = normalizeFiniteNumber(aucMicResult?.target_max);
    if (resultMin != null && resultMin > 0) {
      return resultMax != null && resultMax > 0 ? (resultMin + resultMax) / 2 : resultMin;
    }
    return null;
  }, [resolvedMethod, result, submittedTargetAucMic]);
  const targetAucRangeForSummary = useMemo(() => {
    if (resolvedMethod !== 'Target AUC') return null;
    const aucResult = normalizePkpdResults(result.pkpd_results, resolvedMethod)?.['Target AUC'];
    const targetMin = normalizeFiniteNumber(aucResult?.target_min);
    const targetMax = normalizeFiniteNumber(aucResult?.target_max);
    if (targetMin == null || targetMin <= 0) return null;
    const displayMin = convertCanonicalValue(targetMin, aucUnit, 'auc');
    const displayMax = convertCanonicalValue(targetMax, aucUnit, 'auc');
    if (targetMax != null && targetMax > 0) {
      return `${Number(displayMin).toFixed(1)} - ${Number(displayMax).toFixed(1)} ${aucUnit}`;
    }
    return `≥${Number(displayMin).toFixed(1)} ${aucUnit}`;
  }, [aucUnit, resolvedMethod, result.pkpd_results]);
  const continuousPrimaryMetric = useMemo(() => {
    switch (resolvedMethod) {
      case 'Target Concentration':
        return {
          label: '预计 Css',
          dataKey: 'predicted_css',
          unit: concentrationUnit,
          decimals: 2,
        };
      case 'Target AUC':
        return {
          label: '预测AUC24',
          dataKey: 'predicted_auc24',
          unit: aucUnit,
          decimals: 1,
        };
      case 'AUC/MIC':
        return {
          label: '预测AUC24/MIC',
          dataKey: 'auc24_mic',
          unit: '',
          decimals: 1,
        };
      case '%fT>MIC':
        return {
          label: '%fT>MIC',
          dataKey: '%fT>MIC',
          unit: '%',
          decimals: 1,
        };
      default:
        return null;
    }
  }, [concentrationUnit, resolvedMethod]);
  const methodRegimenCols = useMemo(() => {
    if (isContinuousMode && resolvedMethod === 'Target Concentration') {
      return [
        { title: `预计 Css (${concentrationUnit})`, source: 'field', dataKey: 'predicted_css', format: 'number', decimals: 2, width: 70 } as RegimenColumn,
        { title: `泵速 (${doseMassUnit}/h)`, source: 'field', dataKey: 'maintenance_rate_mg_h', format: 'number', decimals: 2, width: 72 } as RegimenColumn,
      ];
    }
    if (isContinuousMode && resolvedMethod === 'Target AUC') {
      return [
        { title: `预测AUC24 (${aucUnit})`, source: 'field', dataKey: 'predicted_auc24', format: 'number', decimals: 1, width: 72 } as RegimenColumn,
        { title: `泵速 (${doseMassUnit}/h)`, source: 'field', dataKey: 'maintenance_rate_mg_h', format: 'number', decimals: 2, width: 72 } as RegimenColumn,
      ];
    }
    if (isContinuousMode && resolvedMethod === 'AUC/MIC') {
      return [
        { title: '目标AUC₂₄/MIC', source: 'field', dataKey: 'target_auc_mic', format: 'number', decimals: 1, width: 72 } as RegimenColumn,
        { title: `预测AUC24 (${aucUnit})`, source: 'field', dataKey: 'predicted_auc24', format: 'number', decimals: 1, width: 72 } as RegimenColumn,
      ];
    }
    if (isContinuousMode && resolvedMethod === '%fT>MIC') {
      return [
        { title: '%fT>MIC (%)', source: 'pkpd', dataKey: 'value', format: 'percent', decimals: 1, width: 68 } as RegimenColumn,
        { title: 'PTA (%)', source: 'pkpd', dataKey: 'pta', format: 'percent', decimals: 1, width: 54 } as RegimenColumn,
        { title: `泵速 (${doseMassUnit}/h)`, source: 'field', dataKey: 'maintenance_rate_mg_h', format: 'number', decimals: 2, width: 72 } as RegimenColumn,
      ];
    }
    return applyConfiguredConcentrationUnits(
      methodDef?.regimenColumns || [],
      drugConfig,
      resolvedMethod,
      resolvedAdministrationMode,
      continuousTargetParameter,
    );
  }, [
    aucUnit,
    concentrationUnit,
    continuousTargetParameter,
    drugConfig,
    isContinuousMode,
    methodDef?.regimenColumns,
    doseMassUnit,
    resolvedAdministrationMode,
    resolvedMethod,
  ]);

  useEffect(() => {
    setSelectedRegimen(null);
    setModifiedDoses({});
    setModifiedInfusions({});
    setRecalcOverrides({});
    setRecalcStatus({});
    setPkpdOverride(null);
    setEditedInterpretation(undefined);
    setRegenInterpretation(undefined);
    Object.values(regimenDebounceRefs.current).forEach((timer) => clearTimeout(timer));
    regimenDebounceRefs.current = {};
    regimenLatestRequestRef.current = {};
    return () => {
      Object.values(regimenDebounceRefs.current).forEach((timer) => clearTimeout(timer));
    };
  }, [result]);

  useEffect(() => {
    setPkpdOverride(null);
    setEditedInterpretation(undefined);
  }, [resolvedMethod, selectedRegimen]);

  const baseRegimens = useMemo(() => {
    if (!Array.isArray(result.all_regimens) || result.all_regimens.length === 0) {
      return [];
    }

    return [...result.all_regimens]
      .sort((a: any, b: any) => (b.interval || 0) - (a.interval || 0))
      .map((regimen: any) => {
        const normalizedPkpd = normalizePkpdResults(regimen.pkpd_results, resolvedMethod);
        return {
          ...regimen,
          infusion_h: hasZeroInputDuration ? 0 : (regimen.infusion_h ?? result.infusion_duration_h),
          mic: regimen.mic ?? result.mic,
          mic_source: regimen.mic_source ?? result.mic_source,
          predicted_auc24: regimen.predicted_auc24 ?? regimen.auc24 ?? null,
          auc24_mic: regimen.auc24_mic
            ?? normalizedPkpd['AUC/MIC']?.value
            ?? normalizedPkpd['AUC/MIC']?.details?.auc24_mic
            ?? null,
          target_auc_mic: targetAucMicForTable,
          pkpd_results: normalizedPkpd,
        };
      });
  }, [hasZeroInputDuration, resolvedMethod, result.all_regimens, result.infusion_duration_h, result.mic, result.mic_source, targetAucMicForTable]);

  const recalcRegimen = useCallback(async (
    index: number,
    newDose: number,
    newInfusion: number,
    infusionSource: 'user_input' | 'drug_config_default' | 'administration_mode' | undefined,
    regimen: any,
    curveOnly = false,
  ) => {
    if (!caseId || !modelId) return;
    const resolvedMic = normalizeFiniteNumber(regimen.mic ?? result.mic);
    const resolvedMicSource = regimen.mic_source ?? result.mic_source;
    const requestKey = `${newDose}:${newInfusion}:${infusionSource ?? 'unspecified'}:${resolvedMic ?? 'no-mic'}:${resolvedMicSource ?? 'unspecified'}:${curveOnly ? 'curve' : 'full'}`;
    regimenLatestRequestRef.current[index] = requestKey;
    setRecalcStatus((prev) => ({ ...prev, [index]: 'loading' }));
    try {
      const res = await doseAPI.recalcRegimen({
        case_id: caseId,
        model_id: modelId,
        dose_mg: newDose,
        interval_h: isContinuousMode ? 24 : regimen.interval,
        selected_method: resolvedMethod,
        administration_mode: resolvedAdministrationMode,
        tdm_data: tdmData,
        dosing_regimens: result.regimen_history || undefined,
        infusion_h: isContinuousMode ? 24 : (hasZeroInputDuration ? 0 : newInfusion),
        infusion_duration_source: infusionSource,
        mic: resolvedMic ?? undefined,
        mic_source: resolvedMicSource,
        n_sim_doses: isContinuousMode ? 10 : (regimen.interval > 0 ? calcSimDoses(regimen.interval) : 10),
        pta_context: result.pta_context,
        map_parameters: result.map_parameters,
        loading_strategy: regimen.loading_regimen
          ? { ...regimen.loading_regimen, enabled: regimen.loading_regimen.enabled !== false }
          : result.loading_regimen
            ? { ...result.loading_regimen, enabled: result.loading_regimen.enabled !== false }
            : { enabled: false },
        curve_only: curveOnly,
      });
      if (regimenLatestRequestRef.current[index] !== requestKey) return;
      setRecalcOverrides((prev) => ({
        ...prev,
        [index]: {
          ...res,
          __sourceDoseMg: newDose,
          __sourceInfusionH: newInfusion,
        },
      }));
      setRecalcStatus((prev) => ({ ...prev, [index]: 'done' }));
    } catch (error: any) {
      console.error('Initial candidate regimen recalculation failed', error);
      if (regimenLatestRequestRef.current[index] !== requestKey) return;
      const detail = error?.response?.data?.detail;
      message.error(`候选方案 #${index + 1} 重算失败：${typeof detail === 'string' ? detail : '请检查剂量与输注时间'}`);
      setRecalcStatus((prev) => ({ ...prev, [index]: 'error' }));
      setRecalcOverrides((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  }, [caseId, hasZeroInputDuration, isContinuousMode, modelId, resolvedAdministrationMode, resolvedMethod, result.map_parameters, result.mic, result.mic_source, result.pta_context, result.regimen_history, tdmData]);

  const scheduleCandidateRecalc = useCallback((
    index: number,
    dose: number,
    infusion: number,
    regimen: any,
  ) => {
    const original = baseRegimens[index] || regimen;
    const originalDose = normalizeFiniteNumber(original?.dose);
    const originalInfusion = hasZeroInputDuration
      ? 0
      : normalizeFiniteNumber(original?.infusion_h ?? result.infusion_duration_h);
    const doseModified = originalDose == null || Math.abs(dose - originalDose) > 0.000001;
    const infusionModified = originalInfusion == null || Math.abs(infusion - originalInfusion) > 0.000001;
    const infusionSource = isContinuousMode || hasZeroInputDuration
      ? 'administration_mode'
      : infusionModified
        ? 'user_input'
        : original?.infusion_duration_source ?? result.infusion_duration_source;

    setModifiedDoses((prev) => {
      const next = { ...prev };
      if (doseModified) next[index] = dose;
      else delete next[index];
      return next;
    });
    setModifiedInfusions((prev) => {
      const next = { ...prev };
      if (infusionModified) next[index] = infusion;
      else delete next[index];
      return next;
    });
    setRecalcOverrides((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    if (regimenDebounceRefs.current[index]) {
      clearTimeout(regimenDebounceRefs.current[index]);
    }
    if (!doseModified && !infusionModified) {
      delete regimenDebounceRefs.current[index];
      delete regimenLatestRequestRef.current[index];
      setRecalcStatus((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      return;
    }
    setRecalcStatus((prev) => ({ ...prev, [index]: 'loading' }));
    regimenDebounceRefs.current[index] = setTimeout(() => {
      void recalcRegimen(index, dose, infusion, infusionSource, original);
    }, 300);
  }, [baseRegimens, hasZeroInputDuration, isContinuousMode, recalcRegimen, result.infusion_duration_h, result.infusion_duration_source]);

  const handleCandidateDoseChange = useCallback((index: number, value: number | null, regimen: any) => {
    if (value == null) return;
    const infusion = hasZeroInputDuration
      ? 0
      : normalizeFiniteNumber(modifiedInfusions[index] ?? regimen.infusion_h ?? result.infusion_duration_h);
    if (infusion == null || (!hasZeroInputDuration && infusion <= 0)) return;
    scheduleCandidateRecalc(index, value, infusion, regimen);
  }, [hasZeroInputDuration, modifiedInfusions, result.infusion_duration_h, scheduleCandidateRecalc]);

  const handleCandidateInfusionChange = useCallback((index: number, value: number | null, regimen: any) => {
    if (value == null || value <= 0) return;
    const dose = normalizeFiniteNumber(modifiedDoses[index] ?? regimen.dose);
    if (dose == null || dose <= 0) return;
    scheduleCandidateRecalc(index, dose, value, regimen);
  }, [modifiedDoses, scheduleCandidateRecalc]);

  const regimens = useMemo(() => baseRegimens.map((regimen: any, index: number) => {
    const override = recalcOverrides[index];
    const modifiedDose = modifiedDoses[index];
    const modifiedInfusion = modifiedInfusions[index];
    const effectiveDose = modifiedDose ?? regimen.dose;
    const effectiveInfusion = hasZeroInputDuration
      ? 0
      : (modifiedInfusion ?? regimen.infusion_h ?? result.infusion_duration_h);
    const effectiveDoseKg = weight > 0 ? +(effectiveDose / weight).toFixed(1) : regimen.dose_kg;
    const normalizedPkpd = normalizePkpdResults(override?.pkpd_results ?? regimen.pkpd_results, resolvedMethod);
    const resolvedPkpd = normalizedPkpd[resolvedMethod];
    const aucMicPkpd = normalizedPkpd['AUC/MIC'];
    const resolvedAuc24Mic = aucMicPkpd?.value
      ?? aucMicPkpd?.details?.auc24_mic
      ?? (resolvedMethod === 'AUC/MIC' ? resolvedPkpd?.value ?? resolvedPkpd?.details?.auc24_mic : undefined)
      ?? regimen.auc24_mic;
    const candidateLoading = override?.loading_regimen
      ?? regimen.loading_regimen
      ?? result.loading_regimen;
    const hasCandidateLoading = candidateLoading?.enabled !== false
      && normalizeFiniteNumber(candidateLoading?.dose_mg) != null;
    const candidateMaintenance = {
      ...(candidateLoading ? (override?.maintenance_regimen ?? regimen.maintenance_regimen ?? {}) : {}),
      dose_mg: effectiveDose,
      dose_mg_kg: override?.dose_mg_kg ?? effectiveDoseKg,
      interval_h: override?.interval_h ?? regimen.interval,
    };

    return {
      ...regimen,
      dose: effectiveDose,
      dose_kg: override?.dose_mg_kg ?? effectiveDoseKg,
      interval: override?.interval_h ?? regimen.interval,
      infusion_h: hasZeroInputDuration ? 0 : (override?.infusion_h ?? effectiveInfusion),
      infusion_duration_h: hasZeroInputDuration ? 0 : (override?.infusion_duration_h ?? effectiveInfusion),
      infusion_duration_source: override?.infusion_duration_source
        ?? (modifiedInfusion != null ? 'user_input' : regimen.infusion_duration_source ?? result.infusion_duration_source),
      predicted_auc24: override?.predicted_auc24 ?? regimen.predicted_auc24 ?? regimen.auc24 ?? null,
      maintenance_daily_dose_mg: override?.maintenance_daily_dose_mg ?? regimen.maintenance_daily_dose_mg ?? result.maintenance_daily_dose_mg,
      maintenance_rate_mg_h: override?.maintenance_rate_mg_h ?? regimen.maintenance_rate_mg_h ?? result.maintenance_rate_mg_h,
      predicted_css: override?.predicted_css ?? regimen.predicted_css ?? result.predicted_css,
      predicted_cmin: override?.predicted_cmin ?? regimen.predicted_cmin ?? null,
      predicted_cmax: override?.predicted_cmax ?? regimen.predicted_cmax ?? null,
      recommended_loading_dose_mg: override?.recommended_loading_dose_mg
        ?? regimen.recommended_loading_dose_mg
        ?? (hasCandidateLoading ? candidateLoading.dose_mg : undefined),
      recommended_loading_dose_mg_kg: override?.recommended_loading_dose_mg_kg
        ?? regimen.recommended_loading_dose_mg_kg
        ?? (hasCandidateLoading ? candidateLoading.dose_mg_kg : undefined),
      strategy_type: override?.strategy_type
        ?? regimen.strategy_type
        ?? (hasCandidateLoading ? result.strategy_type : undefined),
      loading_regimen: hasCandidateLoading ? candidateLoading : undefined,
      maintenance_regimen: hasCandidateLoading ? candidateMaintenance : undefined,
      transition_time_h: hasCandidateLoading
        ? resolveLoadingTransitionTime(candidateLoading, override?.interval_h ?? regimen.interval)
        : undefined,
      bag_interval_h: override?.bag_interval_h ?? regimen.bag_interval_h ?? result.bag_interval_h,
      auc24_mic: resolvedAuc24Mic,
      target_auc_mic: targetAucMicForTable,
      pta: override?.pta ?? regimen.pta ?? normalizedPkpd[resolvedMethod]?.pta ?? null,
      evaluation: override?.evaluation ?? regimen.evaluation,
      target_met_raw: override?.target_met_raw
        ?? resolvedPkpd?.details?.target_met_raw
        ?? resolvedPkpd?.target_met_raw
        ?? regimen.target_met_raw,
      target_status: override?.target_status
        ?? resolvedPkpd?.details?.target_status
        ?? resolvedPkpd?.target_status
        ?? regimen.target_status,
      max_dose_exceeded: override?.max_dose_exceeded
        ?? resolvedPkpd?.details?.max_dose_exceeded
        ?? resolvedPkpd?.max_dose_exceeded
        ?? regimen.max_dose_exceeded,
      is_optimal: override?.is_optimal ?? regimen.is_optimal,
      safety_check: override?.safety_check ?? regimen.safety_check ?? result.safety_check,
      pkpd_results: normalizedPkpd,
      pk_parameters: override?.pk_parameters ?? regimen.pk_parameters ?? result.pk_parameters,
      pta_context: override?.pta_context ?? regimen.pta_context ?? result.pta_context,
      pta_meta: override?.pta_meta ?? regimen.pta_meta ?? result.pta_meta,
      simulation_data: override?.simulation_data ?? regimen.simulation_data,
      combined_curve: override?.combined_curve ?? regimen.combined_curve,
      tdm_points: override?.tdm_points ?? regimen.tdm_points,
      regimen_history: override?.regimen_history ?? regimen.regimen_history,
      _modified: modifiedDose != null || modifiedInfusion != null,
      _recalc_loading: recalcStatus[index] === 'loading',
    };
  }), [
    baseRegimens,
    modifiedDoses,
    modifiedInfusions,
    recalcOverrides,
    recalcStatus,
    resolvedMethod,
    hasZeroInputDuration,
    result.infusion_duration_h,
    result.pk_parameters,
    result.safety_check,
    targetAucMicForTable,
    weight,
  ]);

  const selectedRegimenOverride = useMemo(
    () => (selectedRegimen !== null ? recalcOverrides[selectedRegimen] ?? null : null),
    [recalcOverrides, selectedRegimen],
  );

  const displayResult = useMemo(() => {
    if (selectedRegimen !== null && regimens[selectedRegimen]) {
      const regimen = regimens[selectedRegimen];
      const override = selectedRegimenOverride;
      const candidateLoading = override?.loading_regimen ?? regimen.loading_regimen ?? result.loading_regimen;
      const hasCandidateLoading = candidateLoading?.enabled !== false
        && normalizeFiniteNumber(candidateLoading?.dose_mg) != null;
      return {
        ...result,
        recommended_dose_mg: regimen.dose,
        recommended_dose_mg_kg: override?.dose_mg_kg ?? regimen.dose_kg,
        dosing_interval_h: override?.interval_h ?? regimen.interval,
        infusion_duration_h: hasZeroInputDuration ? 0 : (regimen.infusion_h ?? result.infusion_duration_h),
        infusion_duration_source: regimen.infusion_duration_source ?? result.infusion_duration_source,
        mic: override?.mic ?? regimen.mic ?? result.mic,
        mic_source: override?.mic_source ?? regimen.mic_source ?? result.mic_source,
        predicted_auc24: override?.predicted_auc24 ?? regimen.predicted_auc24 ?? result.predicted_auc24,
        maintenance_daily_dose_mg: override?.maintenance_daily_dose_mg ?? regimen.maintenance_daily_dose_mg ?? result.maintenance_daily_dose_mg,
        maintenance_rate_mg_h: override?.maintenance_rate_mg_h ?? regimen.maintenance_rate_mg_h ?? result.maintenance_rate_mg_h,
        predicted_css: override?.predicted_css ?? regimen.predicted_css ?? result.predicted_css,
        predicted_cmin: override?.predicted_cmin ?? regimen.predicted_cmin ?? result.predicted_cmin,
        predicted_cmax: override?.predicted_cmax ?? regimen.predicted_cmax ?? result.predicted_cmax,
        recommended_loading_dose_mg: override?.recommended_loading_dose_mg
          ?? regimen.recommended_loading_dose_mg
          ?? (hasCandidateLoading ? candidateLoading.dose_mg : undefined),
        recommended_loading_dose_mg_kg: override?.recommended_loading_dose_mg_kg
          ?? regimen.recommended_loading_dose_mg_kg
          ?? (hasCandidateLoading ? candidateLoading.dose_mg_kg : undefined),
        strategy_type: override?.strategy_type
          ?? regimen.strategy_type
          ?? (hasCandidateLoading ? result.strategy_type : undefined),
        loading_regimen: hasCandidateLoading ? candidateLoading : undefined,
        maintenance_regimen: hasCandidateLoading ? {
          ...(override?.maintenance_regimen ?? regimen.maintenance_regimen ?? {}),
          dose_mg: regimen.dose,
          dose_mg_kg: override?.dose_mg_kg ?? regimen.dose_kg,
          interval_h: override?.interval_h ?? regimen.interval,
        } : undefined,
        transition_time_h: hasCandidateLoading
          ? resolveLoadingTransitionTime(candidateLoading, override?.interval_h ?? regimen.interval)
          : undefined,
        bag_interval_h: override?.bag_interval_h ?? regimen.bag_interval_h ?? result.bag_interval_h,
        auc24_mic: regimen.auc24_mic ?? result.auc24_mic,
        target_auc_mic: regimen.target_auc_mic ?? targetAucMicForTable,
        pta: override?.pta ?? regimen.pta ?? (regimen.pkpd_results?.[resolvedMethod]?.pta ?? null),
        evaluation: override?.evaluation ?? regimen.evaluation ?? result.evaluation,
        target_met_raw: override?.target_met_raw ?? regimen.target_met_raw ?? result.target_met_raw,
        target_status: override?.target_status ?? regimen.target_status ?? result.target_status,
        max_dose_exceeded: override?.max_dose_exceeded ?? regimen.max_dose_exceeded ?? result.max_dose_exceeded,
        dose_limit_capped: override?.dose_limit_capped ?? regimen.dose_limit_capped ?? result.dose_limit_capped,
        requires_manual_review: override?.requires_manual_review ?? regimen.requires_manual_review ?? result.requires_manual_review,
        required_dose_mg: override?.required_dose_mg ?? regimen.required_dose_mg ?? result.required_dose_mg,
        required_dose_mg_kg: override?.required_dose_mg_kg ?? regimen.required_dose_mg_kg ?? result.required_dose_mg_kg,
        prediction_method: override?.prediction_method ?? regimen.prediction_method ?? result.prediction_method,
        max_dose_mg_kg: override?.max_dose_mg_kg ?? regimen.max_dose_mg_kg ?? result.max_dose_mg_kg,
        max_dose_mg_m2: override?.max_dose_mg_m2 ?? regimen.max_dose_mg_m2 ?? result.max_dose_mg_m2,
        is_optimal: override?.is_optimal ?? regimen.is_optimal ?? result.is_optimal,
        safety_check: override?.safety_check ?? regimen.safety_check ?? result.safety_check,
        pkpd_results: normalizePkpdResults(
          override?.pkpd_results ?? regimen.pkpd_results ?? result.pkpd_results,
          resolvedMethod,
        ),
        pk_parameters: override?.pk_parameters ?? regimen.pk_parameters ?? result.pk_parameters,
        simulation_data: override?.simulation_data
          ?? regimen.simulation_data,
        combined_curve: override?.combined_curve
          ?? regimen.combined_curve,
        tdm_points: override?.tdm_points ?? regimen.tdm_points ?? result.tdm_points,
        regimen_history: override?.regimen_history ?? regimen.regimen_history ?? result.regimen_history,
      };
    }

    return {
      ...result,
      target_auc_mic: (result as any).target_auc_mic ?? targetAucMicForTable,
      pkpd_results: normalizePkpdResults(result.pkpd_results, resolvedMethod),
    };
  }, [hasZeroInputDuration, regimens, resolvedMethod, result, selectedRegimen, selectedRegimenOverride, targetAucMicForTable]);

  const displayPkpdResults = useMemo(
    () => normalizePkpdResults(displayResult.pkpd_results, resolvedMethod),
    [displayResult.pkpd_results, resolvedMethod],
  );
  const finalPkpdResults = useMemo(
    () => (pkpdOverride ? { ...displayPkpdResults, ...pkpdOverride } : displayPkpdResults),
    [displayPkpdResults, pkpdOverride],
  );

  const regimenStatuses = useMemo(
    () => regimens.map((regimen: any) => resolveRegimenStatus({ regimen, selectedMethod: resolvedMethod, drugConfig })),
    [drugConfig, regimens, resolvedMethod],
  );
  const activeRegimenStatus = useMemo(
    () => resolveRegimenStatus({
      regimen: {
        ...displayResult,
        pkpd_results: finalPkpdResults,
      },
      selectedMethod: resolvedMethod,
      drugConfig,
    }),
    [displayResult, drugConfig, finalPkpdResults, resolvedMethod],
  );
  const activeEvaluationStatus = useMemo(
    () => resolveEvaluationStatus(activeRegimenStatus, (displayResult as any)?.pta),
    [activeRegimenStatus, displayResult],
  );

  const hasSelectedRegimen = selectedRegimen !== null && !!regimens[selectedRegimen];
  const hasCurrentNoAdjustment = regimens.length === 0 && activeRegimenStatus.clinicallyMet;
  const shouldShowDoseDetails = hasSelectedRegimen || hasCurrentNoAdjustment;

  useEffect(() => {
    if ((!hasSelectedRegimen && !hasCurrentNoAdjustment) || !result.calculation_id) {
      setRegenInterpretation(undefined);
      return;
    }
    if (!displayResult) return;
    let cancelled = false;
    setInterpretLoading(true);
    doseAPI.interpret(result.calculation_id, {
      selected_regimen_active: hasSelectedRegimen,
      dose: displayResult.recommended_dose_mg,
      dose_kg: displayResult.recommended_dose_mg_kg,
      interval: displayResult.dosing_interval_h,
      infusion_h: displayResult.infusion_duration_h,
      auc24: displayResult.predicted_auc24,
      strategy_type: displayResult.strategy_type,
      transition_time_h: displayResult.transition_time_h ?? undefined,
      recommended_loading_dose_mg: displayResult.recommended_loading_dose_mg,
      recommended_loading_dose_mg_kg: displayResult.recommended_loading_dose_mg_kg,
      loading_regimen: displayResult.loading_regimen,
      maintenance_regimen: displayResult.maintenance_regimen,
    }, selectedMethod).then((text) => {
      if (cancelled) return;
      setRegenInterpretation(text);
    }).catch(() => {
      if (cancelled) return;
      setRegenInterpretation(result.clinical_interpretation);
    }).finally(() => {
      if (!cancelled) setInterpretLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [displayResult, hasCurrentNoAdjustment, hasSelectedRegimen, regimens, result.calculation_id, result.clinical_interpretation, selectedMethod, selectedRegimen]);

  const displayInterpretation = shouldShowDoseDetails
    ? (regenInterpretation ?? result.clinical_interpretation)
    : undefined;
  const safety = displayResult.safety_check || { safe: true, warnings: [], errors: [] };
  const displayCombinedCurveData = useMemo(() => {
    const combinedCurve = displayResult.combined_curve;
    if (!combinedCurve) return null;
    const isModelBased = combinedCurve.model_based === true
      || String(combinedCurve.simulation_source || '').includes('rxSolve');
    const visibleCurve = hasZeroInputDuration && !isModelBased ? null : combinedCurve;
    return projectCanonicalConcentrationCurve(visibleCurve, concentrationUnit);
  }, [concentrationUnit, displayResult.combined_curve, hasZeroInputDuration]);
  const displayTdmPoints = useMemo(
    () => projectCanonicalTdmPoints(displayResult.tdm_points, concentrationUnit),
    [concentrationUnit, displayResult.tdm_points],
  );

  const displaySimulationData = useMemo(() => {
    if (displayResult.simulation_data?.time?.length) {
      return projectCanonicalConcentrationCurve(displayResult.simulation_data, concentrationUnit);
    }
    if (selectedRegimen === null) {
      return projectCanonicalConcentrationCurve(result.simulation_data, concentrationUnit);
    }
    if (hasZeroInputDuration) return null;

    // New calculations carry one model-generated curve per candidate.  This
    // PK-parameter fallback keeps historical IV results displayable without
    // triggering a dose recalculation merely because the user selects a row.
    const pk = displayResult.pk_parameters || result.pk_parameters;
    const firstPositive = (keys: string[]): number | null => {
      for (const key of keys) {
        const value = normalizeFiniteNumber(pk?.[key]);
        if (value !== null && value > 0) return value;
      }
      return null;
    };
    const vc = firstPositive(['Vc', 'VC', 'vc', 'V', 'V1', 'Vd', 'vd', 'volume', 'Vc_individual', 'Vc_pop']);
    const cl = firstPositive(['CL', 'Cl', 'cl', 'clearance', 'clearance_l_h', 'CL_individual', 'CL_pop']);
    const ke = firstPositive(['ke', 'Ke', 'kel', 'KEL']) ?? (cl && vc ? cl / vc : null);
    const dose = normalizeFiniteNumber(displayResult.recommended_dose_mg);
    const interval = normalizeFiniteNumber(displayResult.dosing_interval_h);
    const infusion = normalizeFiniteNumber(displayResult.infusion_duration_h);
    if (!ke || !vc || !dose || !interval || infusion == null || infusion <= 0) return null;
    return projectCanonicalConcentrationCurve(
      generatePKCurve(
        ke,
        vc,
        dose,
        interval,
        infusion,
        isContinuousMode ? 10 : calcSimDoses(interval),
      ),
      concentrationUnit,
    );
  }, [concentrationUnit, displayResult, hasZeroInputDuration, isContinuousMode, result.pk_parameters, result.simulation_data, selectedRegimen]);

  const getRegimenColValue = (record: any, col: RegimenColumn): number | undefined => {
    if (col.source === 'field') {
      const value = record[col.dataKey];
      return typeof value === 'number' ? value : undefined;
    }
    const pkpd = record.pkpd_results?.[resolvedMethod];
    if (!pkpd) return undefined;
    const parts = col.dataKey.split('.');
    let value: any = pkpd;
    parts.forEach((part) => {
      value = value?.[part];
    });
    return typeof value === 'number' ? value : undefined;
  };

  const formatColValue = (value: number | undefined, col: RegimenColumn): string => {
    if (value == null) return '-';
    const key = col.dataKey.toLowerCase();
    let projected = value;
    if (key.includes('auc') && !key.includes('mic')) {
      projected = convertCanonicalValue(value, aucUnit, 'auc') ?? value;
    } else if (/(cmin|cmax|css|concentration|trough|peak)/.test(key) && !key.includes('mic')) {
      projected = convertCanonicalValue(value, concentrationUnit, 'concentration') ?? value;
    } else if (key.includes('rate_mg_h')) {
      projected = convertCanonicalValue(value, `${doseMassUnit}/h`, 'dose_rate') ?? value;
    }
    const display = formatDisplayNumber(projected, col.decimals ?? 2);
    return col.format === 'percent' ? `${display}%` : display;
  };

  const renderRecalcFeedback = (index: number) => {
    if (recalcStatus[index] === 'error') {
      return <Text type="danger">重算失败</Text>;
    }
    if (recalcStatus[index] === 'loading') {
      return <Text type="secondary">重算中...</Text>;
    }
    return null;
  };

  const handleContinue = () => {
    if (!shouldShowDoseDetails) {
      message.warning('请先选择一个候选给药方案');
      return;
    }
    if (selectedRegimen !== null && recalcStatus[selectedRegimen] === 'loading') {
      message.warning('当前候选方案正在完整重算，请等待完成后再生成报告');
      return;
    }
    if (selectedRegimen !== null && recalcStatus[selectedRegimen] === 'error') {
      message.error('当前候选方案重算失败，请修正输入并重试后再生成报告');
      return;
    }
    const finalResult = {
      ...displayResult,
      pkpd_results: finalPkpdResults,
      report_has_selected_regimen: hasSelectedRegimen,
      report_selected_regimen: hasSelectedRegimen ? {
        ...displayResult,
        pkpd_results: finalPkpdResults,
      } : null,
      ...(editedInterpretation !== undefined
        ? { clinical_interpretation: editedInterpretation }
        : displayInterpretation !== undefined
          ? { clinical_interpretation: displayInterpretation }
          : {}),
    };
    onContinue(finalResult);
  };

  const regimenSelectCell = (index: number) => (
    <Radio
      checked={selectedRegimen === index}
      onChange={() => setSelectedRegimen(index)}
      onClick={(event) => event.stopPropagation()}
    />
  );

  const regimenEvaluationCell = (record: any, index: number) => {
    const evaluationStatus = resolveEvaluationStatus(regimenStatuses[index], record?.pta);
    return record._recalc_loading
      ? <Spin size="small" />
      : (
        <Tag
          color={evaluationStatus.color}
          style={{ margin: 0, paddingInline: 5, fontSize: 11, lineHeight: '18px', whiteSpace: 'nowrap', display: 'inline-flex' }}
        >
          {evaluationStatus.label}
        </Tag>
      );
  };

  const formatInfusionSourceLabel = (source: unknown) => {
    if (source === 'user_input') return '用户设置';
    if (source === 'drug_config_default') return '默认';
    if (source === 'administration_mode') return '给药模式';
    return '来源未标记';
  };

  const renderCandidateDoseEditor = (
    record: any,
    index: number,
    width: React.CSSProperties['width'] = 88,
  ) => (
    <InputNumber
      size="small"
      min={1}
      step={doseMassUnit === 'mg' ? 1 : 0.001}
      precision={doseMassUnit === 'mg' ? 0 : 3}
      value={convertCanonicalValue(modifiedDoses[index] ?? record.dose, doseMassUnit, 'mass') ?? undefined}
      style={{ width }}
      onClick={(event) => event.stopPropagation()}
      onChange={(nextValue) => handleCandidateDoseChange(
        index,
        nextValue == null ? null : convertUnitValue(nextValue, doseMassUnit, 'mg', 'mass'),
        baseRegimens[index] || record,
      )}
    />
  );

  const renderCandidateInfusionEditor = (
    record: any,
    index: number,
    width: React.CSSProperties['width'] = 76,
  ) => (
    <InputNumber
      size="small"
      min={0.1}
      max={normalizeFiniteNumber(record.interval) ?? undefined}
      step={0.1}
      precision={2}
      value={modifiedInfusions[index] ?? normalizeFiniteNumber(record.infusion_h) ?? undefined}
      style={{ width }}
      onClick={(event) => event.stopPropagation()}
      onChange={(value) => handleCandidateInfusionChange(index, value, baseRegimens[index] || record)}
    />
  );

  const hasLoadingStrategy = result.strategy_type === 'loading_then_maintenance'
    || result.loading_regimen?.enabled === true
    || regimens.some((regimen: any) => regimen?.strategy_type === 'loading_then_maintenance');

  const desktopRegimenColumns = [
    {
      title: '选',
      dataIndex: 'select',
      width: 44,
      align: 'center' as const,
      render: (_: any, record: any, index: number) => regimenSelectCell(
        resolveCandidateSourceIndex(record, index),
      ),
    },
    {
      title: `剂量 (${doseMassUnit})`,
      dataIndex: 'dose',
      width: 116,
      render: (_value: number, record: any, index: number) => (
        <Space direction="vertical" size={2}>
          {renderCandidateDoseEditor(record, resolveCandidateSourceIndex(record, index))}
          <Text type="secondary" style={{ fontSize: 11 }}>{formatResultDosePerKg(record.dose_kg)}</Text>
          {renderRecalcFeedback(resolveCandidateSourceIndex(record, index))}
        </Space>
      ),
    },
    ...(!isContinuousMode && !hasZeroInputDuration ? [{
      title: '输注 (h)',
      dataIndex: 'infusion_h',
      width: 90,
      render: (_value: number, record: any, index: number) => (
        <Space direction="vertical" size={2}>
          {renderCandidateInfusionEditor(record, resolveCandidateSourceIndex(record, index))}
          <Text type="secondary" style={{ fontSize: 11 }}>
            {formatInfusionSourceLabel(record.infusion_duration_source)}
          </Text>
        </Space>
      ),
    }] : []),
    ...(hasLoadingStrategy ? [{
      title: '负荷 → 维持',
      key: 'loading_maintenance',
      width: 220,
      render: (_: any, record: any) => record.loading_regimen?.dose_mg != null
        ? (
          <div className="dose-calculation__loading-maintenance-summary">
            <span className="dose-calculation__loading-maintenance-label">负荷方案</span>
            <span className="dose-calculation__loading-maintenance-value">
              {formatResultDose(record.loading_regimen.dose_mg)} × {record.loading_regimen.count || 1} 次
            </span>
            <span className="dose-calculation__loading-maintenance-label">维持方案</span>
            <span className="dose-calculation__loading-maintenance-value">
              {formatResultDose(record.maintenance_regimen?.dose_mg ?? record.dose)} q{record.maintenance_regimen?.interval_h ?? record.interval}h
            </span>
            {record.transition_time_h != null && (
              <>
                <span className="dose-calculation__loading-maintenance-label">转换时间</span>
                <span className="dose-calculation__loading-maintenance-value">{record.transition_time_h} h 起</span>
              </>
            )}
          </div>
        )
        : '-',
    }] : []),
    ...methodRegimenCols.map((col) => ({
      title: <Tooltip title={col.title}><span>{col.title.replace(/\s*\(.*\)$/, '')}</span></Tooltip>,
      dataIndex: col.dataKey,
      width: col.width ?? 76,
      ellipsis: true,
      render: (_: any, record: any) => {
        if (record._recalc_loading) return <Spin size="small" />;
        const value = getRegimenColValue(record, col);
        return (
          <span style={record._modified ? { color: '#0B2545', fontWeight: 500 } : undefined}>
            {formatColValue(value, col)}
          </span>
        );
      },
    })),
    {
      title: '评价',
      key: 'evaluation',
      width: 100,
      align: 'center' as const,
      className: 'candidate-evaluation-column',
      render: (_: any, record: any, index: number) => regimenEvaluationCell(
        record,
        resolveCandidateSourceIndex(record, index),
      ),
    },
  ];

  const renderMobileRegimenCard = (record: any, sourceIndex: number) => {
    const evaluationStatus = resolveEvaluationStatus(regimenStatuses[sourceIndex], record?.pta);
    const recalcState: CandidateRegimenRecalcState = (
      recalcStatus[sourceIndex] === 'loading' || record?._recalc_loading
        ? 'loading'
        : recalcStatus[sourceIndex] === 'error' || record?._recalc_error
          ? 'error'
          : 'idle'
    );
    const intervalLabel = isContinuousMode ? '连续输注' : `Q${record.interval}h`;

    return (
      <EditableCandidateRegimenCard
        title={formatResultDose(record.dose)}
        subtitle={`${intervalLabel} · ${formatResultDosePerKg(record.dose_kg)}`}
        metrics={methodRegimenCols.map((col) => ({
          key: col.dataKey,
          label: col.title,
          value: formatColValue(getRegimenColValue(record, col), col),
        }))}
        phaseSummary={record.strategy_type === 'loading_then_maintenance' ? {
          loading: record.loading_regimen,
          maintenance: record.maintenance_regimen,
          loadingDoseLabel: formatResultDose(record.loading_regimen?.dose_mg),
          maintenanceDoseLabel: formatResultDose(record.maintenance_regimen?.dose_mg ?? record.dose),
          transitionTimeH: record.transition_time_h,
        } : undefined}
        editors={[
          {
            key: 'dose',
            label: `剂量 (${doseMassUnit})`,
            control: renderCandidateDoseEditor(record, sourceIndex, '100%'),
          },
          ...(!isContinuousMode && !hasZeroInputDuration ? [{
            key: 'infusion',
            label: '输注时间 (h)',
            control: renderCandidateInfusionEditor(record, sourceIndex, '100%'),
          }] : []),
        ]}
        evaluationLabel={evaluationStatus.label}
        evaluationColor={evaluationStatus.color}
        selected={selectedRegimen === sourceIndex}
        modified={Boolean(record?._modified)}
        recalcState={recalcState}
        selectionLabel={`选择 ${intervalLabel}、${formatResultDose(record.dose)} 的候选方案`}
        onSelect={() => setSelectedRegimen(sourceIndex)}
      />
    );
  };

  return (
    <div style={{ marginTop: 24 }}>
      <Divider />
      <Title level={5}><CalculatorOutlined /> 计算结果</Title>

      {(result.dose_limit_capped || result.requires_manual_review) && (
        <Alert
          type="warning"
          showIcon
          message="已达到最大安全剂量，仍可能低于治疗目标，需人工复核"
          description={result.required_dose_mg != null
            ? `模型达到目标原需约 ${formatResultDose(result.required_dose_mg)}/次，系统已按配置上限封顶为 ${formatResultDose(result.recommended_dose_mg)}/次。封顶方案的 PK/PD 指标已按实际剂量重新计算，PTA 不沿用原超限方案。`
            : '所有模型候选方案均超过配置的单次最大剂量；系统已返回安全封顶方案供复核，未自动放宽剂量上限。'}
          style={{ marginBottom: 16 }}
        />
      )}

      {regimens.length > 0 && (
        <Card title="候选给药方案（请选择一个方案写入报告）" size="small" style={{ marginBottom: 16 }}>
          <Alert
            message="可直接调整每行的剂量与输注时间；任一数值变化都会完整重算该行的 PK/PD、PTA、评价与药时曲线。"
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            banner
          />
          <GroupedCandidateRegimenTable
            className="dose-calculation__candidate-table"
            rows={regimens}
            columns={desktopRegimenColumns}
            selectedIndex={selectedRegimen}
            onSelect={setSelectedRegimen}
            getEvaluation={(regimen, index) => resolveEvaluationStatus(
              regimenStatuses[index],
              regimen?.pta,
            )}
            isRecommended={(regimen) => regimen?.is_recommended === true}
            formatDose={(dose) => formatResultDose(dose)}
            isContinuous={isContinuousMode}
            isMobile={isMobileShell}
            renderMobileRow={renderMobileRegimenCard}
            resetKey={result.calculation_id}
          />
          {!hasSelectedRegimen && (
            <Alert
              message="请选择一个候选给药方案后查看个体化结果、曲线和临床解读。"
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
            />
          )}
        </Card>
      )}

      {shouldShowDoseDetails ? (
      <>
        {hasCurrentNoAdjustment && (
          <Alert
            type="info"
            showIcon
            message="当前方案已满足当前评价规则，可供临床复核。"
            description="下方展示当前方案的安全检查、PK/PD 分析、药时曲线和 AI 临床解读；是否维持当前方案或进一步调整，请结合临床情况由用户决定。"
            style={{ marginBottom: 16 }}
          />
        )}
        <Row gutter={[16, 16]} className="dose-calculation__result-grid">
          <Col xs={24} md={12}>
            <Card
              title={hasCurrentNoAdjustment ? '当前给药方案' : '已选给药方案'}
              size="small"
              className="dose-calculation__result-card dose-calculation__clinical-card"
              extra={<Tag color={activeEvaluationStatus.color}>{activeEvaluationStatus.label}</Tag>}
            >
              <Descriptions column={1} size="small">
                {isContinuousMode && (
                  <>
                    <Descriptions.Item label="维持总日剂量">
                      <Text strong style={{ fontSize: 18, color: '#0B2545' }}>
                        {formatResultDose(displayResult.maintenance_daily_dose_mg ?? displayResult.recommended_dose_mg)}/24h
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="泵速">
                      {formatResultDose(displayResult.maintenance_rate_mg_h ?? 0)}/h
                    </Descriptions.Item>
                    {continuousPrimaryMetric?.dataKey === 'predicted_css' && (
                      <Descriptions.Item label={continuousPrimaryMetric.label}>
                        {formatConcentrationValue(
                          displayResult.predicted_css ?? displayResult.predicted_cmin,
                          continuousPrimaryMetric.decimals,
                        )}
	                      </Descriptions.Item>
                    )}
	                    {displayResult.bag_interval_h != null && (
	                      <Descriptions.Item label="连续输注间隔">
	                        每 {displayResult.bag_interval_h} 小时
	                      </Descriptions.Item>
	                    )}
	                    {displayResult.recommended_loading_dose_mg != null && (
                      <Descriptions.Item label="负荷剂量">
                        {formatResultDose(displayResult.recommended_loading_dose_mg)}
                        {displayResult.recommended_loading_dose_mg_kg != null ? ` (${formatResultDosePerKg(displayResult.recommended_loading_dose_mg_kg)})` : ''}
                      </Descriptions.Item>
                    )}
                  </>
                )}
                {!isContinuousMode && (
                  <>
                    <Descriptions.Item label="所选剂量">
                      <Text strong style={{ fontSize: 18, color: '#0B2545' }}>
                        {formatResultDose(displayResult.recommended_dose_mg)} ({formatResultDosePerKg(displayResult.recommended_dose_mg_kg)})
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="给药间隔">每 {displayResult.dosing_interval_h} 小时</Descriptions.Item>
                    {!hasZeroInputDuration && (
                      <Descriptions.Item label="输注时间">{displayResult.infusion_duration_h} 小时</Descriptions.Item>
                    )}
                    {displayResult.strategy_type === 'loading_then_maintenance' && displayResult.loading_regimen?.dose_mg != null && (
                      <Descriptions.Item label="负荷 → 维持">
                        <Space direction="vertical" size={0}>
                          <Text type="warning">
                            负荷剂量 {formatResultDose(displayResult.loading_regimen.dose_mg)} × {displayResult.loading_regimen.count || 1}
                          </Text>
                          <Text type="secondary">
                            维持剂量 {formatResultDose(displayResult.maintenance_regimen?.dose_mg ?? displayResult.recommended_dose_mg)} q{displayResult.maintenance_regimen?.interval_h ?? displayResult.dosing_interval_h}h
                          </Text>
                          {displayResult.transition_time_h != null && (
                            <Text type="secondary">{displayResult.transition_time_h} h 起进入维持期</Text>
                          )}
                        </Space>
                      </Descriptions.Item>
                    )}
                  </>
                )}
                {resolvedMethod === 'Target AUC' && (
                  <Descriptions.Item label="目标AUC₂₄范围">
                    {targetAucRangeForSummary || 'N/A'}
                  </Descriptions.Item>
                )}
                {methodRegimenCols.map((col) => {
                  const pkpd = finalPkpdResults?.[resolvedMethod];
                  let value: any;
                  if (col.source === 'field') {
                    value = (displayResult as any)[col.dataKey];
                  } else {
                    const parts = col.dataKey.split('.');
                    value = pkpd;
                    parts.forEach((part) => {
                      value = value?.[part];
                    });
                  }
                  const display = value != null
                    ? formatColValue(Number(value), col)
                    : 'N/A';
                  return (
                    <Descriptions.Item key={col.dataKey} label={col.title}>
                      {display}
                    </Descriptions.Item>
                  );
                })}
              </Descriptions>
              {selectedRegimen !== null && recalcStatus[selectedRegimen] === 'loading' && (
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  当前方案指标重算中...
                </Text>
              )}
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <div className="dose-calculation__clinical-stack">
              <Card title="安全检查" size="small" className="dose-calculation__clinical-card dose-calculation__safety-card">
                <div className={`dose-calculation__safety-status ${!safety.safe ? 'dose-calculation__safety-status--danger' : safety.warnings?.length ? 'dose-calculation__safety-status--warning' : 'dose-calculation__safety-status--ok'}`}>
                  {safety.safe ? (
                    <Tag color="green" icon={<SafetyOutlined />}>安全检查通过</Tag>
                  ) : (
                    <Tag color="red">存在安全风险</Tag>
                  )}
                  <span className="dose-calculation__safety-copy">
                    {safety.safe && !safety.warnings?.length && !safety.errors?.length
                      ? '安全边界内'
                      : `${(safety.warnings?.length || 0) + (safety.errors?.length || 0)} 项需复核`}
                  </span>
                </div>
                {((safety.warnings?.length || 0) > 0 || (safety.errors?.length || 0) > 0) && (
                  <div className="dose-calculation__safety-alerts">
                    {safety.warnings?.map((warning: any, index: number) => (
                      <Alert key={index} type="warning" message={typeof warning === 'string' ? warning : (warning?.msg || JSON.stringify(warning))} banner />
                    ))}
                    {safety.errors?.map((error: any, index: number) => (
                      <Alert key={index} type="error" message={typeof error === 'string' ? error : (error?.msg || JSON.stringify(error))} banner />
                    ))}
                  </div>
                )}
              </Card>

            {hasDisplayablePkParameters(displayResult.pk_parameters) && (
              <Card title="个体化 PK 参数 (CL, V)" size="small" className="dose-calculation__clinical-card dose-calculation__pk-card">
                <Descriptions
                  column={isMobileShell ? 1 : 2}
                  size="small"
                  styles={{
                    label: { minWidth: 132, whiteSpace: 'normal' },
                    content: { minWidth: 96 },
                  }}
                >
                  {Object.entries(displayResult.pk_parameters).filter(
                    ([key, value]) => PK_PARAMETER_KEYS.has(key) && normalizeFiniteNumber(value) !== null,
                  ).map(([key, value]) => {
                    let label = key;
                    let unit = '';
                    if (['CL', 'Cl', 'cl', 'clearance', 'clearance_l_h', 'CL_individual'].includes(key)) { label = '清除率 (CL)'; unit = ' L/h'; }
                    else if (key === 'CL_pop') { label = '群体 CL'; unit = ' L/h'; }
                    else if (['Vc', 'VC', 'vc', 'V', 'V1', 'Vd', 'vd', 'volume', 'Vc_individual'].includes(key)) { label = '分布容积 (Vc)'; unit = ' L'; }
                    else if (key === 'Vc_pop') { label = '群体 Vc'; unit = ' L'; }
                    else if (['ke', 'Ke', 'kel', 'KEL'].includes(key)) { label = '消除速率常数 (ke)'; unit = ' h⁻¹'; }
                    else if (key === 'half_life') { label = '半衰期 (t½)'; unit = ' h'; }
                    return (
                      <Descriptions.Item key={key} label={label}>
                        {typeof value === 'number' ? formatDisplayNumber(value, 2) : String(value)}{unit}
                      </Descriptions.Item>
                    );
                  })}
                  {result.steady_state_info && (
                    <Descriptions.Item label="达稳态时间">
                      约 {result.steady_state_info.n_doses} 个剂量（~{result.steady_state_info.time_h} 小时）
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}
            </div>
          </Col>
        </Row>

        {(displayCombinedCurveData || displaySimulationData) && (
          <Card title="药物浓度-时间曲线" style={{ marginTop: 16 }}>
            <PKCurve
              key={`pk-${selectedRegimen ?? 'default'}-${displayResult.dosing_interval_h}-${displayResult.recommended_dose_mg}-${displayCombinedCurveData ? 'combined' : 'single'}`}
              simulationData={displaySimulationData}
              combinedCurveData={displayCombinedCurveData}
              targetRange={(() => {
                const paramMap: Record<string, string> = {
                  'Target Concentration': isContinuousMode ? continuousTargetParameter : 'Cmin',
                  'Target Cmax': 'Cmax',
                  'Target AUC': 'AUC24',
                  'AUC/MIC': 'AUC24/MIC',
                  '%fT>MIC': '%fT>MIC',
                };
                const target = findTargetForAdministrationMode(
                  drugConfig,
                  resolvedMethod,
                  resolvedAdministrationMode,
                  continuousTargetParameter,
                  paramMap[resolvedMethod],
                );
                if (!target) return {};
                const parameter = String(target.parameter || '').toLowerCase();
                if (parameter.includes('auc') || parameter.includes('ft>') || parameter.includes('ft >')) return {};
                return { troughMin: target.min, troughMax: target.max };
              })()}
              concentrationUnit={concentrationUnit}
              mic={convertCanonicalValue(result.pkpd_results?.['%fT>MIC']?.details?.default_mic, micUnit, 'concentration') ?? undefined}
              nephrotoxicityCmin={getExposureSafetyLimit(drugConfig, 'exposure.cmin', concentrationUnit, {
                selectedMethod: resolvedMethod,
                administrationMode: resolvedAdministrationMode,
                covariates,
              })}
              tdmPoints={displayTdmPoints}
              doseEvents={(displaySimulationData?.dose_events || []).map((event: any) => ({
                time: Number(event.time_h ?? event.time ?? 0),
                dose: Number(event.dose_mg ?? event.dose ?? 0),
                eventType: String(event.event_type ?? event.phase ?? 'maintenance'),
                label: String(event.label ?? event.event_type ?? event.phase ?? 'Dose'),
                doseLabel: event.dose_mg != null ? `${formatResultDose(event.dose_mg)}` : undefined,
              }))}
              phaseMarkers={displaySimulationData?.phase_markers}
              micUnit={micUnit}
            />
          </Card>
        )}

        <PkPdResultsCard
          pkpdResults={finalPkpdResults}
          preferredMethod={resolvedMethod}
          evaluationRegimen={{ ...displayResult, pkpd_results: finalPkpdResults }}
          onPkpdResultsChange={setPkpdOverride}
          drugConfig={drugConfig}
          administrationMode={resolvedAdministrationMode}
        />

        <ClinicalInterpretation
          interpretation={displayInterpretation}
          regimenChanged={selectedRegimen !== null}
          activeRegimen={{
            dose: displayResult.recommended_dose_mg,
            interval: displayResult.dosing_interval_h,
            infusionH: displayResult.infusion_duration_h,
          }}
          loading={interpretLoading}
          onInterpretationChange={setEditedInterpretation}
        />

        {result._note && (
          <Alert type="info" message={result._note} style={{ marginTop: 16 }} showIcon />
        )}

        {renderContinueAction(isMobileShell, handleContinue)}
      </>
      ) : regimens.length === 0 ? (
        <Alert
          message="请选择一个候选给药方案后查看个体化结果、曲线和临床解读。"
          type="info"
          showIcon
        />
      ) : null}
    </div>
  );
};

/** PK公式法结果展示 */
const PKFormulaResultDisplay: React.FC<{
  result: PKFormulaResult;
  onContinue: (r: any) => void;
  drugConfig?: any;
  selectedMethod?: string;
  weight?: number;
  covariates?: Covariates;
  adminReplay?: boolean;
}> = ({ result, onContinue, drugConfig, selectedMethod, weight, covariates, adminReplay = false }) => {
  const { isMobileShell } = useResponsive();
  const safety = result.safety_check || { safe: true, warnings: [], errors: [] };
  const concentrationUnit = unitFromResult(
    result,
    'concentration',
    drugConfig?.units?.concentration?.display_unit || 'mg/L',
  );
  const aucUnit = unitFromResult(result, 'auc', drugConfig?.units?.auc?.display_unit || 'mg*h/L');
  const micUnit = unitFromResult(
    result,
    'mic',
    drugConfig?.units?.mic?.display_unit || concentrationUnit,
  );
  const doseMassUnit = unitFromResult(
    result,
    'dose',
    drugConfig?.units?.dose?.display_mass_unit || 'mg',
  );
  const formatResultDose = (value: unknown) => {
    const numeric = normalizeFiniteNumber(value);
    return numeric == null ? 'N/A' : formatCanonicalDose(numeric, { dose_mass_unit: doseMassUnit });
  };
  const formatResultDosePerKg = (value: unknown) => {
    const numeric = normalizeFiniteNumber(value);
    if (numeric == null) return 'N/A';
    const converted = convertUnitValue(numeric, 'mg/kg', `${doseMassUnit}/kg`, 'dose_per_weight');
    return `${formatDoseMgKg(converted)} ${doseMassUnit}/kg`;
  };
  const formatConcentrationValue = (value: unknown, decimals = 2) => {
    const displayed = convertCanonicalValue(value, concentrationUnit, 'concentration');
    return displayed == null ? 'N/A' : `${displayed.toFixed(decimals)} ${concentrationUnit}`;
  };
  const formatAucValue = (value: unknown, decimals = 1) => {
    const displayed = convertCanonicalValue(value, aucUnit, 'auc');
    return displayed == null ? 'N/A' : `${displayed.toFixed(decimals)} ${aucUnit}`;
  };
  const formatPkParameter = (value: unknown) => {
    const numeric = normalizeFiniteNumber(value);
    return numeric == null ? 'N/A' : numeric.toFixed(2);
  };
  const [editedInterpretation, setEditedInterpretation] = useState<string | undefined>(undefined);
  const [selectedGridRegimen, setSelectedGridRegimen] = useState<{ method: string; index: number; regimen: GridSearchRegimen } | null>(null);
  // 用户在 PkPdResultsCard 中修改 MIC 后的覆盖值
  const [pkpdOverride, setPkpdOverride] = useState<Record<string, PkPdMethodResult> | null>(null);

  // 检测是否为 %fT>MIC 类药物
  const isFtMicDrug = !!(result.pkpd_results?.['%fT>MIC']);
  const isMeropenemFormula = result.strategy_key === 'meropenem_chen_2018_ss_two_point_ftmic'
    || drugConfig?.pk_formula?.strategy_key === 'meropenem_chen_2018_ss_two_point_ftmic';

  // 美罗培南公式法输入的是稳态、输注结束后的两个消除相样本。
  // 样本点只映射到“原方案的一个稳态代表性给药间期”，不再人为投影到第4～5剂。
  const pkFormulaTdmPoints = useMemo(() => {
    const r = result as any;
    const points: { time: number; concentration: number; label?: string }[] = [];
    const infDur = normalizeFiniteNumber(r.infusion_duration_h);
    const interval = normalizeFiniteNumber(r.dosing_interval_h);
    const halfLife = normalizeFiniteNumber(r.half_life);
    const tPeak = normalizeFiniteNumber(r.t_peak);
    const tTrough = normalizeFiniteNumber(r.t_trough);
    const isMeropenemFormula = r.strategy_key === 'meropenem_chen_2018_ss_two_point_ftmic'
      || drugConfig?.pk_formula?.strategy_key === 'meropenem_chen_2018_ss_two_point_ftmic';
    if (infDur == null || infDur <= 0 || interval == null || interval <= 0) return [];
    const displayContext = r.pk_formula_curve_context;
    if (
      isMeropenemFormula
      && displayContext?.mode === 'steady_state_two_intervals'
      && Array.isArray(displayContext.sample_points)
    ) {
      const contextPoints = displayContext.sample_points
        .filter((point: any) => Number.isFinite(Number(point?.time_h)) && Number.isFinite(Number(point?.concentration)))
        .map((point: any) => ({
          time: Number(point.time_h),
          concentration: Number(point.concentration),
          label: point.key === 'early' ? '\u8f83\u65e9\u6837\u672c' : '\u8f83\u665a\u6837\u672c',
          before_next_dose_h: Number(point.before_next_dose_h),
        }));
      if (contextPoints.length > 0) {
        return projectCanonicalTdmPoints(contextPoints, concentrationUnit) || [];
      }
    }
    const doseIndex = Math.min(5, Math.max(4, Math.ceil(4 * (halfLife || interval) / interval)));
    if (isMeropenemFormula && r.early_concentration != null && r.early_concentration > 0) {
      const earlyBefore = normalizeFiniteNumber(r.early_sample_before_next_dose_h);
      const earlyTime = earlyBefore == null ? null : interval - earlyBefore;
      if (earlyTime == null || earlyTime <= infDur || earlyTime >= interval) return [];
      points.push({
        time: +earlyTime.toFixed(2),
        concentration: r.early_concentration,
      });
    } else if (r.measured_trough != null && r.measured_trough > 0 && tTrough != null && tTrough >= 0) {
      points.push({
        time: +(doseIndex * interval - tTrough).toFixed(2),
        concentration: r.measured_trough,
        label: '实测谷浓度',
      });
    }
    if (isMeropenemFormula && r.late_concentration != null && r.late_concentration > 0) {
      const lateBefore = normalizeFiniteNumber(r.late_sample_before_next_dose_h);
      const lateTime = lateBefore == null ? null : interval - lateBefore;
      if (lateTime == null || lateTime <= infDur || lateTime >= interval) return points.length > 0 ? projectCanonicalTdmPoints(points, concentrationUnit) : [];
      points.push({
        time: +lateTime.toFixed(2),
        concentration: r.late_concentration,
      });
    } else if (r.measured_peak != null && r.measured_peak > 0 && tPeak != null && tPeak >= 0) {
      points.push({
        time: +(doseIndex * interval + infDur + tPeak).toFixed(2),
        concentration: r.measured_peak,
        label: '实测峰浓度',
      });
    }
    return projectCanonicalTdmPoints(points.length > 0 ? points : undefined, concentrationUnit);
  }, [concentrationUnit, drugConfig?.pk_formula?.strategy_key, isMeropenemFormula, result]);

  // 候选方案的 PK/PD 结果来自后端完整重算，不在浏览器内套用近似公式。
  const displayPkpdResults = useMemo(() => {
    return selectedGridRegimen?.regimen.pkpd_results ?? result.pkpd_results;
  }, [result.pkpd_results, selectedGridRegimen]);

  // 合并用户 MIC 修改
  const finalPkpdResults = useMemo(() => {
    if (!pkpdOverride) return displayPkpdResults;
    return { ...displayPkpdResults, ...pkpdOverride };
  }, [displayPkpdResults, pkpdOverride]);

  const ftMicData = finalPkpdResults?.['%fT>MIC'];
  const ftMicDetails = ftMicData?.details;
  const sanfordAdjustment: any = result.sanford_adjustment;
  const sanfordGoalReasonMap: Record<string, string> = {
    below_target_use_lower_bound: '当前 AUC24 低于下限，默认按目标下限回推',
    above_target_use_upper_bound: '当前 AUC24 高于上限，默认按目标上限回推',
    within_target_default_to_lower_bound: '当前已达标，默认按目标下限回推',
    user_requested_goal: '按当前选定目标值回推',
  };
  const resolvedWeight = useMemo(() => {
    if (typeof weight === 'number' && weight > 0) return weight;
    if (
      typeof result.recommended_dose_mg === 'number'
      && typeof result.recommended_dose_mg_kg === 'number'
      && result.recommended_dose_mg_kg > 0
    ) {
      return result.recommended_dose_mg / result.recommended_dose_mg_kg;
    }
    return 0;
  }, [result.recommended_dose_mg, result.recommended_dose_mg_kg, weight]);
  const targetMidpointDisplay = useMemo(() => {
    if (!sanfordAdjustment) return undefined;
    return Number(((sanfordAdjustment.display_target_min + sanfordAdjustment.display_target_max) / 2).toFixed(1));
  }, [sanfordAdjustment]);
  const [manualDoseMg, setManualDoseMg] = useState<number | null>(result.recommended_dose_mg ?? result.dose ?? null);
  const [manualIntervalH, setManualIntervalH] = useState<number | null>(result.dosing_interval_h ?? result.dosing_interval ?? null);
  const [manualInfusionH, setManualInfusionH] = useState<number | null>(result.infusion_duration_h ?? null);
  const [manualTargetDisplay, setManualTargetDisplay] = useState<number | null>(targetMidpointDisplay ?? null);
  const [modifiedAucMicDoses, setModifiedAucMicDoses] = useState<Record<number, number>>({});
  const [aucMicRecalcRows, setAucMicRecalcRows] = useState<Record<number, GridSearchRegimen>>({});
  const [aucMicRecalcStatus, setAucMicRecalcStatus] = useState<Record<number, 'loading' | 'done' | 'error'>>({});
  const [aucMicRecalcErrors, setAucMicRecalcErrors] = useState<Record<number, string>>({});
  const aucMicDebounceRefs = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const aucMicLatestRequestRef = useRef<Record<number, string>>({});
  const [manualAdjustmentRegimen, setManualAdjustmentRegimen] = useState<(GridSearchRegimen & {
    predicted_auc24: number;
    pkpd_results: Record<string, PkPdMethodResult>;
  }) | null>(null);
  const [manualRecalcLoading, setManualRecalcLoading] = useState(false);
  const [manualRecalcError, setManualRecalcError] = useState<string | null>(null);

  // 每次重新计算都回到原给药方案，避免沿用上一次候选方案导致曲线和结果未刷新。
  useEffect(() => {
    setSelectedGridRegimen(null);
    setPkpdOverride(null);
    setManualAdjustmentRegimen(null);
  }, [result.calculation_id]);

  // 根据选中方案调用后端重新生成AI解读
  const [regenInterpretation, setRegenInterpretation] = useState<string | undefined>(undefined);
  const [interpretLoading, setInterpretLoading] = useState(false);

  useEffect(() => {
    setEditedInterpretation(undefined);
  }, [selectedGridRegimen]);

  useEffect(() => {
    const sourceRegimen = selectedGridRegimen?.regimen;
    setManualDoseMg(sourceRegimen?.dose_mg ?? result.recommended_dose_mg ?? result.dose ?? null);
    setManualIntervalH(sourceRegimen?.interval_h ?? result.dosing_interval_h ?? result.dosing_interval ?? null);
    setManualInfusionH(sourceRegimen?.infusion_h ?? result.infusion_duration_h ?? null);
  }, [
    result.calculation_id,
    result.dose,
    result.dosing_interval,
    result.dosing_interval_h,
    result.infusion_duration_h,
    result.recommended_dose_mg,
    selectedGridRegimen,
  ]);

  useEffect(() => {
    setManualTargetDisplay(targetMidpointDisplay ?? null);
  }, [result.calculation_id, targetMidpointDisplay]);

  useEffect(() => {
    setModifiedAucMicDoses({});
    setAucMicRecalcRows({});
    setAucMicRecalcStatus({});
    setAucMicRecalcErrors({});
    Object.values(aucMicDebounceRefs.current).forEach((timer) => clearTimeout(timer));
    aucMicDebounceRefs.current = {};
    aucMicLatestRequestRef.current = {};
    return () => {
      Object.values(aucMicDebounceRefs.current).forEach((timer) => clearTimeout(timer));
    };
  }, [result.calculation_id]);

  const pkFormulaTargetMethod = result.target_type || selectedMethod || 'Target AUC';
  const preferredMethod = selectedGridRegimen?.method || selectedMethod || pkFormulaTargetMethod;
  const configuredMic = useMemo(() => {
    const micFromResult = Number(result.mic);
    if (Number.isFinite(micFromResult) && micFromResult > 0) return micFromResult;
    const micFromPkpd = Number(
      result.pkpd_results?.['AUC/MIC']?.details?.mic
      ?? result.pkpd_results?.['Target AUC']?.details?.mic,
    );
    if (Number.isFinite(micFromPkpd) && micFromPkpd > 0) return micFromPkpd;
    const micFromConfig = Number(drugConfig?.default_mic);
    if (Number.isFinite(micFromConfig) && micFromConfig > 0) {
      return convertUnitValue(micFromConfig, micUnit, 'mg/L', 'concentration');
    }
    return null;
  }, [drugConfig?.default_mic, micUnit, result.mic, result.pkpd_results]);
  const handleFormulaCandidateSelect = useCallback(async (
    method: string,
    index: number,
    regimen: GridSearchRegimen,
  ) => {
    if (!isMeropenemFormula || method !== '%fT>MIC') {
      setSelectedGridRegimen({ method, index, regimen });
      return;
    }
    try {
      const payload = {
        calculation_id: result.calculation_id,
        dose_mg: regimen.dose_mg,
        interval_h: regimen.interval_h,
        infusion_h: regimen.infusion_h,
        target_type: '%fT>MIC',
        mic: configuredMic ?? undefined,
      };
      const recalculated = adminReplay
        ? await adminAPI.replayPkFormulaRecalcRegimen(payload)
        : await doseAPI.recalcPkFormulaRegimen(payload);
      setSelectedGridRegimen({
        method,
        index,
        regimen: { ...regimen, ...recalculated, pkpd_results: recalculated.pkpd_results || regimen.pkpd_results },
      });
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.warning(typeof detail === 'string' ? detail : '候选方案重算失败，已保留原候选结果');
      setSelectedGridRegimen({ method, index, regimen });
    }
  }, [adminReplay, configuredMic, isMeropenemFormula, result.calculation_id]);
  const baselineDoseMg = selectedGridRegimen?.regimen.dose_mg ?? result.recommended_dose_mg ?? result.dose ?? 0;
  const baselineIntervalH = selectedGridRegimen?.regimen.interval_h ?? result.dosing_interval_h ?? result.dosing_interval ?? 0;
  const baselineInfusionH = selectedGridRegimen?.regimen.infusion_h ?? result.infusion_duration_h ?? null;
  const manualTargetAbsoluteAuc = useMemo(() => {
    if (!(manualTargetDisplay != null && manualTargetDisplay > 0)) return null;
    if (pkFormulaTargetMethod !== 'AUC/MIC') {
      return Number(convertUnitValue(manualTargetDisplay, aucUnit, 'mg*h/L', 'auc').toFixed(2));
    }
    if (!(configuredMic != null && configuredMic > 0)) return null;
    return Number((manualTargetDisplay * configuredMic).toFixed(2));
  }, [aucUnit, configuredMic, manualTargetDisplay, pkFormulaTargetMethod]);
  const manualAdjustmentDirty = useMemo(() => {
    const dose = normalizeFiniteNumber(manualDoseMg);
    const interval = normalizeFiniteNumber(manualIntervalH);
    const infusion = normalizeFiniteNumber(manualInfusionH);
    if (dose == null || dose <= 0 || interval == null || interval <= 0 || infusion == null || infusion <= 0) {
      return false;
    }
    return (
      Math.abs(dose - baselineDoseMg) > 0.05
      || Math.abs(interval - baselineIntervalH) > 0.05
      || Math.abs(infusion - (baselineInfusionH ?? 0)) > 0.05
    );
  }, [baselineDoseMg, baselineInfusionH, baselineIntervalH, manualDoseMg, manualInfusionH, manualIntervalH]);

  useEffect(() => {
    if (!manualAdjustmentDirty) {
      setManualAdjustmentRegimen(null);
      setManualRecalcError(null);
      setManualRecalcLoading(false);
      return undefined;
    }
    const dose = normalizeFiniteNumber(manualDoseMg);
    const interval = normalizeFiniteNumber(manualIntervalH);
    const infusion = normalizeFiniteNumber(manualInfusionH);
    if (dose == null || interval == null || infusion == null || infusion >= interval) {
      setManualAdjustmentRegimen(null);
      setManualRecalcError('输注时间必须大于 0 且小于给药间隔');
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setManualRecalcLoading(true);
      setManualRecalcError(null);
      try {
        const recalcPayload = {
          calculation_id: result.calculation_id,
          dose_mg: dose,
          interval_h: interval,
          infusion_h: infusion,
          target_type: pkFormulaTargetMethod,
          mic: configuredMic ?? undefined,
        };
        const recalculated = adminReplay
          ? await adminAPI.replayPkFormulaRecalcRegimen(recalcPayload)
          : await doseAPI.recalcPkFormulaRegimen(recalcPayload);
        if (!cancelled) {
          setManualAdjustmentRegimen(recalculated as GridSearchRegimen & {
            predicted_auc24: number;
            pkpd_results: Record<string, PkPdMethodResult>;
          });
        }
      } catch (error: any) {
        if (!cancelled) {
          setManualAdjustmentRegimen(null);
          setManualRecalcError(error?.response?.data?.detail || '候选方案重算失败');
        }
      } finally {
        if (!cancelled) setManualRecalcLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    configuredMic,
    manualAdjustmentDirty,
    manualDoseMg,
    manualInfusionH,
    manualIntervalH,
    pkFormulaTargetMethod,
    result.calculation_id,
  ]);
  const manualTargetRecommendations = useMemo(() => {
    if (!manualAdjustmentRegimen || !(manualTargetAbsoluteAuc && manualTargetAbsoluteAuc > 0)) return null;
    const currentDailyDose = manualAdjustmentRegimen.dose_mg * 24 / manualAdjustmentRegimen.interval_h;
    const targetDailyDose = currentDailyDose * manualTargetAbsoluteAuc / manualAdjustmentRegimen.predicted_auc24;
    if (!(targetDailyDose > 0)) return null;
    return {
      targetDailyDoseMg: Number(targetDailyDose.toFixed(1)),
      sameIntervalDoseMg: Number((targetDailyDose * manualAdjustmentRegimen.interval_h / 24).toFixed(1)),
      sameDoseIntervalH: Number((24 * manualAdjustmentRegimen.dose_mg / targetDailyDose).toFixed(2)),
    };
  }, [manualAdjustmentRegimen, manualTargetAbsoluteAuc]);
  const editableAucMicRows = useMemo(() => {
    const sourceRows = (result.grid_search_results?.['AUC/MIC'] || sanfordAdjustment?.candidates || []) as GridSearchRegimen[];
    return sourceRows.map((row, index) => {
      const baselineDose = row.dose_mg ?? (row as any).dose ?? 0;
      const modifiedDose = modifiedAucMicDoses[index];
      if (!(modifiedDose != null) || Math.abs(modifiedDose - baselineDose) < 0.05) {
        return row;
      }
      const rowWithoutEvaluation = { ...(row as any) };
      delete rowWithoutEvaluation.evaluation;
      const recalculated = aucMicRecalcRows[index];
      const unresolvedFields = recalculated ? {} : {
        auc24: undefined,
        predicted_auc24: undefined,
        auc24_mic: undefined,
        predicted_cmax: undefined,
        predicted_cmin: undefined,
        pta: undefined,
        target_met_raw: undefined,
        target_met: undefined,
        target_status: undefined,
        max_dose_exceeded: undefined,
        is_optimal: false,
        pkpd_results: {},
        safety_check: undefined,
        simulation_data: undefined,
      };
      return {
        ...rowWithoutEvaluation,
        ...unresolvedFields,
        ...(recalculated || {}),
        dose_mg: modifiedDose,
        dose: modifiedDose,
        dose_mg_kg: resolvedWeight > 0
          ? Number((modifiedDose / resolvedWeight).toFixed(2))
          : row.dose_mg_kg,
        dose_kg: resolvedWeight > 0
          ? Number((modifiedDose / resolvedWeight).toFixed(2))
          : (row as any).dose_kg,
        _modified: true,
        _recalc_loading: aucMicRecalcStatus[index] === 'loading',
        _recalc_error: aucMicRecalcErrors[index],
      } as GridSearchRegimen;
    });
  }, [
    aucMicRecalcErrors,
    aucMicRecalcRows,
    aucMicRecalcStatus,
    modifiedAucMicDoses,
    resolvedWeight,
    result.grid_search_results,
    sanfordAdjustment,
  ]);
  const handleEditableAucMicDoseChange = useCallback((index: number, value: number | null, regimen: GridSearchRegimen) => {
    const sourceRows = (result.grid_search_results?.['AUC/MIC'] || sanfordAdjustment?.candidates || []) as Array<GridSearchRegimen & { dose?: number }>;
    const baselineDose = sourceRows[index]?.dose_mg ?? sourceRows[index]?.dose ?? regimen.dose_mg ?? (regimen as any).dose ?? 0;
    if (value == null || !Number.isFinite(value) || value <= 0 || Math.abs(value - baselineDose) < 0.05) {
      setModifiedAucMicDoses((prev) => {
        if (!(index in prev)) return prev;
        const next = { ...prev };
        delete next[index];
        return next;
      });
      setAucMicRecalcRows((prev) => {
        if (!(index in prev)) return prev;
        const next = { ...prev };
        delete next[index];
        return next;
      });
      setAucMicRecalcStatus((prev) => {
        if (!(index in prev)) return prev;
        const next = { ...prev };
        delete next[index];
        return next;
      });
      setAucMicRecalcErrors((prev) => {
        if (!(index in prev)) return prev;
        const next = { ...prev };
        delete next[index];
        return next;
      });
      if (aucMicDebounceRefs.current[index]) clearTimeout(aucMicDebounceRefs.current[index]);
      delete aucMicDebounceRefs.current[index];
      delete aucMicLatestRequestRef.current[index];
      if (selectedGridRegimen?.method === 'AUC/MIC' && selectedGridRegimen.index === index) {
        const resetRegimen = sourceRows[index];
        if (resetRegimen) {
          setSelectedGridRegimen({ method: 'AUC/MIC', index, regimen: resetRegimen });
        }
      }
      return;
    }

    setModifiedAucMicDoses((prev) => ({ ...prev, [index]: value }));
    setAucMicRecalcStatus((prev) => ({ ...prev, [index]: 'loading' }));
    setAucMicRecalcErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    if (aucMicDebounceRefs.current[index]) clearTimeout(aucMicDebounceRefs.current[index]);
    const intervalH = normalizeFiniteNumber(
      regimen.interval_h ?? (regimen as any).interval ?? result.dosing_interval_h ?? result.dosing_interval,
    );
    const infusionH = normalizeFiniteNumber(regimen.infusion_h ?? result.infusion_duration_h);
    if (intervalH == null || intervalH <= 0 || infusionH == null || infusionH <= 0 || infusionH >= intervalH) {
      const errorText = '输注时间必须大于 0 且小于给药间隔';
      setAucMicRecalcErrors((prev) => ({ ...prev, [index]: errorText }));
      setAucMicRecalcStatus((prev) => ({ ...prev, [index]: 'error' }));
      message.error(`候选方案 #${index + 1} 重算失败：${errorText}`);
      return;
    }
    const requestKey = `${value}:${intervalH}:${infusionH}:${configuredMic ?? 'no-mic'}`;
    aucMicLatestRequestRef.current[index] = requestKey;
    aucMicDebounceRefs.current[index] = setTimeout(async () => {
      try {
        const recalcPayload = {
          calculation_id: result.calculation_id,
          dose_mg: value,
          interval_h: intervalH,
          infusion_h: infusionH,
          target_type: 'AUC/MIC',
          mic: configuredMic ?? undefined,
        };
        const recalculated = adminReplay
          ? await adminAPI.replayPkFormulaRecalcRegimen(recalcPayload)
          : await doseAPI.recalcPkFormulaRegimen(recalcPayload);
        if (aucMicLatestRequestRef.current[index] !== requestKey) return;
        setAucMicRecalcRows((prev) => ({ ...prev, [index]: recalculated }));
        setAucMicRecalcStatus((prev) => ({ ...prev, [index]: 'done' }));
      } catch (error: any) {
        if (aucMicLatestRequestRef.current[index] !== requestKey) return;
        const detail = error?.response?.data?.detail;
        const errorText = typeof detail === 'string' ? detail : '请检查剂量、间隔、输注时间与 MIC';
        setAucMicRecalcErrors((prev) => ({ ...prev, [index]: errorText }));
        setAucMicRecalcStatus((prev) => ({ ...prev, [index]: 'error' }));
        setAucMicRecalcRows((prev) => {
          const next = { ...prev };
          delete next[index];
          return next;
        });
        message.error(`候选方案 #${index + 1} 重算失败：${errorText}`);
      }
    }, 350);
  }, [configuredMic, result.calculation_id, result.dosing_interval, result.dosing_interval_h, result.grid_search_results, result.infusion_duration_h, sanfordAdjustment, selectedGridRegimen]);

  useEffect(() => {
    if (selectedGridRegimen?.method !== 'AUC/MIC') return;
    const nextRegimen = editableAucMicRows[selectedGridRegimen.index];
    if (!nextRegimen) return;
    if ((nextRegimen as any)._recalc_loading || (nextRegimen as any)._recalc_error) return;
    if (selectedGridRegimen.regimen !== nextRegimen) {
      setSelectedGridRegimen({ method: 'AUC/MIC', index: selectedGridRegimen.index, regimen: nextRegimen });
    }
  }, [editableAucMicRows, selectedGridRegimen]);
  const activePkRegimen = manualAdjustmentDirty
    ? manualAdjustmentRegimen
    : (selectedGridRegimen?.regimen || (isMeropenemFormula ? (result.original_regimen as any) : undefined));
  const displayDoseMg = activePkRegimen?.dose_mg ?? result.recommended_dose_mg;
  const displayDoseMgKg = activePkRegimen?.dose_mg_kg ?? result.recommended_dose_mg_kg;
  const displayIntervalH = activePkRegimen?.interval_h ?? result.dosing_interval_h ?? result.dosing_interval;
  const displayInfusionH = activePkRegimen?.infusion_h ?? result.infusion_duration_h;
  const displayPredictedAuc24 = activePkRegimen?.predicted_auc24 ?? activePkRegimen?.auc24 ?? result.auc24;
  const displayPredictedCmin = activePkRegimen?.predicted_cmin ?? result.true_trough;
  const displayPredictedCmax = activePkRegimen?.predicted_cmax ?? result.true_peak;
  const adjustmentTargetLabel = sanfordAdjustment?.display_label
    || (pkFormulaTargetMethod === 'AUC/MIC' ? '目标 AUC24/MIC' : '目标 AUC24');
  const adjustmentTargetUnit = sanfordAdjustment?.display_unit
    || (pkFormulaTargetMethod === 'AUC/MIC' ? '' : aucUnit);
  const manualAwareSimulationData = useMemo(() => {
    // 美罗培南公式法的曲线用于解释原方案和稳态采样位置。
    // 选择候选方案只更新 MIC/%fT>MIC 结果，不替换这条曲线。
    if (isMeropenemFormula) {
      const formulaResult: any = result as any;
    const displayContext = formulaResult.pk_formula_curve_context;
      if (
        displayContext?.mode === 'steady_state_two_intervals'
        && displayContext?.curve?.time?.length > 1
        && displayContext?.curve?.concentration?.length === displayContext.curve.time.length
      ) {
        return displayContext.curve;
      }
      const pk = formulaResult.pk_parameters || {};
      const ke = normalizeFiniteNumber(result.ke ?? pk.ke ?? pk.ke_individual);
      const vd = normalizeFiniteNumber(result.vd ?? pk.Vc ?? pk.V ?? pk.Vc_individual);
      const dose = normalizeFiniteNumber(result.original_regimen?.dose_mg ?? result.dose ?? result.recommended_dose_mg);
      const interval = normalizeFiniteNumber(result.original_regimen?.interval_h ?? result.dosing_interval_h);
      const infusion = normalizeFiniteNumber(result.original_regimen?.infusion_h ?? result.infusion_duration_h);
      if (ke != null && vd != null && dose != null && interval != null && infusion != null) {
        const steadyStateCurve = generateSteadyStatePKCurveIntervals(ke, vd, dose, interval, infusion, 2);
        if (steadyStateCurve.time.length > 1) return steadyStateCurve;
      }
      return result.simulation_data;
    }
    const regimenForCurve = manualAdjustmentRegimen || selectedGridRegimen?.regimen;
    return regimenForCurve?.simulation_data ?? result.simulation_data;
  }, [isMeropenemFormula, manualAdjustmentRegimen, result, selectedGridRegimen]);
  const manualAwarePkpdResults = useMemo<Record<string, PkPdMethodResult> | undefined>(() => {
    const baseResults = manualAdjustmentRegimen?.pkpd_results
      || selectedGridRegimen?.regimen.pkpd_results
      || displayPkpdResults;
    if (!pkpdOverride) return baseResults;
    return { ...baseResults, ...pkpdOverride };
  }, [displayPkpdResults, manualAdjustmentRegimen, pkpdOverride, selectedGridRegimen]);
  const originalPkpdResults = useMemo(
    () => result.original_regimen_pkpd || result.pkpd_results,
    [result.original_regimen_pkpd, result.pkpd_results],
  );
  const originalFtMicData = originalPkpdResults?.['%fT>MIC'];
  const originalFtMicDetails = originalFtMicData?.details;
  const manualAwareFtMicData = manualAwarePkpdResults?.['%fT>MIC'];
  const manualAwareFtMicDetails = manualAwareFtMicData?.details;
  const activePrimaryResult = manualAwarePkpdResults?.[preferredMethod];
  const activeSafety = (activePkRegimen as any)?.safety_check ?? safety;
  const activeBackendEvaluation = !manualAdjustmentDirty && !pkpdOverride
    ? ((activePkRegimen as any)?.evaluation ?? result.evaluation)
    : undefined;
  const activeLoadingRegimen = activePkRegimen
    ? ((activePkRegimen as any).loading_regimen ?? result.loading_regimen)
    : result.loading_regimen;
  const displayResult = useMemo(() => ({
    ...result,
    recommended_dose_mg: displayDoseMg,
    recommended_dose_mg_kg: displayDoseMgKg,
    dosing_interval_h: displayIntervalH,
    infusion_duration_h: displayInfusionH,
    predicted_auc24: displayPredictedAuc24,
    predicted_cmin: displayPredictedCmin,
    predicted_cmax: displayPredictedCmax,
    // A selected PK-formula candidate owns its own phase regimen.  Do not
    // fall back to the original result's loading/maintenance interval, which
    // would make a q8h selection appear as the original q12h plan.
    strategy_type: activePkRegimen
      ? ((activePkRegimen as any).strategy_type
        ?? (activeLoadingRegimen ? result.strategy_type : undefined))
      : result.strategy_type,
    transition_time_h: activePkRegimen
      ? resolveLoadingTransitionTime(
          activeLoadingRegimen,
          displayIntervalH,
        )
      : result.transition_time_h,
    recommended_loading_dose_mg: activePkRegimen
      ? ((activePkRegimen as any).recommended_loading_dose_mg ?? activeLoadingRegimen?.dose_mg)
      : result.recommended_loading_dose_mg,
    recommended_loading_dose_mg_kg: activePkRegimen
      ? ((activePkRegimen as any).recommended_loading_dose_mg_kg ?? activeLoadingRegimen?.dose_mg_kg)
      : result.recommended_loading_dose_mg_kg,
    loading_regimen: activePkRegimen ? activeLoadingRegimen : result.loading_regimen,
    maintenance_regimen: activePkRegimen
      ? {
          ...((activePkRegimen as any).maintenance_regimen || {}),
          dose_mg: displayDoseMg,
          dose_mg_kg: displayDoseMgKg,
          interval_h: displayIntervalH,
        }
      : result.maintenance_regimen,
    pkpd_results: manualAwarePkpdResults,
    simulation_data: manualAwareSimulationData,
    pta: (activePkRegimen as any)?.pta ?? activePrimaryResult?.pta ?? (result as any).pta,
    target_met_raw: (activePkRegimen as any)?.target_met_raw ?? activePrimaryResult?.target_met_raw ?? (result as any).target_met_raw,
    target_status: (activePkRegimen as any)?.target_status ?? activePrimaryResult?.target_status ?? (result as any).target_status,
    max_dose_exceeded: (activePkRegimen as any)?.max_dose_exceeded ?? activePrimaryResult?.max_dose_exceeded ?? (result as any).max_dose_exceeded,
    is_optimal: (activePkRegimen as any)?.is_optimal ?? (result as any).is_optimal,
    evaluation: activeBackendEvaluation,
    safety_check: activeSafety,
  }), [
    activeBackendEvaluation,
    activeLoadingRegimen,
    activePkRegimen,
    activePrimaryResult,
    activeSafety,
    displayDoseMg,
    displayDoseMgKg,
    displayInfusionH,
    displayIntervalH,
    displayPredictedAuc24,
    displayPredictedCmax,
    displayPredictedCmin,
    manualAwarePkpdResults,
    manualAwareSimulationData,
    result,
  ]);

  const activeRegimenStatus = useMemo(
    () => resolveRegimenStatus({ regimen: displayResult, selectedMethod: preferredMethod, drugConfig }),
    [displayResult, preferredMethod, drugConfig],
  );
  const activeEvaluationStatus = useMemo(
    () => resolveEvaluationStatus(activeRegimenStatus, (displayResult as any).pta),
    [activeRegimenStatus, displayResult],
  );

  const interpretationRegimen = manualAdjustmentDirty ? manualAdjustmentRegimen : (selectedGridRegimen?.regimen || null);

  useEffect(() => {
    if (!interpretationRegimen || !result.calculation_id) {
      setRegenInterpretation(undefined);
      return;
    }
    setInterpretLoading(true);
    doseAPI.interpret(result.calculation_id, {
      selected_regimen_active: Boolean(interpretationRegimen),
      dose: displayResult.recommended_dose_mg,
      dose_kg: displayResult.recommended_dose_mg_kg,
      interval: displayResult.dosing_interval_h,
      auc24: displayResult.predicted_auc24,
      strategy_type: displayResult.strategy_type,
      transition_time_h: displayResult.transition_time_h ?? undefined,
      recommended_loading_dose_mg: displayResult.recommended_loading_dose_mg,
      recommended_loading_dose_mg_kg: displayResult.recommended_loading_dose_mg_kg,
      loading_regimen: displayResult.loading_regimen,
      maintenance_regimen: displayResult.maintenance_regimen,
    }, selectedMethod).then(text => {
      setRegenInterpretation(text);
    }).catch(() => {
      setRegenInterpretation(result.clinical_interpretation);
    }).finally(() => setInterpretLoading(false));
  }, [displayResult, interpretationRegimen, result.calculation_id, result.clinical_interpretation, selectedMethod]);

  const displayInterpretation = interpretationRegimen ? regenInterpretation : result.clinical_interpretation;

  const handleContinue = () => {
    const finalResult = {
      ...displayResult,
      report_has_selected_regimen: Boolean(selectedGridRegimen),
      report_selected_regimen: selectedGridRegimen ? {
        ...selectedGridRegimen.regimen,
        pkpd_results: manualAwarePkpdResults || null,
        safety_check: activeSafety,
        simulation_data: manualAwareSimulationData,
      } : null,
      pk_formula_original_regimen: result.original_regimen || null,
      pk_formula_original_pkpd: originalPkpdResults || null,
      pk_formula_selected_regimen: selectedGridRegimen?.regimen || null,
      pk_formula_selected_pkpd: selectedGridRegimen ? manualAwarePkpdResults || null : null,
      tdm_points: isMeropenemFormula ? (pkFormulaTdmPoints || []) : (displayResult as any).tdm_points,
      ...(editedInterpretation !== undefined
        ? { clinical_interpretation: editedInterpretation }
        : displayInterpretation !== undefined
          ? { clinical_interpretation: displayInterpretation }
          : {}),
    };
    onContinue(finalResult);
  };

  return (
    <div style={{ marginTop: 24 }}>
      <Divider />
      <Title level={5}>PK公式法计算结果</Title>

      <Row gutter={[16, 16]} className="dose-calculation__result-grid">
        <Col xs={24} md={12}>
          <Card
            title="给药方案评估"
            size="small"
            className="dose-calculation__result-card dose-calculation__clinical-card"
            extra={<Tag color={activeEvaluationStatus.color}>{activeEvaluationStatus.label}</Tag>}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="当前剂量">
                <Text strong style={{ fontSize: 18, color: '#0B2545' }}>
                  {formatResultDose(displayDoseMg)}
                  {displayDoseMgKg != null && ` (${formatResultDosePerKg(displayDoseMgKg)})`}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="给药间隔">每 {displayIntervalH} 小时</Descriptions.Item>
              <Descriptions.Item label="输注时间">{displayInfusionH} 小时</Descriptions.Item>
              {isFtMicDrug ? (
                <>
                  <Descriptions.Item label="%fT>MIC">
                    <Text strong style={{ color: originalFtMicData?.target_met ? '#52c41a' : '#ff4d4f' }}>
                      {originalFtMicData?.value != null ? `${originalFtMicData.value.toFixed(1)}%` : 'N/A'}
                    </Text>
                    {originalFtMicData?.target_min != null && ` (目标 ≥${originalFtMicData.target_min}%)`}
                  </Descriptions.Item>
                  <Descriptions.Item label="PTA (达标概率)">
                    {originalFtMicData?.pta != null ? `${originalFtMicData.pta.toFixed(1)}%` : 'N/A'}
                    {originalFtMicDetails?.pta_target != null && ` (目标 ≥${originalFtMicDetails.pta_target}%)`}
                  </Descriptions.Item>
                  {originalFtMicDetails?.fu != null && (
                    <Descriptions.Item label="游离分数 (fu)">{originalFtMicDetails.fu}</Descriptions.Item>
                  )}
                  {originalFtMicDetails?.default_result?.free_css_peak != null && (
                    <Descriptions.Item label="游离峰浓度">{formatConcentrationValue(originalFtMicDetails.default_result.free_css_peak, 3)}</Descriptions.Item>
                  )}
                  {originalFtMicDetails?.default_result?.free_css_trough != null && (
                    <Descriptions.Item label="游离谷浓度">{formatConcentrationValue(originalFtMicDetails.default_result.free_css_trough, 3)}</Descriptions.Item>
                  )}
                  {originalFtMicDetails?.default_mic != null && (
                    <Descriptions.Item label="MIC">{convertCanonicalValue(originalFtMicDetails.default_mic, micUnit, 'concentration')} {micUnit}</Descriptions.Item>
                  )}
                </>
              ) : (
                <>
                  <Descriptions.Item label="预测AUC24">
                    {formatAucValue(displayPredictedAuc24)}
                  </Descriptions.Item>
                  <Descriptions.Item label="预测峰浓度 (Cmax)">{formatConcentrationValue(displayPredictedCmax)}</Descriptions.Item>
                  <Descriptions.Item label="预测谷浓度 (Cmin)">{formatConcentrationValue(displayPredictedCmin)}</Descriptions.Item>
                  {displayResult.pta != null && (
                    <Descriptions.Item label="PTA (达标概率)">
                      {Number(displayResult.pta).toFixed(1)}%
                    </Descriptions.Item>
                  )}
                </>
              )}
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <div className="dose-calculation__clinical-stack">
            <Card title="安全检查" size="small" className="dose-calculation__clinical-card dose-calculation__safety-card">
              <div className={`dose-calculation__safety-status ${!activeSafety.safe ? 'dose-calculation__safety-status--danger' : activeSafety.warnings?.length ? 'dose-calculation__safety-status--warning' : 'dose-calculation__safety-status--ok'}`}>
                {activeSafety.safe ? (
                  <Tag color="green" icon={<SafetyOutlined />}>安全检查通过</Tag>
                ) : (
                  <Tag color="red">存在安全风险</Tag>
                )}
                <span className="dose-calculation__safety-copy">
                  {activeSafety.safe && !activeSafety.warnings?.length && !activeSafety.errors?.length
                    ? '安全边界内'
                    : `${(activeSafety.warnings?.length || 0) + (activeSafety.errors?.length || 0)} 项需复核`}
                </span>
              </div>
              {((activeSafety.warnings?.length || 0) > 0 || (activeSafety.errors?.length || 0) > 0) && (
                <div className="dose-calculation__safety-alerts">
                  {activeSafety.warnings?.map((w: any, i: number) => (
                    <Alert key={i} type="warning" message={typeof w === 'string' ? w : (w?.msg || JSON.stringify(w))} banner />
                  ))}
                  {activeSafety.errors?.map((e: any, i: number) => (
                    <Alert key={i} type="error" message={typeof e === 'string' ? e : (e?.msg || JSON.stringify(e))} banner />
                  ))}
                </div>
              )}
            </Card>

            <Card title="个体PK参数" size="small" className="dose-calculation__clinical-card dose-calculation__pk-card">
              <Descriptions
                column={isMobileShell ? 1 : 2}
                size="small"
                styles={{
                  label: { minWidth: 132, whiteSpace: 'normal' },
                  content: { minWidth: 96 },
                }}
              >
                <Descriptions.Item label="消除速率常数 (ke)">{formatPkParameter(result.ke)} h⁻¹</Descriptions.Item>
                <Descriptions.Item label="半衰期">{formatPkParameter(result.half_life)} h</Descriptions.Item>
                <Descriptions.Item label="表观分布容积 (Vd)">{formatPkParameter(result.vd)} L ({formatPkParameter(result.vd_per_kg)} L/kg)</Descriptions.Item>
                <Descriptions.Item label="清除率 (CL)">{formatPkParameter(result.clearance)} L/h</Descriptions.Item>
                {result.true_peak != null && <Descriptions.Item label="稳态峰浓度">{formatPkParameter(result.true_peak)} {concentrationUnit}</Descriptions.Item>}
                {result.true_trough != null && <Descriptions.Item label="稳态谷浓度">{formatPkParameter(result.true_trough)} {concentrationUnit}</Descriptions.Item>}
                {result.auc_tau != null && <Descriptions.Item label="AUCτ">{formatPkParameter(result.auc_tau)} {aucUnit}</Descriptions.Item>}
                {result.auc24 != null && <Descriptions.Item label="AUC₂₄">{formatPkParameter(result.auc24)} {aucUnit}</Descriptions.Item>}
                {result.steady_state_info && (
                  <Descriptions.Item label="达稳态时间">
                    约 {result.steady_state_info.n_doses} 个剂量（~{result.steady_state_info.time_h} 小时）
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          </div>
        </Col>
      </Row>

      {false && sanfordAdjustment && (
        <Card title={'AUC24 \u8c03\u6574\u5efa\u8bae'} size="small" style={{ marginTop: 16 }}>
          {manualRecalcLoading && <Alert type="info" showIcon message="正在按当前输入重算候选方案" style={{ marginBottom: 12 }} />}
          {manualRecalcError && <Alert type="error" showIcon message={manualRecalcError} style={{ marginBottom: 12 }} />}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form layout="vertical">
                <Form.Item label={'\u7ed9\u836f\u5242\u91cf (mg)'} style={{ marginBottom: 12 }}>
                  <InputNumber
                    min={1}
                    step={10}
                    value={manualDoseMg}
                    onChange={setManualDoseMg}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item label={'\u7ed9\u836f\u95f4\u9694 (h)'} style={{ marginBottom: 12 }}>
                  <InputNumber
                    min={1}
                    step={0.5}
                    value={manualIntervalH}
                    onChange={setManualIntervalH}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item label={'\u8f93\u6ce8\u65f6\u95f4 (h)'} style={{ marginBottom: 12 }}>
                  <InputNumber
                    min={0.1}
                    step={0.5}
                    value={manualInfusionH}
                    onChange={setManualInfusionH}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item label={`${adjustmentTargetLabel}${adjustmentTargetUnit ? ` (${adjustmentTargetUnit})` : ''}`} style={{ marginBottom: 12 }}>
                  <InputNumber
                    min={0.1}
                    step={pkFormulaTargetMethod === 'AUC/MIC' ? 10 : 25}
                    value={manualTargetDisplay}
                    onChange={setManualTargetDisplay}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Space wrap>
                  {sanfordAdjustment.same_interval_regimen && (
                    <Button
                      size="small"
                      onClick={() => {
                        setManualDoseMg(sanfordAdjustment.same_interval_regimen?.dose_mg ?? null);
                        setManualIntervalH(sanfordAdjustment.same_interval_regimen?.interval_h ?? null);
                        setManualInfusionH(sanfordAdjustment.same_interval_regimen?.infusion_h ?? result.infusion_duration_h ?? null);
                      }}
                    >
                      {'\u5e94\u7528\u540c\u95f4\u9694\u5efa\u8bae'}
                    </Button>
                  )}
                  {sanfordAdjustment.same_dose_regimen && (
                    <Button
                      size="small"
                      onClick={() => {
                        setManualDoseMg(sanfordAdjustment.same_dose_regimen?.dose_mg ?? null);
                        setManualIntervalH(sanfordAdjustment.same_dose_regimen?.interval_h ?? null);
                        setManualInfusionH(sanfordAdjustment.same_dose_regimen?.infusion_h ?? result.infusion_duration_h ?? null);
                      }}
                    >
                      {'\u5e94\u7528\u540c\u5242\u91cf\u5efa\u8bae'}
                    </Button>
                  )}
                  <Button
                    size="small"
                    onClick={() => {
                      setManualDoseMg(selectedGridRegimen?.regimen.dose_mg ?? result.recommended_dose_mg ?? result.dose ?? null);
                      setManualIntervalH(selectedGridRegimen?.regimen.interval_h ?? result.dosing_interval_h ?? result.dosing_interval ?? null);
                      setManualInfusionH(selectedGridRegimen?.regimen.infusion_h ?? result.infusion_duration_h ?? null);
                      setManualTargetDisplay(targetMidpointDisplay ?? null);
                    }}
                  >
                    {'\u91cd\u7f6e'}
                  </Button>
                </Space>
              </Form>
            </Col>

            <Col xs={24} md={12}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label={'\u76ee\u6807\u8303\u56f4'}>
                  {sanfordAdjustment.display_target_min}
                  {sanfordAdjustment.display_unit
                    ? ` - ${sanfordAdjustment.display_target_max} ${sanfordAdjustment.display_unit}`
                    : ` - ${sanfordAdjustment.display_target_max}`}
                </Descriptions.Item>
                <Descriptions.Item label={adjustmentTargetLabel}>
                  {manualTargetDisplay != null
                    ? `${manualTargetDisplay}${adjustmentTargetUnit ? ` ${adjustmentTargetUnit}` : ''}`
                    : 'N/A'}
                </Descriptions.Item>
                {pkFormulaTargetMethod === 'AUC/MIC' && manualTargetAbsoluteAuc != null && (
                  <Descriptions.Item label={'\u6362\u7b97\u540e\u76ee\u6807 AUC24'}>
                    {formatAucValue(manualTargetAbsoluteAuc, 2)}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label={`\u5355\u6b21\u5242\u91cf (${doseMassUnit}/kg)`}>
                  {formatResultDosePerKg(manualAdjustmentRegimen?.dose_mg_kg ?? displayDoseMgKg)}
                </Descriptions.Item>
                <Descriptions.Item label={'\u8f93\u6ce8\u65f6\u95f4 (h)'}>
                  {manualAdjustmentRegimen?.infusion_h ?? displayInfusionH}
                </Descriptions.Item>
                <Descriptions.Item label={'\u9884\u6d4b\u8c37\u6d53\u5ea6'}>
                  {formatConcentrationValue(manualAdjustmentRegimen?.predicted_cmin ?? displayPredictedCmin)}
                </Descriptions.Item>
                <Descriptions.Item label={'\u9884\u6d4b\u5cf0\u6d53\u5ea6'}>
                  {formatConcentrationValue(manualAdjustmentRegimen?.predicted_cmax ?? displayPredictedCmax)}
                </Descriptions.Item>
                <Descriptions.Item label={'\u9884\u6d4bAUC24'}>
                  {formatAucValue(manualAdjustmentRegimen?.predicted_auc24 ?? displayPredictedAuc24)}
                </Descriptions.Item>
                <Descriptions.Item label={'\u56de\u63a8\u8bf4\u660e'}>
                  {sanfordGoalReasonMap[sanfordAdjustment.goal_reason] || sanfordAdjustment.goal_reason}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>

          {manualTargetRecommendations && (
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              message={`\u6309\u5f53\u524d\u76ee\u6807\u503c\u63a8\u7b97\uff1a\u540c\u7ed9\u836f\u95f4\u9694\u65f6\u5efa\u8bae\u5355\u6b21\u5242\u91cf ${formatResultDose(manualTargetRecommendations?.sameIntervalDoseMg)}\uff1b\u540c\u5355\u6b21\u5242\u91cf\u65f6\u5efa\u8bae\u7ed9\u836f\u95f4\u9694 q${manualTargetRecommendations?.sameDoseIntervalH}h\u3002`}
            />
          )}
        </Card>
      )}

      {manualAwareSimulationData && (
        <Card title="药时曲线" style={{ marginTop: 16 }}>
          <PKCurve
            key={isMeropenemFormula
              ? `pk-formula-${result.calculation_id || 'latest'}-original`
              : `pk-formula-${result.calculation_id || 'latest'}-${displayIntervalH || 'default'}-${displayDoseMg || 'default'}-${manualAdjustmentDirty ? 'manual' : 'base'}`}
            simulationData={projectCanonicalConcentrationCurve(manualAwareSimulationData, concentrationUnit)}
            targetRange={(() => {
              const targets = drugConfig?.targets || [];
              const t = targets.find((t: any) => t.method === preferredMethod)
                || targets.find((t: any) => {
                  const paramMap: Record<string, string> = {
                    'Target Concentration': drugConfig?.continuous_infusion?.target_parameter || 'Cmin',
                    'Target Cmax': 'Cmax',
                    'Target AUC': 'AUC24',
                    'AUC/MIC': 'AUC24/MIC',
                    '%fT>MIC': '%fT>MIC',
                  };
                  return t.parameter === paramMap[preferredMethod || ''];
                });
              if (!t) return {};
              const p = (t.parameter || '').toLowerCase();
              if (p.includes('auc') || p.includes('ft>') || p.includes('ft >')) return {};
              return { troughMin: t.min, troughMax: t.max };
            })()}
            // 美罗培南公式法完全由当前原方案数据自动缩放，不使用固定时间窗口。
            timeRange={undefined}
            autoScale={isMeropenemFormula}
            mic={convertCanonicalValue(result.pkpd_results?.['%fT>MIC']?.details?.default_mic, micUnit, 'concentration') ?? undefined}
            nephrotoxicityCmin={getExposureSafetyLimit(drugConfig, 'exposure.cmin', concentrationUnit, {
              selectedMethod: preferredMethod,
              administrationMode: result.administration_mode,
              covariates,
            })}
            tdmPoints={pkFormulaTdmPoints}
            formulaCurveContext={isMeropenemFormula ? (result as any).pk_formula_curve_context : undefined}
            concentrationUnit={concentrationUnit}
            micUnit={micUnit}
          />
        </Card>
      )}

      <PkPdResultsCard
        pkpdResults={manualAwarePkpdResults}
        preferredMethod={preferredMethod}
        evaluationRegimen={displayResult}
        gridSearchResults={result.grid_search_results}
        editableGridMethod={editableAucMicRows.length > 0 ? 'AUC/MIC' : undefined}
        editableGridRows={editableAucMicRows}
        onEditableGridDoseChange={handleEditableAucMicDoseChange}
        selectedGridRegimen={selectedGridRegimen ? { method: selectedGridRegimen.method, index: selectedGridRegimen.index } : null}
        onSelectGridRegimen={handleFormulaCandidateSelect}
        onPkpdResultsChange={setPkpdOverride}
        drugConfig={drugConfig}
        hideFtMicBreakpointTable={isMeropenemFormula && !selectedGridRegimen}
        ftMicPanelTitle={selectedGridRegimen ? '候选方案分析（已选方案）：不同 MIC 断点下的 %fT>MIC 与 PTA:' : undefined}
        formulaCandidateOnly={isMeropenemFormula}
      />

      {isMeropenemFormula && (
        <MeropenemFormulaPlanComparison
          originalRegimen={result.original_regimen}
          originalPkpdResult={originalPkpdResults?.['%fT>MIC']}
          selectedRegimen={selectedGridRegimen?.regimen}
          selectedPkpdResult={selectedGridRegimen?.regimen.pkpd_results?.['%fT>MIC']}
          drugConfig={drugConfig}
        />
      )}

      <ClinicalInterpretation
        interpretation={displayInterpretation}
        regimenChanged={selectedGridRegimen !== null || manualAdjustmentDirty}
        loading={interpretLoading}
        onInterpretationChange={setEditedInterpretation}
      />

      {renderContinueAction(isMobileShell, handleContinue)}
      {/*
        <Button type="primary" size="large" onClick={handleContinue}>
          生成报告
        </Button>
      </div>
      */}
    </div>
  );
};

export default DoseCalculation;
export { PkPdResultsCard };
